import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useMediasoup from '../hooks/useMediasoup';
import { useExamContext } from '../context/ExamContext';
import { getExamToken } from '../auth/examToken';
import { calculateLiveEndTime, parseExamSchedule } from '../hooks/useExamTimer';
import ExamTimerDisplay from './ExamTimerDisplay';
import DeviceLogoutCountdown from './DeviceLogoutCountdown';
import { createBoundedUploadQueue } from '../utils/boundedUploadQueue';
import { createChunkUploader } from '../utils/chunkUploader';
import {
  attachLiveAudio,
  cameraStreamFromList,
  liveVideoTrack,
  nextChunkFilename,
  RECORDING_GOOD_CHUNK_BYTES,
  RECORDING_STALL_MS,
  recordingBadChunkProgress,
  recordingChunkHealth,
  recordingDevice,
  recordingStartDecision,
  shouldRestartRecordingAfterVisible,
  startRecorderOrVideoOnly,
} from '../utils/recordingLifecycle';
import { createRepeatGate } from '../utils/repeatGate';
import { ENV } from '../config/env';
import * as phpApi from '../api/phpApi';
import * as nodeApi from '../api/nodeApi';
import * as aiApi from '../api/aiApi';
import './ExamRoom.css';
import { jsPDF } from 'jspdf';
import Calculator from './Calculator';

import {
  FaDesktop, FaClock, FaExclamationTriangle, FaCheckCircle,
  FaUserGraduate, FaChalkboardTeacher, FaSpinner, FaSync,
  FaCamera, FaRedo, FaRobot, FaArrowRight, FaHourglassHalf,
  FaFilePdf, FaImage, FaTimes, FaTrash, FaEye, FaPhone, FaMobileAlt,
  FaExclamationCircle, FaInfoCircle, FaListOl, FaArrowsAlt, FaMicrophone, FaCrop, FaStopCircle
} from 'react-icons/fa';

// --- VIDEO RECORDING CONFIGURATION ---
const VIDEO_RECORD_CHUNK_MS = 60000;
// Students all start recording on the same exam-started broadcast; the first chunk is shortened by a random
// 0..30 s so the per-minute uploads of a whole room do not hit the server in the same second.
const VIDEO_RECORD_CHUNK_STAGGER_MS = 30000;
const VIDEO_CHUNK_RETRY_DELAYS_MS = [3000, 10000];
const RECORDING_OVERLAY_FPS = 20;
// The other device staying disconnected this long during the exam logs this device out as well.
const DEVICE_DISCONNECT_LOGOUT_MS = 2 * 60 * 1000;

// --- HELPER COMPONENT FOR QUESTIONS ---
const QuestionItem = React.memo(({ q, index, answers, onAnswerChange }) => {

  const handleChange = (val) => {
    onAnswerChange(q.question_key, val);
  };

  return (
    <div className="question-card" id={`question-${q.question_key}`}>
      <div className="question-header">
        <span className="q-number-badge">Q{index}</span>
        <div className="q-content-wrapper">
          <span className="q-text" dangerouslySetInnerHTML={{ __html: q.qtext }}></span>
          {q.qimage && <img src={q.qimage} alt="Question Diagram" className="q-image" />}
        </div>
        <div className="q-meta">
          <span className="q-marks-badge">({q.maxmark} Marks)</span>
        </div>
      </div>

      <div className="answer-area">
        {q.ques_type === 'MCQ' || (q.achoice1 && q.achoice2) ? (
          <div className="mcq-options">
            {['achoice1', 'achoice2', 'achoice3', 'achoice4'].map(optKey => (
              q[optKey] && (
                <label key={optKey} className={`mcq-label ${answers[q.question_key] === optKey ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`q_${q.question_key}`}
                    value={optKey}
                    checked={answers[q.question_key] === optKey}
                    onChange={() => handleChange(optKey)}
                  />
                  <span className="opt-text" dangerouslySetInnerHTML={{ __html: q[optKey] }}></span>
                  {answers[q.question_key] === optKey && <FaCheckCircle className="opt-check" />}
                </label>
              )
            ))}
          </div>
        ) : (
          <textarea
            value={answers[q.question_key] || ''}
            onChange={(e) => handleChange(e.target.value)}
            className="student-input"
            rows={6}
            placeholder="Type your answer here..."
          />
        )}
      </div>
    </div>
  );
});

const ExamRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const ctx = useExamContext();
  const decoded = ctx?.decoded ?? null;
  const token = ctx?.token ?? getExamToken();

  const userId = ctx?.userId ?? decoded?.user_id ?? 'guest';
  const userName = ctx?.userName ?? decoded?.user_name ?? 'Student';
  const fullName = ctx?.fullName ?? decoded?.full_name ?? '';
  const registrationId = ctx?.registrationId ?? decoded?.registration_id ?? null;
  const currentRoomId = roomId;
  const EXAM_STOPPED_KEY = `exam_stopped_${currentRoomId}`;
  const EXAM_PAUSED_KEY = `exam_paused_${currentRoomId}_${userId}`;
  const [apiRegisterNumber, setApiRegisterNumber] = useState(null);
  const [apiRoomId, setApiRoomId] = useState(null);

  const [assessmentId, setAssessmentId] = useState(decoded?.assessment_id ?? null);

  const assessmentIdRef = useRef(assessmentId);
  useEffect(() => {
    assessmentIdRef.current = assessmentId;
  }, [assessmentId]);
  const registerNo = apiRegisterNumber ?? decoded?.register_no ?? decoded?.register_number ?? userId;
  const roomDisplay = apiRoomId ?? currentRoomId;
  const courseCode = decoded?.course ?? '';

  const BASE_URL = ENV.PHP_API_BASE;
  const AI_SERVER_BASE = ENV.AI_API_BASE;
  const REQUIRED_CAMERAS = ENV.REQUIRED_CAMERAS;

  // --- LOCAL STORAGE KEYS ---
  const ANSWERS_KEY = `exam_answers_${roomId}_${userId}`;
  const FULLSCREEN_REQUIRED_KEY = `exam_fullscreen_required_${roomId}_${userId}`;

  // --- STATE ---
  const [examStarted, setExamStarted] = useState(false);
  const [isExamStoppedByInvigilator, setIsExamStoppedByInvigilator] = useState(false);
  const isExamStoppedByInvigilatorRef = useRef(false);
  // Per-student pause (independent of room-wide exam-ended / EXAM_STOPPED_KEY)
  const [isStudentPausedByInvigilator, setIsStudentPausedByInvigilator] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem(`exam_paused_${roomId}_${userId}`);
    } catch (_) {
      return false;
    }
  });
  const isStudentPausedByInvigilatorRef = useRef(isStudentPausedByInvigilator);
  useEffect(() => {
    isStudentPausedByInvigilatorRef.current = isStudentPausedByInvigilator;
  }, [isStudentPausedByInvigilator]);
  const isExamBlockedByInvigilator = isExamStoppedByInvigilator || isStudentPausedByInvigilator;
  const [isStudentReady, setIsStudentReady] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [examFinished, setExamFinished] = useState(false);
  const [isTimeExceeded, setIsTimeExceeded] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]); // { file: File, preview: string }[]
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // --- DRAG AND DROP HANDLERS ---
  const handleReorderDragStart = (e, index) => {
    setDraggedIdx(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index);
    }
  };

  const handleReorderDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== index) {
      setDragOverIdx(index);
    }
  };

  const handleReorderDragOver = (e) => {
    e.preventDefault();
  };

  const handleReorderDrop = (e, index) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== index) {
      const newFiles = [...imageFiles];
      const item = newFiles.splice(draggedIdx, 1)[0];
      newFiles.splice(index, 0, item);
      setImageFiles(newFiles);
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handleReorderTouchStart = (e, index) => {
    // Only drag if there are multiple images
    if (imageFiles.length > 1) {
      setDraggedIdx(index);
    }
  };

  const handleReorderTouchMove = (e) => {
    if (draggedIdx === null) return;
    const touch = e.targetTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element) {
      const itemEl = element.closest('[data-index]');
      if (itemEl && itemEl.dataset && itemEl.dataset.index) {
        const targetIdx = parseInt(itemEl.dataset.index, 10);
        if (!isNaN(targetIdx) && targetIdx !== dragOverIdx) {
          setDragOverIdx(targetIdx);
        }
      }
    }
  };

  const handleReorderTouchEnd = () => {
    if (draggedIdx !== null && dragOverIdx !== null && draggedIdx !== dragOverIdx) {
      const newFiles = [...imageFiles];
      const item = newFiles.splice(draggedIdx, 1)[0];
      newFiles.splice(dragOverIdx, 0, item);
      setImageFiles(newFiles);
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
  };
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  // uploadStep: null | 'converting' | 'uploading' | 'saving' | 'done' | 'error'
  const [uploadStep, setUploadStep] = useState(null);
  const [uploadStepError, setUploadStepError] = useState(null);

  const [sections, setSections] = useState([]);
  const [questionsMap, setQuestionsMap] = useState({});
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [questionPaperPath, setQuestionPaperPath] = useState(null);
  const [questionPaperUrl, setQuestionPaperUrl] = useState(null);
  const [institutionLogoUrl, setInstitutionLogoUrl] = useState(null);
  const [pdfUnavailable, setPdfUnavailable] = useState(false);

  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(ANSWERS_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isFullscreenActive, setIsFullscreenActive] = useState(() => (
    typeof document !== 'undefined' && !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      document.mozFullScreenElement
    )
  ));

  const [serverOffset, setServerOffset] = useState(0);
  const [examEndTime, setExamEndTime] = useState(null);
  const examEndTimeRef = useRef(null);

  const [inlineAlertMessage, setInlineAlertMessage] = useState(null);

  const [examScheduledStart, setExamScheduledStart] = useState(null);
  // Raw epoch (ms) of the scheduled exam start, used to trigger auto-retry polling
  const examScheduledStartEpochRef = useRef(null);
  const [isOneMinuteToStart, setIsOneMinuteToStart] = useState(false);

  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // --- Progress Alerts Refs ---
  const alertsInitialized = useRef(false);
  const alert50Shown = useRef(false);
  const alert75Shown = useRef(false);
  const alert90Shown = useRef(false);

  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null); // Initial verification photo from liveness check
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [isSystemActivated, setIsSystemActivated] = useState(false); // 🔹 NEW STATE: Tracks if user clicked Start

  // Mobile identity verification (compare live face with S3 reference photo)
  const [isIdentityVerified, setIsIdentityVerified] = useState(false);
  const [isMobileEntered, setIsMobileEntered] = useState(false);
  const [identityCheckStatus, setIdentityCheckStatus] = useState('idle'); // 'idle' | 'checking' | 'verified' | 'failed' | 'no_reference'
  const [identityMessage, setIdentityMessage] = useState('');

  const [livenessMessage, setLivenessMessage] = useState("Initializing Camera...");
  const [retakeAttempts, setRetakeAttempts] = useState(0);
  const [retakeExpired, setRetakeExpired] = useState(false);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [proctorVideoEnabled, setProctorVideoEnabled] = useState(true);
  const [proctorAudioEnabled, setProctorAudioEnabled] = useState(true);
  const proctorAudioEnabledRef = useRef(true);
  const proctorVideoEnabledRef = useRef(true); // 🟢 Track proctor audio state without stale closure
  const [privateAudioTargetUserId, setPrivateAudioTargetUserId] = useState(null);
  const [privateCallWithProctor, setPrivateCallWithProctor] = useState(false);
  const [showPrivateCallNotification, setShowPrivateCallNotification] = useState(false);
  const [cameraWarning, setCameraWarning] = useState(null); // Warning message to show on camera
  const [zoomedImage, setZoomedImage] = useState(null); // For image zoom view
  const [showCameraModal, setShowCameraModal] = useState(false); // Camera preview modal
  const [capturedPhotoForCrop, setCapturedPhotoForCrop] = useState(null); // Photo captured for cropping
  const [isCropEnabled, setIsCropEnabled] = useState(false); // Toggle for crop lines
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 }); // Crop selection area
  const [isDragging, setIsDragging] = useState(false); // For crop area dragging (legacy, kept for safety)
  const [dragState, setDragState] = useState(null); // { type: 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'new', startX, startY, startCrop }
  const [imageDisplaySize, setImageDisplaySize] = useState({ width: 0, height: 0 }); // Actual displayed image size
  const [focusPoint, setFocusPoint] = useState(null); // Focus indicator { x, y, visible }
  const [selectedCameraIndex, setSelectedCameraIndex] = useState(0); // Camera index for photo capture (mobile switch)
  const [livenessCameraIndex, setLivenessCameraIndex] = useState(0); // Camera index for liveness verification (mobile switch)
  const [alternateCameraStream, setAlternateCameraStream] = useState(null); // Alternate camera stream for switching (mobile)
  const [alternateLivenessStream, setAlternateLivenessStream] = useState(null); // Alternate camera stream for liveness (mobile)
  const cameraModalVideoRef = useRef(null); // Video ref for camera modal
  const cropImageRef = useRef(null); // Ref for the captured image element
  const rearCameraInputRef = useRef(null); // Hidden file input for native rear-camera capture
  const nativeCaptureActiveRef = useRef(false); // native camera open: page is hidden but the student has not left

  const [previewImage, setPreviewImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isFullPreviewOpen, setIsFullPreviewOpen] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [refreshedFrontStream, setRefreshedFrontStream] = useState(null);
  const refreshedFrontStreamRef = useRef(null);
  const restoreMonitoringStreamsRef = useRef(null);

  const successCountRef = useRef(0);
  const scanInterval = useRef(null);
  const isProcessingFrame = useRef(false);
  const checkPreviewRef = useRef(null);
  const proctorVideoRef = useRef(null);
  const proctorScreenRef = useRef(null);

  const hasAutoSubmitted = useRef(false);
  const handleAutoSubmitRef = useRef(null);
  const monitoringIntervalRef = useRef(null);
  const [monitoringPassedOnce, setMonitoringPassedOnce] = useState(false);
  const [getQuestionsTriggeredOnce, setGetQuestionsTriggeredOnce] = useState(false);
  const isMonitoringProcessingRef = useRef(false);
  const mediaRecorderRef = useRef(null);

  const recordingUploadIdRef = useRef(null);
  const recordingPartNumberRef = useRef(0);
  const recordingFinalizingRef = useRef(false);
  const allowViolationReportRef = useRef(createRepeatGate(ENV.VIOLATION_REPEAT_INTERVAL_SEC * 1000));
  const recordingStartPayloadRef = useRef(null);
  const recordingStartSentRef = useRef(false);
  const recordingFilenameRef = useRef(null); // 🔹 FIX: Was used in sendFinalizeBeacon but never declared
  const uploadVideoChunkRef = useRef();
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [recordingChunksUploaded, setRecordingChunksUploaded] = useState(0);
  const cctvOverlayFrameRef = useRef(null);
  const cameraWarningRef = useRef(null); // Current violation text for recording overlay (kept in sync with cameraWarning)
  const examStartedSocketEventRef = useRef(false); // Track if we received exam-started socket event
  const waitingForSocketStatusRef = useRef(false); // Track if we're waiting for socket get-exam-status to complete (prevents race condition on refresh)
  const initialExamStatusFetchedRef = useRef(false); // Ensure we fetch exam status once on load (fixes timer on mobile when socket connects late)

  // Upload / question paper restriction
  const [canUploadAnswerSheet, setCanUploadAnswerSheet] = useState(false);
  const [allowedUploadTimeString, setAllowedUploadTimeString] = useState('');

  useEffect(() => {
    if (!examStarted || !examEndTime) return;

    const checkUploadEligibility = () => {
      const nowMs = Date.now() + serverOffset;
      const remainingMs = examEndTime - nowMs;
      const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));

      if (remainingSec <= 900) {
        setCanUploadAnswerSheet(true);
      } else {
        setCanUploadAnswerSheet(false);

        const allowedMs = examEndTime - (900 * 1000);
        const allowedDate = new Date(allowedMs - serverOffset);

        const formattedTime = allowedDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true   // Ensures AM/PM format
        });

        setAllowedUploadTimeString(formattedTime);
      }
    };

    checkUploadEligibility();
    const interval = setInterval(checkUploadEligibility, 1000);
    return () => clearInterval(interval);
  }, [examStarted, examEndTime, serverOffset]);

  // --- EARLY QUESTION PAPER REVEAL (1 MINUTE BEFORE) ---
  useEffect(() => {
    // If exam has officially started or finished, we don't need to poll
    if (examStarted || examFinished) {
      if (!isOneMinuteToStart && examStarted) setIsOneMinuteToStart(true);
      return;
    }

    const checkEarlyReveal = () => {
      if (examScheduledStartEpochRef.current) {
        const currentTime = Date.now() + serverOffset;
        const timeUntilStart = examScheduledStartEpochRef.current - currentTime;

        // Show questions strictly within 10s before start and never past start time unless examStarted is true
        if (timeUntilStart <= 10000 && timeUntilStart > 0) {
          if (!isOneMinuteToStart) setIsOneMinuteToStart(true);
        } else {
          if (isOneMinuteToStart) setIsOneMinuteToStart(false);
        }
      }
    };

    checkEarlyReveal();
    const interval = setInterval(checkEarlyReveal, 1000);
    return () => clearInterval(interval);
  }, [examStarted, examFinished, serverOffset, isOneMinuteToStart]);

  // Pinch-to-Zoom Refs
  const lastPinchDistance = useRef(null);
  const startZoomLevel = useRef(1);
  const fullscreenExitWarnedRef = useRef(false);
  const snapshotValidationInProgressRef = useRef(false); // Prevent duplicate snapshot validation calls
  const snapshotValidatedRef = useRef(false); // Track if snapshot has been validated for current IDs
  const snapshotRetryInProgressRef = useRef(false); // Prevent duplicate retry calls
  const studentDurationRef = useRef(null); // 🔹 FIX: Store student-specific duration from PHP (overrides generic socket duration)
  const examStartedAtRef = useRef(null); // 🔹 FIX: Store authoritative start time from socket
  const activationRunRef = useRef(false); // Prevent duplicate run in React Strict Mode (avoids two screen-share popups)

  const {
    isConnected,
    hasJoinedRoom,
    localStreams, remoteStreams, roomUsers,
    localVideoRef1, localVideoRef2, startLocalVideo,
    startScreenShare, isScreenSharing, getScreenShareTrack, socketRef,
    replaceFirstCameraTrack,
    showScreenShareButton, setShowScreenShareButton,
    isMobileDevice,
    mediaConnectivityError,
  } = useMediasoup(currentRoomId, userId, 'student', userName, registrationId, fullName, assessmentId);
  const getScreenShareTrackRef = useRef(getScreenShareTrack);
  useEffect(() => { getScreenShareTrackRef.current = getScreenShareTrack; }, [getScreenShareTrack]);

  const remoteStreamsRef = useRef([]);
  useEffect(() => {
    remoteStreamsRef.current = remoteStreams;
  }, [remoteStreams]);

  // Additional mobile detection fallback (for camera switch button visibility)
  const isMobileDeviceFallback = isMobileDevice ||
    (typeof window !== 'undefined' && (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (window.innerWidth < 768 && 'ontouchstart' in window)
    ));

  const proctorVideo = remoteStreams.find(s => s.kind?.includes('video') && s.userType !== 'student');
  // 🔹 2. ADD THIS: Find the Proctor's Audio Stream
  const proctorAudio = remoteStreams.find(s => s.kind === 'audio' && s.userType === 'staff');

  // 🔹 3. ADD THIS: Create a Ref for the audio element
  const proctorAudioRef = useRef(null);
  const proctorScreen = remoteStreams.find(s => s.kind === 'screen' || (s.userType === 'staff' && s.kind === 'screen'));
  useEffect(() => {
    if (proctorAudio && proctorAudioRef.current) {
      attachStream(proctorAudioRef.current, proctorAudio);
    }
  }, [proctorAudio]);

  // Browsers block unmuted autoplay until the page gets a user gesture (e.g. right after a refresh), and the
  // element stays paused for good once play() is rejected. Retry when the mic is enabled and on the next tap/key.
  useEffect(() => {
    const el = proctorAudioRef.current;
    if (!el || !proctorAudio || !proctorAudioEnabled) return;
    const tryPlay = () => {
      if (el.srcObject && el.paused) el.play().catch(() => {});
    };
    tryPlay();
    const events = ['pointerdown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, tryPlay, { passive: true }));
    return () => events.forEach(e => document.removeEventListener(e, tryPlay));
  }, [proctorAudio, proctorAudioEnabled]);

  // Reactive audio track synchronization for global proctor audio and private calls
  useEffect(() => {
    const proctorAudioObj = remoteStreams.find(s => s.userType === 'staff' && s.kind === 'audio');
    if (proctorAudioObj && proctorAudioObj.stream) {
      const track = proctorAudioObj.stream.getAudioTracks()[0];
      if (track) {
        if (!proctorAudioEnabled) {
          track.enabled = false;
        } else if (privateAudioTargetUserId !== null) {
          const isMe = String(privateAudioTargetUserId) === String(userId);
          track.enabled = isMe;
          console.log(`[Sync] Setting proctor audio track enabled=${isMe} for target=${privateAudioTargetUserId}`);
        } else {
          track.enabled = true;
          console.log(`[Sync] Restoring proctor audio track enabled=true`);
        }
      }
    }
    if (proctorAudioRef.current) {
      if (!proctorAudioEnabled) {
        proctorAudioRef.current.muted = true;
      } else if (privateAudioTargetUserId !== null) {
        proctorAudioRef.current.muted = String(privateAudioTargetUserId) !== String(userId);
      } else {
        proctorAudioRef.current.muted = false;
      }
    }
  }, [proctorAudioEnabled, privateAudioTargetUserId, remoteStreams, userId]);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // --- ANTI-CHEAT: DEVTOOLS AND CONTEXT MENU BLOCKERS ---
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Block context menu (right click) except inside inputs/textareas
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e) => {
      // Prevent F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, Ctrl+Shift+C
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j' || e.key === 'C' || e.key === 'c')) ||
        (e.ctrlKey && (e.key === 'U' || e.key === 'u'))
      ) {
        e.preventDefault();
        showToast("Developer tools shortcuts are disabled during the exam.", "warning");
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showToast]);

  const getFullscreenElement = useCallback(() => {
    if (typeof document === 'undefined') return null;
    return (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      document.mozFullScreenElement ||
      null
    );
  }, []);

  const requestExamFullscreen = useCallback(async (showWarning = false) => {
    if (typeof document === 'undefined') return false;
    if (getFullscreenElement()) return true;

    const target = document.documentElement;
    const requestFn =
      target.requestFullscreen ||
      target.webkitRequestFullscreen ||
      target.msRequestFullscreen ||
      target.mozRequestFullScreen;

    if (typeof requestFn !== 'function') {
      if (showWarning) {
        showToast("Fullscreen mode is not supported in this browser.", "warning");
      }
      return false;
    }

    try {
      await requestFn.call(target);
      return true;
    } catch (error) {
      const isPermissionOrGesture = (error?.message || '').includes('Permissions') || (error?.name === 'TypeError' && /permission|gesture|user\s*interaction|denied/i.test(String(error?.message)));
      if (!isPermissionOrGesture) {
        console.warn('Fullscreen request failed:', error);
      }
      if (showWarning) {
        showToast("Fullscreen is mandatory. Press F11 and allow fullscreen to continue.", "warning");
      }
      return false;
    }
  }, [getFullscreenElement, showToast]);

  const attachStream = (videoElement, stream) => {
    if (videoElement && stream) {
      const mediaStream = stream.stream || stream;

      // 🔹 FIX: Ensure all tracks are enabled (important for mobile devices)
      // EXCEPT proctor audio if someone else is in a private call
      const isProctorAudio = stream.userType === 'staff' && stream.kind === 'audio';
      const isProctorVideo = (stream.userType === 'staff' || stream.userType !== 'student') && stream.kind?.includes('video');

      const tracks = mediaStream.getTracks?.() || [];
      tracks.forEach(track => {
        if (track.readyState === 'live' || track.readyState === 'paused') {
          if (isProctorAudio) {
            if (!proctorAudioEnabledRef.current) {
              track.enabled = false;
            } else if (privateAudioTargetUserId !== null) {
              track.enabled = (String(privateAudioTargetUserId) === String(userId));
            } else {
              track.enabled = true;
            }
          } else if (isProctorVideo) {
            track.enabled = proctorVideoEnabledRef.current;
          } else {
            track.enabled = true;
          }
        }
      });

      if (videoElement.srcObject !== mediaStream) {
        videoElement.srcObject = mediaStream;
        videoElement.onloadedmetadata = () => {
          videoElement.play().catch(e => console.warn("Autoplay blocked:", e));
        };
      } else {
        if (videoElement.paused) videoElement.play().catch(e => console.warn(e));
      }
    }
  };

  // --- UNIFIED CAMERA RECOVERY: Robustly re-acquire or resume monitoring streams ---
  const recoverVideoStreams = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return; // No media API (e.g. non-HTTPS); skip to avoid repeated TypeError
    }

    console.log("🔄 Triggering aggressive camera recovery check...");

    // 1. Audit current track states
    localStreams.forEach((s, idx) => {
      const stream = s.stream || s;
      if (stream && stream.getTracks) {
        stream.getTracks().forEach(t => {
          console.log(`📹 Monitor Track ${idx} [${t.kind}]: id=${t.id}, state=${t.readyState}, enabled=${t.enabled}`);
        });
      }
    });

    // 2. Force reset video elements to clear frozen frames
    if (localVideoRef1.current) localVideoRef1.current.srcObject = null;
    if (localVideoRef2.current) localVideoRef2.current.srcObject = null;
    if (checkPreviewRef.current) checkPreviewRef.current.srcObject = null;

    // 3. Detect if tracks were stopped or frozen
    const needsReacquisition = localStreams.length === 0 || localStreams.some(s => {
      const stream = s.stream || s;
      return stream.getTracks().some(t => t.readyState === 'ended' || (t.kind === 'video' && !t.enabled));
    });

    if (needsReacquisition) {
      console.log("📷 Camera tracks ended or missing, re-acquiring monitoring streams...");
      try {
        // A. Try standard re-acquisition via Mediasoup hook
        const reacquired = await startLocalVideo(REQUIRED_CAMERAS);
        const reacquiredLive = Array.isArray(reacquired) && reacquired.some(s =>
          (s.stream || s).getVideoTracks?.().some(t => t.readyState === 'live'));

        // B. Mobile Fallback: If tracks still look dead, manually force a fresh getUserMedia track
        if (!reacquiredLive && navigator.mediaDevices && isMobileDeviceFallback && replaceFirstCameraTrack) {
          console.log("Mobile detected, forcing a manual hardware re-sync...");
          const freshMedia = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
            audio: false
          });
          const freshTrack = freshMedia.getVideoTracks()[0];
          if (freshTrack) {
            await replaceFirstCameraTrack(freshTrack);
            console.log("Manually injected fresh front track into Mediasoup.");
          }
        }
      } catch (err) {
        console.error("❌ Failed to re-acquire monitoring streams:", err);
      }
    } else {
      console.log("✨ Tracks are still live, ensuring they are enabled and playing...");
      localStreams.forEach(streamItem => {
        const stream = streamItem.stream || streamItem;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach(track => { if (track.kind === 'video') track.enabled = true; });
        }
      });
    }

    // 4. Re-bind streams and kickstart render loop with a slight delay
    setTimeout(() => {
      // Re-binding for main monitoring feeds
      if (localVideoRef1.current && localStreams[0]) attachStream(localVideoRef1.current, localStreams[0]);
      if (localVideoRef2.current && localStreams[1]) attachStream(localVideoRef2.current, localStreams[1]);

      // Re-binding for Liveness / Identity Verification (shown on mobile mobile)
      if (isOverlayVisible && checkPreviewRef.current) {
        const livenessStream = livenessCameraIndex === 1 && alternateLivenessStream
          ? alternateLivenessStream
          : (refreshedFrontStream || localStreams[0]);
        if (livenessStream) attachStream(checkPreviewRef.current, livenessStream);
      }

      // Re-binding for Camera Modal (Photo Capture)
      if (showCameraModal && cameraModalVideoRef.current) {
        const modalStream = alternateCameraStream || refreshedFrontStream || (localStreams[0]?.stream || localStreams[0]);
        if (modalStream) attachStream(cameraModalVideoRef.current, modalStream);
      }

      document.querySelectorAll('video').forEach(v => {
        v.play().catch(err => console.warn("Delayed play failed:", err));
      });
      console.log("🎬 Full recovery cycle complete.");
    }, 400); // 400ms delay to ensure DOM and hardware are ready
  }, [localStreams, startLocalVideo, REQUIRED_CAMERAS, isOverlayVisible, localVideoRef1, localVideoRef2, attachStream, isMobileDeviceFallback, replaceFirstCameraTrack, livenessCameraIndex, alternateLivenessStream, refreshedFrontStream, showCameraModal, alternateCameraStream]);

  // --- Direct camera restore after native camera closes ---
  // getUserMedia directly + attach via ref, bypassing stale Mediasoup state.
  // Used after: Take Photo → crop modal closes, or user cancels native camera.
  const doDirectCameraRestore = useCallback(async () => {
    console.log('📷 doDirectCameraRestore: re-acquiring front camera...');
    try {
      if (!navigator.mediaDevices) return;
      const freshStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      const freshTrack = freshStream.getVideoTracks()[0];

      // Inject the fresh track into the existing localStreams[0] MediaStream so that
      // when the useEffect re-attaches localStreams[0] after replaceFirstCameraTrack
      // triggers a Mediasoup state update, it already contains the live track.
      if (localStreams[0]) {
        const existingStream = localStreams[0].stream || localStreams[0];
        if (existingStream && existingStream instanceof MediaStream) {
          existingStream.getVideoTracks().forEach(t => {
            existingStream.removeTrack(t);
            t.stop();
          });
          existingStream.addTrack(freshTrack);
        }
        // Attach the updated existing stream (useEffect will use same ref, no overwrite)
        if (localVideoRef1.current) {
          localVideoRef1.current.srcObject = existingStream || freshStream;
          localVideoRef1.current.play().catch(() => { });
        }
      } else {
        // No localStreams yet — just attach fresh stream directly
        if (localVideoRef1.current) {
          localVideoRef1.current.srcObject = freshStream;
          localVideoRef1.current.play().catch(() => { });
        }
      }

      // Also push new track into Mediasoup producer so proctor feed updates
      if (freshTrack && replaceFirstCameraTrack) {
        await replaceFirstCameraTrack(freshTrack);
      }
      console.log('doDirectCameraRestore: front camera restored.');
    } catch (err) {
      console.error('❌ doDirectCameraRestore failed, falling back:', err);
      setTimeout(() => recoverVideoStreams(), 500);
    }
  }, [localStreams, localVideoRef1, replaceFirstCameraTrack, recoverVideoStreams]);

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const calculateEndTime = (examDate, examTime, durationMinutes) => {
    if (!examDate || !examTime || !durationMinutes) return null;
    const examStartString = `${examDate} ${examTime}`;
    const globalStartTimestamp = new Date(examStartString).getTime();
    const totalDurationMs = parseInt(durationMinutes) * 60 * 1000;
    return globalStartTimestamp + totalDurationMs;
  };

  useEffect(() => {
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
  }, [answers, ANSWERS_KEY]);

  const captureFrame = (video, quality = 0.7, maxWidth = 0) => {
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (maxWidth > 0 && w > maxWidth) {
      h = Math.round((h * maxWidth) / w);
      w = maxWidth;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  };

  /** Compress JPEG data URL to stay under maxBytes (e.g. 15 KB) while preserving visual quality. */
  const INITIAL_PHOTO_MAX_BYTES = 15 * 1024;
  const compressImageToMaxBytes = (dataUrl, maxBytes = INITIAL_PHOTO_MAX_BYTES) => {
    return new Promise((resolve) => {
      const getSize = (url) => {
        const base64 = url.indexOf(',') >= 0 ? url.split(',')[1] : url;
        return base64 ? Math.round((base64.length * 3) / 4) : 0;
      };
      if (getSize(dataUrl) <= maxBytes) {
        resolve(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let w = img.width, h = img.height;
        const scales = [1, 0.85, 0.7, 0.6, 0.5, 0.4, 0.35, 0.3];
        const qualities = [0.92, 0.85, 0.78, 0.7, 0.65, 0.58, 0.5, 0.45];
        for (const scale of scales) {
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          for (const q of qualities) {
            const out = canvas.toDataURL('image/jpeg', q);
            if (getSize(out) <= maxBytes) {
              resolve(out);
              return;
            }
          }
        }
        resolve(canvas.toDataURL('image/jpeg', 0.5));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  // Monitoring: smaller frame = less load; still processes live video correctly
  const MONITOR_CAPTURE_MAX_WIDTH = 640;
  const MONITOR_JPEG_QUALITY = 0.55;

  // Detect paper/document edges automatically
  const detectPaperEdges = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const width = canvas.width;
        const height = canvas.height;

        // Convert to grayscale and apply edge detection
        const grayData = new Uint8ClampedArray(width * height);
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          grayData[i / 4] = gray;
        }

        // Apply Gaussian blur for noise reduction
        const blurred = new Uint8ClampedArray(width * height);
        const kernel = [1, 2, 1, 2, 4, 2, 1, 2, 1];
        const kernelSum = 16;

        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            let sum = 0;
            for (let ky = -1; ky <= 1; ky++) {
              for (let kx = -1; kx <= 1; kx++) {
                const idx = (y + ky) * width + (x + kx);
                const kIdx = (ky + 1) * 3 + (kx + 1);
                sum += grayData[idx] * kernel[kIdx];
              }
            }
            blurred[y * width + x] = sum / kernelSum;
          }
        }

        // Edge detection using Sobel operator
        const edges = new Uint8ClampedArray(width * height);
        const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
        const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            let gx = 0, gy = 0;
            for (let ky = -1; ky <= 1; ky++) {
              for (let kx = -1; kx <= 1; kx++) {
                const idx = (y + ky) * width + (x + kx);
                const kIdx = (ky + 1) * 3 + (kx + 1);
                gx += blurred[idx] * sobelX[kIdx];
                gy += blurred[idx] * sobelY[kIdx];
              }
            }
            const magnitude = Math.sqrt(gx * gx + gy * gy);
            edges[y * width + x] = magnitude > 50 ? 255 : 0; // Threshold
          }
        }

        // Find contours and detect largest rectangular area (paper)
        // Simplified: Find corners by scanning edges
        const margin = Math.min(width, height) * 0.1;
        let minX = margin, minY = margin, maxX = width - margin, maxY = height - margin;

        // Find top-left corner
        for (let y = margin; y < height * 0.5; y++) {
          for (let x = margin; x < width * 0.5; x++) {
            if (edges[y * width + x] > 128) {
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              break;
            }
          }
        }

        // Find bottom-right corner
        for (let y = height - margin; y > height * 0.5; y--) {
          for (let x = width - margin; x > width * 0.5; x--) {
            if (edges[y * width + x] > 128) {
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
              break;
            }
          }
        }

        // Ensure valid rectangle
        if (maxX > minX && maxY > minY && (maxX - minX) > width * 0.3 && (maxY - minY) > height * 0.3) {
          resolve({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            detected: true
          });
        } else {
          // Fallback: use full image
          resolve({
            x: margin,
            y: margin,
            width: width - 2 * margin,
            height: height - 2 * margin,
            detected: false
          });
        }
      };
      img.src = imageDataUrl;
    });
  };

  // Enhanced image processing for better readability (significantly improved)
  const enhanceImage = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // Use high resolution but cap it to avoid extreme file sizes (max 2000px width)
        const maxDimension = 2000;
        let scale = 1;
        if (img.width > maxDimension || img.height > maxDimension) {
          scale = Math.min(maxDimension / img.width, maxDimension / img.height);
        }

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');

        // Apply image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'medium'; // Balance quality/performance

        // Draw image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Get image data for processing
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Apply advanced enhancement filters
        for (let i = 0; i < data.length; i += 4) {
          // Enhanced contrast and brightness
          const contrast = 1.2; // Slightly reduced from 1.5 to avoid over-saturation
          const brightness = 10;

          // Red channel
          data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));
          // Green channel
          data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness));
          // Blue channel
          data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness));
        }

        // Put enhanced image data back
        ctx.putImageData(imageData, 0, 0);

        // Apply unsharp mask for sharpening
        const sharpened = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const sharpData = sharpened.data;
        const originalData = new Uint8ClampedArray(data);

        // Unsharp mask kernel
        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < canvas.width - 1; x++) {
            const idx = (y * canvas.width + x) * 4;
            for (let c = 0; c < 3; c++) {
              const center = originalData[idx + c];
              const blur = (
                originalData[((y - 1) * canvas.width + (x - 1)) * 4 + c] +
                originalData[((y - 1) * canvas.width + x) * 4 + c] +
                originalData[((y - 1) * canvas.width + (x + 1)) * 4 + c] +
                originalData[(y * canvas.width + (x - 1)) * 4 + c] +
                originalData[(y * canvas.width + (x + 1)) * 4 + c] +
                originalData[((y + 1) * canvas.width + (x - 1)) * 4 + c] +
                originalData[((y + 1) * canvas.width + x) * 4 + c] +
                originalData[((y + 1) * canvas.width + (x + 1)) * 4 + c]
              ) / 8;
              const sharp = center + (center - blur) * 1.0; // Reduced sharpening factor from 1.2
              sharpData[idx + c] = Math.min(255, Math.max(0, sharp));
            }
            sharpData[idx + 3] = originalData[idx + 3]; // Alpha
          }
        }

        ctx.putImageData(sharpened, 0, 0);

        // Convert to high quality JPEG (0.9 for better quality, still significantly smaller than 1.0)
        const enhancedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
        resolve(enhancedDataUrl);
      };
      img.src = imageDataUrl;
    });
  };

  // --- LIVENESS LOGIC ---
  const startLiveScanning = useCallback(() => {
    if (isScanning || isHumanVerified || !checkPreviewRef.current || !AI_SERVER_BASE) return;
    setIsScanning(true);
    setLivenessMessage("🔍 Analyzing via Server...");

    if (scanInterval.current) clearInterval(scanInterval.current);

    // Increased interval for low-end devices (2 seconds instead of 1)
    const scanIntervalMs = 2000;
    let consecutiveFailures = 0;
    let infraFailures = 0;
    let nextAttemptAt = 0;

    const scanTick = async () => {
      const video = checkPreviewRef.current;
      if (!video || video.readyState !== 4 || isProcessingFrame.current || isHumanVerified) return;
      if (Date.now() < nextAttemptAt) return;

      isProcessingFrame.current = true;
      // Reduced quality from 0.85 to 0.65 for low-end devices (smaller payload, faster upload)
      const imageBase64 = captureFrame(video, 0.65);

      try {
        const data = await aiApi.verifyLiveness(imageBase64, { timeoutMs: ENV.LIVENESS_TIMEOUT_MS });
        const { AI_OUTCOME } = aiApi;

        if (data.outcome !== AI_OUTCOME.NO_VIOLATION && data.outcome !== AI_OUTCOME.VIOLATION) {
          // AI unreachable / overloaded: back off (2 s, 4 s, 8 s ... capped) so a struggling server is not hammered.
          consecutiveFailures++;
          infraFailures++;
          successCountRef.current = 0;
          nextAttemptAt = Date.now() + Math.min(scanIntervalMs * 2 ** (infraFailures - 1), ENV.LIVENESS_MAX_BACKOFF_MS);
          if (data.outcome === AI_OUTCOME.AI_TIMEOUT) {
            setLivenessMessage("Request timeout. Retrying... (slow connection detected)");
          } else if (data.outcome === AI_OUTCOME.NETWORK_ERROR) {
            setLivenessMessage("Connection failed. Retrying...");
          } else if (data.outcome === AI_OUTCOME.AI_UNAVAILABLE || data.outcome === AI_OUTCOME.AI_RATE_LIMIT) {
            setLivenessMessage("Verification server is busy. Retrying shortly...");
          } else {
            setLivenessMessage("Server error. Retrying...");
          }
          if (consecutiveFailures >= 5) {
            setLivenessMessage("Connection issues. Please check internet and wait...");
          }
          return;
        }
        infraFailures = 0;

        if (data.status === 'success') {
          // Only counted when all rules satisfied and liveness confirmed
          consecutiveFailures = 0;
          successCountRef.current += 1;
          // Show Python success message when provided, else our progress text
          const successMsg = data.message || "Liveness confirmed";
          setLivenessMessage(`${successMsg} — Holding... ${successCountRef.current}/3`);

          if (successCountRef.current >= 3) {
            clearInterval(scanInterval.current);
            setIsScanning(false);
            setIsHumanVerified(true);
            setLivenessMessage("" + (data.message || "Identity Verified!"));
            // handleFinalUpload uploads the snapshot; on mobile also runs face-match
            handleFinalUpload();
          }
        } else {
          // Show the exact message from Python (app.py) on screen
          const serverMessage = data.message || data.error || "Please face the camera and follow the instructions.";
          const serverHint = data.hint ? (data.hint.trim() ? ` ${data.hint}` : "") : "";
          setLivenessMessage(serverMessage + serverHint);
          successCountRef.current = 0;
          consecutiveFailures++;
        }
      } catch (err) {
        console.error("AI Error:", err);
        consecutiveFailures++;
        setLivenessMessage("Server error. Retrying...");
      } finally {
        isProcessingFrame.current = false;
      }
    };

    // Random start offset so students who open the exam together do not hit the AI server in lockstep.
    // clearInterval also cancels this pending timeout (timers share one id pool).
    scanInterval.current = setTimeout(() => {
      scanInterval.current = setInterval(scanTick, scanIntervalMs);
      scanTick();
    }, Math.floor(Math.random() * ENV.LIVENESS_START_JITTER_MS));
  }, [isScanning, isHumanVerified, AI_SERVER_BASE]);

  const handleFinalUpload = async () => {
    setIsUploading(true);
    const video = checkPreviewRef.current || localVideoRef1.current;
    if (!video) { setIsUploading(false); return; }

    // Capture image from the verified liveness check, then compress to <=15 KB for storage/upload
    const rawImage = captureFrame(video, 0.95);
    const highQualityImage = await compressImageToMaxBytes(rawImage, INITIAL_PHOTO_MAX_BYTES);

    // Store as the initial verification photo (only set once during liveness verification)
    if (!capturedImage) {
      setCapturedImage(highQualityImage);
      console.log("Initial verification photo captured and stored for monitoring (<=15 KB)");
    }

    // Wait for assessmentId if not available yet (it will be set from getQuestions response)
    // IMPORTANT: Read from assessmentIdRef.current (NOT assessmentId state).
    // assessmentId state is stale here because this function runs inside a closure
    // created before getQuestions completed. assessmentIdRef.current is always live.
    let currentAssessmentId = assessmentIdRef.current || assessmentId;
    let retryCount = 0;
    const maxRetries = 20; // Wait up to 10 seconds (20 × 500ms)

    while (!currentAssessmentId && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 500));
      currentAssessmentId = assessmentIdRef.current || assessmentId; // Re-check from REF
      retryCount++;
    }

    // Helper: trigger mobile identity verification using the already-captured live image.
    // Defined inline so it always has access to currentAssessmentId and highQualityImage.
    const runMobileIdentityCheck = () => {
      if (isMobileDeviceFallback) {
        // Use ref so the stale closure inside startLiveScanning sees the latest function.
        mobileIdentityVerifyRef.current?.(currentAssessmentId, highQualityImage);
      }
    };

    // Validate required IDs
    if (!currentAssessmentId || !registrationId) {
      console.warn('Missing assessmentId for snapshot upload (will retry later)', {
        assessmentId: currentAssessmentId,
        registrationId,
        note: 'assessmentId will be set when getQuestions completes. Snapshot will be uploaded automatically.'
      });
      // Store image locally - it will be uploaded when assessmentId becomes available
      setLivenessMessage("Identity Verified. Snapshot will be saved when exam starts.");
      setIsUploading(false);
      // Still run identity check if IDs are missing — it will handle the missing ID case gracefully
      runMobileIdentityCheck();
      return;
    }

    try {
      // Check if snapshot already exists (prevent duplicates)
      let checkResult = null;
      try {
        checkResult = await nodeApi.snapshotCheck({
          assessment_id: currentAssessmentId,
          registration_id: registrationId
        }).catch(() => null);
        if (checkResult && checkResult.status !== 'success') checkResult = null;
      } catch (checkErr) {
        console.warn('Snapshot check failed (will attempt upload anyway):', checkErr);
        // Continue to upload even if check fails
      }

      if (!retakeAttempts && checkResult?.status === 'success' && checkResult.exists) {
        // Snapshot already exists, use existing URL (only if not retaking)
        console.log('Snapshot already exists, using existing S3 URL');
        setCapturedImage(checkResult.s3Url || highQualityImage);
        setLivenessMessage("Identity Verified & Snapshot Found.");
        setIsUploading(false);
        // FIX: Must still run identity check even when snapshot already exists
        runMobileIdentityCheck();
        return;
      }

      const result = await nodeApi.snapshotUpload({
        assessment_id: currentAssessmentId,
        registration_id: registrationId,
        image: highQualityImage,
        overwrite: retakeAttempts > 0
      });

      if (result.status === 'success') {
        // Store S3 URL instead of base64
        if (result.s3Url) {
          setCapturedImage(result.s3Url);
        }
        setLivenessMessage("Identity Verified & Snapshot Saved.");
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Snapshot upload error:', err);
      // Don't fail liveness check - snapshot can be retried later
      setCapturedImage(highQualityImage);
      setLivenessMessage("Identity Verified. Snapshot upload will be retried.");
      console.warn('Snapshot upload failed, stored locally. Error:', err.message);
    } finally {
      setIsUploading(false);
    }

    // On mobile: run identity verification after snapshot upload completes
    runMobileIdentityCheck();
  };

  // Ref to always point to the latest handleMobileIdentityVerify.
  // This breaks the stale closure in startLiveScanning's useCallback so the
  // identity check always uses current state/props.
  const mobileIdentityVerifyRef = useRef(null);
  const fetchImageAsBase64Ref = useRef(null);

  /**
   * Mobile-only: Fetch reference photo from S3 (or use override) and compare with live face.
   * @param {string} resolvedAssessmentId - The assessment ID resolved in handleFinalUpload.
   * @param {string} liveImageBase64      - Already-captured live frame (avoids re-capture after scan stops).
   * @param {string} [referenceBase64Override] - Optional. When provided (mobile re-capture flow), use as reference and allow 50% match.
   */
  const handleMobileIdentityVerify = async (resolvedAssessmentId, liveImageBase64, referenceBase64Override) => {
    // assessmentId is passed in to avoid stale-closure issues
    const currentAssessmentId = resolvedAssessmentId || assessmentIdRef.current;
    if (!currentAssessmentId || !registrationId) {
      console.warn('handleMobileIdentityVerify: missing IDs, blocking entry');
      setIdentityCheckStatus('failed');
      setIdentityMessage('❌ Could not complete identity check (missing IDs). Please contact admin.');
      setIsIdentityVerified(false);
      return;
    }

    if (!liveImageBase64) {
      console.warn('handleMobileIdentityVerify: no live image provided, blocking entry');
      setIdentityCheckStatus('failed');
      setIdentityMessage('❌ Could not capture live image. Please try again.');
      setIsIdentityVerified(false);
      return;
    }

    const useLocalReference = !!referenceBase64Override;
    const MOBILE_MATCH_THRESHOLD = 0.60; // Allow 60% match on mobile
    setIdentityCheckStatus('checking');
    setIdentityMessage(useLocalReference ? '🔍 Comparing with your stored photo (60% match required)...' : '🔍 Verifying your identity against stored reference...');

    try {
      let referenceBase64 = referenceBase64Override;

      if (!referenceBase64) {
        console.log('🔄 No local reference provided, fetching from Reference API...');
        // 1. Fetch reference photo from Node server (reads from S3 via Redis key)
        const refData = await nodeApi.snapshotReference({
          assessment_id: currentAssessmentId,
          registration_id: registrationId,
        });

        if (refData.status === 'not_found') {
          // No reference photo stored yet — this is the first login, the current upload IS the reference
          console.log('No prior reference photo — first login, treating as verified.');
          setIdentityCheckStatus('no_reference');
          setIdentityMessage('First login detected. Identity will be verified by the proctor.');
          setIsIdentityVerified(true);
          return;
        }

        if (refData.status !== 'success' || !refData.base64) {
          throw new Error('Failed to load reference photo from server.');
        }
        referenceBase64 = refData.base64;
        console.log('Fetched reference photo from API successfully.');
      } else {
        console.log('Using local/override reference photo for identity check.');
      }


      // 2. Send live image + reference to AI server using the robust monitoring endpoint
      const data = await aiApi.verifyExamMonitoring(
        { reference_image: referenceBase64, current_image: liveImageBase64 },
        typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(30000) : undefined
      );
      console.log('Mobile identity verify result:', data);

      if (data.outcome !== aiApi.AI_OUTCOME.VIOLATION && data.outcome !== aiApi.AI_OUTCOME.NO_VIOLATION) {
        // AI gave no verdict (timeout, overloaded, unreachable): not an identity mismatch.
        console.warn(`Mobile identity verify: no AI verdict (${data.outcome}): ${data.message || ''}`);
        setIdentityCheckStatus('failed');
        setIdentityMessage('... Verification service is busy right now. Please try again in a moment.');
        setIsIdentityVerified(false);
        return;
      }

      if (data.status === 'success') {
        setIdentityCheckStatus('verified');
        setIdentityMessage('Identity verified. You may proceed.');
        setIsIdentityVerified(true);
      } else {
        // Verification failed (Mismatch, No Face, etc.)
        setIdentityCheckStatus('failed');
        setIdentityMessage('❌ ' + (data.message || 'Identity not verified. Please contact the administrator.'));
        setIsIdentityVerified(false);
      }

    } catch (err) {
      console.error('handleMobileIdentityVerify error:', err);
      // HARD FAIL: Block student on network/timeout error
      setIdentityCheckStatus('failed');
      setIdentityMessage('❌ Identity check error. Please check your connection and try again.');
      setIsIdentityVerified(false);
    }
  };

  // Keep ref up to date with the latest function on every render
  useEffect(() => {
    mobileIdentityVerifyRef.current = handleMobileIdentityVerify;
  });

  /**
   * Mobile-only: Capture a fresh frame from the current camera and run identity verification
   * against the stored reference (from desktop/first login). Used when user has existing
   * capturedImage but has not yet passed identity check (e.g. logged in on desktop, then on mobile).
   */
  // const handleCaptureAndVerifyIdentity = useCallback(async () => {
  //   if (!isMobileDeviceFallback || !localStreams?.length) return;
  //   const stream = localStreams[0]?.stream || localStreams[0];
  //   if (!stream) return;
  //   setIdentityCheckStatus('checking');
  //   setIdentityMessage('🔍 Capturing image to verify identity...');
  //   let referenceBase64 = null;
  //   if (capturedImage && capturedImage.startsWith('data:image')) {
  //     referenceBase64 = capturedImage;
  //   } else if (capturedImage && capturedImage.startsWith('http')) {
  //     try {
  //       const fetchFn = fetchImageAsBase64Ref.current;
  //       if (!fetchFn) throw new Error('Image loader not ready');
  //       referenceBase64 = await fetchFn(capturedImage);
  //     } catch (e) {
  //       console.error('Failed to load reference image:', e);
  //       setIdentityCheckStatus('failed');
  //       setIdentityMessage('Could not load your stored photo. Please try again.');
  //       setIsIdentityVerified(false);
  //       return;
  //     }
  //   }
  //   if (!referenceBase64) {
  //     setIdentityCheckStatus('failed');
  //     setIdentityMessage('No reference photo available. Please contact the administrator.');
  //     setIsIdentityVerified(false);
  //     return;
  //   }
  //   const video = document.createElement('video');
  //   video.muted = true;
  //   video.playsInline = true;
  //   video.autoplay = true;
  //   video.srcObject = stream;
  //   try {
  //     await new Promise((resolve, reject) => {
  //       video.onloadeddata = () => resolve();
  //       video.onerror = (e) => reject(e);
  //       setTimeout(() => reject(new Error('Video load timeout')), 5000);
  //     });
  //     await new Promise(r => setTimeout(r, 300));
  //     const rawImage = captureFrame(video, 0.95);
  //     const highQualityImage = await compressImageToMaxBytes(rawImage, INITIAL_PHOTO_MAX_BYTES);
  //     video.srcObject = null;
  //     const currentAssessmentId = assessmentIdRef.current || assessmentId;
  //     const verify = mobileIdentityVerifyRef.current;
  //     if (verify) await verify(currentAssessmentId, highQualityImage, referenceBase64);
  //   } catch (err) {
  //     console.error('Capture and verify identity error:', err);
  //     setIdentityCheckStatus('failed');
  //     setIdentityMessage('Could not capture image. Please try again.');
  //     setIsIdentityVerified(false);
  //   }
  // }, [isMobileDeviceFallback, localStreams, capturedImage]);

  const handleActivateSystem = async () => {
    setIsConfiguring(true);
    try {
      if (localStreams.length < REQUIRED_CAMERAS) await startLocalVideo(REQUIRED_CAMERAS);
      // Only attempt screen sharing on desktop devices - skip completely on mobile
      if (!isScreenSharing && !isMobileDevice) {
        try {
          const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

          if (isFirefox || isSafari) {
            setShowScreenShareButton(true);
          } else {
            // For Chrome/Edge, we can safely request fullscreen first, then screen share
            await requestExamFullscreen(true);
            await startScreenShare();
          }
        } catch (err) {
          // Only log errors on desktop, don't show alerts
          console.error("Screen sharing error:", err);
        }
      }
      // On mobile, screen sharing is automatically skipped in startScreenShare()
    } catch (err) {
      console.error("Activate system error:", err);
    }
    finally { setIsConfiguring(false); }
  };

  // 🔹 REMOVED: Auto-start useEffect. Camera starts only when user clicks the "Activate Camera" button.
  // useEffect(() => {
  //   if (activationRunRef.current) return;
  //   activationRunRef.current = true;
  //   handleActivateSystem();
  // }, []);
  const handleRetake = () => {
    // Clean up alternate liveness stream if exists
    if (alternateLivenessStream) {
      alternateLivenessStream.getTracks().forEach(track => track.stop());
      setAlternateLivenessStream(null);
    }
    setCapturedImage(null);
    setIsHumanVerified(false);
    successCountRef.current = 0;
    setLivenessMessage("Restarting Scan...");
    setIsScanning(false);
    setLivenessCameraIndex(0); // Reset to first camera
    setRetakeAttempts(prev => prev + 1);
  };
  const handleConfirmAndStart = async () => {
    // 🔹 FIX: Allow proceeding if capturedImage exists, even without isHumanVerified (liveness optional once image stored)
    if (!capturedImage) return;

    if (localStreams.length < REQUIRED_CAMERAS) {
      showToast(`${REQUIRED_CAMERAS} camera(s) required to join the exam. Please connect and activate device(s).`, "warning");
      return;
    }
    // Screen sharing is only required on desktop devices
    if (!isScreenSharing && !isMobileDevice) {
      showToast("Screen sharing is mandatory to enter the exam on desktop browsers.", "warning");
      return;
    }

    const fullscreenOk = await requestExamFullscreen(true);
    if (!fullscreenOk) return;

    setIsOverlayVisible(false);
    setIsStudentReady(true);
  };

  // Helper: Convert S3 URL to base64 data URL via backend proxy (avoids CORS)
  const fetchImageAsBase64 = useCallback(async (imageUrl) => {
    if (!imageUrl || imageUrl.startsWith('data:image')) {
      return imageUrl; // Already base64
    }

    // If it's an S3 URL, use backend proxy to avoid CORS
    if (imageUrl.startsWith('http')) {
      try {
        // Use backend endpoint to fetch image and return base64
        const result = await nodeApi.snapshotGetBase64({
          image_url: imageUrl,
          assessment_id: assessmentId,
          registration_id: registrationId
        }).catch(() => null);

        if (result && result.status === 'success' && result.base64) {
          return result.base64;
        }

        // Fallback: if backend endpoint doesn't exist, log error and throw
        // Direct fetch will fail with CORS - backend endpoint must be implemented
        console.error('❌ Backend /api/snapshot/get-base64 endpoint not available!');
        console.error('   This endpoint is required to avoid CORS errors when fetching S3 images.');
        console.error('   Please add the endpoint from BACKEND_ENDPOINT_TO_ADD.js to your server.');
        throw new Error('Backend proxy endpoint not available. Cannot fetch image due to CORS restrictions.');
      } catch (err) {
        console.error('Failed to convert S3 URL to base64:', err);
        throw err;
      }
    }

    return imageUrl;
  }, [assessmentId, registrationId]);

  useEffect(() => {
    fetchImageAsBase64Ref.current = fetchImageAsBase64;
  });

  const consecutiveNoFaceRef = useRef(0); // 🔹 Tracks consecutive "looking_down" warnings
  // AI failures (timeout, 5xx, rate limit...) are not violations; back off instead of hammering the AI server.
  const aiBackoffUntilRef = useRef(0);
  const aiErrorCountRef = useRef(0);
  const aiErrorNoticeShownRef = useRef(false);

  // AI monitoring uses cam-1 (first camera) only; liveness and take-photo also use cam-1
  // Compares current frame with initial verification photo from verify-liveness
  const runMonitoringCheck = useCallback(async () => {
    if (Date.now() < aiBackoffUntilRef.current) return;
    if (!localStreams?.length || isMonitoringProcessingRef.current || !AI_SERVER_BASE) {
      if (!localStreams?.length) console.log('🎥 Monitoring skipped: No local streams');
      if (isMonitoringProcessingRef.current) console.log('🎥 Monitoring skipped: already processing');
      if (!AI_SERVER_BASE) console.warn('🎥 Monitoring skipped: AI_API_BASE missing');
      return;
    }
    if (!capturedImage) {
      console.warn('Monitoring check skipped: capturedImage (reference photo) not loaded yet. Will retry on next interval.');
      return;
    }

    console.log(`🎥 Starting monitoring check for ${userId} in room ${currentRoomId}...`);

    if (isMobileDevice) {
      setIdentityCheckStatus('checking');
    }
    // On mobile identity verification overlay (second device), live feed is on checkPreviewRef; use it for capture
    const overlayVideo = checkPreviewRef.current;
    const mainVideo = localVideoRef1.current;
    const video = (isMobileDevice && overlayVideo && (overlayVideo.readyState >= 2 || !mainVideo?.readyState)) ? overlayVideo : mainVideo;
    if (!video || video.readyState < 2) {
      if (isMobileDevice) setIdentityCheckStatus('failed');
      return;
    }

    isMonitoringProcessingRef.current = true;
    let currentFrame;
    try {
      currentFrame = captureFrame(video, MONITOR_JPEG_QUALITY, MONITOR_CAPTURE_MAX_WIDTH);
    } catch (frameErr) {
      console.warn('Capture frame failed:', frameErr);
      isMonitoringProcessingRef.current = false;
      if (isMobileDevice) setIdentityCheckStatus('failed');
      return;
    }

    try {
      // Use cached base64 image (no S3 calls during monitoring)
      // If capturedImage is already base64 (starts with 'data:image'), use it directly
      // Only convert if it's still an S3 URL (second device: reference from S3)
      let referenceImageBase64 = capturedImage;
      if (capturedImage.startsWith('http')) {
        console.warn('Converting S3 URL to base64 during monitoring (should be cached)');
        try {
          referenceImageBase64 = await fetchImageAsBase64(capturedImage);
        } catch (err) {
          console.error('Failed to fetch reference image from S3 URL:', err);
          if (isMobileDevice) {
            setIdentityCheckStatus('failed');
            setIdentityMessage('Could not load your stored photo. Please try again.');
          }
          return;
        }
      }

      // Compare current frame with initial verification photo from verify-liveness
      // Timeout 25s so AI has time to run (no_face, multiple_faces, spoof, person_not_recognized)
      const timeoutMs = 25000;
      const signal = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(timeoutMs) : undefined;
      let data;
      try {
        data = await aiApi.verifyExamMonitoring({
          reference_image: referenceImageBase64,
          current_image: currentFrame,
        }, signal);
      } catch (err) {
        console.warn('Monitoring check failed (timeout or network):', err);
        if (isMobileDevice) {
          setIdentityMessage('❌ Verification request failed. Check connection and try again.');
          setIdentityCheckStatus('failed');
        }
        return;
      }

      if (!data) return;

      if (data.outcome !== aiApi.AI_OUTCOME.VIOLATION && data.outcome !== aiApi.AI_OUTCOME.NO_VIOLATION) {
        // No verdict from the AI: do not count, record, upload or report anything as a violation.
        const delay = Math.min(60000, 2000 * Math.pow(2, aiErrorCountRef.current));
        aiErrorCountRef.current += 1;
        aiBackoffUntilRef.current = Date.now() + delay;
        console.warn(`🎥 Monitoring unavailable (${data.outcome}${data.error_code ? `/${data.error_code}` : ''}): ${data.message || ''}. Next check in ${Math.round(delay / 1000)}s.`);
        if (!aiErrorNoticeShownRef.current) {
          aiErrorNoticeShownRef.current = true;
          showToast('AI monitoring is temporarily busy. Your exam continues normally.', 'info');
        }
        if (isMobileDevice) {
          setIdentityCheckStatus('checking');
          setIdentityMessage('... Verification service is busy. Retrying automatically...');
          setTimeout(() => {
            runMonitoringCheck();
          }, delay + 100);
        }
        return;
      }
      aiErrorCountRef.current = 0;
      aiErrorNoticeShownRef.current = false;

      if (data.status === 'success') {
        // 🔹 FIX: Cascading 'looking_down' into a hard 'no_face' violation
        if (data.warning_type === 'looking_down') {
          consecutiveNoFaceRef.current += 1;
          console.log(`Looking down detected (Count: ${consecutiveNoFaceRef.current})`);

          if (consecutiveNoFaceRef.current > 5) {
            // Override success to FAIL because person is missing for too long
            console.log(`❌ Person missing for too long. Triggering hard 'no_face' violation.`);
            data.status = 'fail';
            data.outcome = aiApi.AI_OUTCOME.VIOLATION;
            data.warning_type = 'no_face';
            data.message = 'Face not detected for a long time. Please keep your face in the camera frame.';
            // Do not reset counter here, let it stay high so it keeps logging violations if user stays gone
          }
        } else {
          // Normal success (face detected correctly), so reset the counter
          consecutiveNoFaceRef.current = 0;
        }
      } else {
        // If it's a real fail (spoof, multiple faces, etc), also reset the no-face counter
        consecutiveNoFaceRef.current = 0;
      }

      // Re-evaluate data.status after override above
      if (data.status === 'success') {
        setMonitoringPassedOnce(true); // Allow recording to start (only after verify-exam-monitoring success)
        if (isMobileDevice && data.status === 'success') {
          setIdentityCheckStatus('verified');
          setIdentityMessage('Identity verified. You may proceed.');
          setIsIdentityVerified(true);
          // NOTE: setShowUploadSection(true) is intentionally NOT called here.
          // The mobile shortcut useEffect handles the transition to the upload screen
          // ONLY after the laptop/desktop has also joined the room (laptopJoined check).
          // This ensures the "Waiting for Laptop Camera" screen shows correctly on mobile.
        }
        // Clear warning when monitoring check passes
        setCameraWarning(null);
      } else if (data.status !== 'success') {
        if (isMobileDevice && data.status !== 'success') {
          setIdentityCheckStatus('checking');
          setIdentityMessage('❌ ' + (data.message || 'Identity not verified. Please contact the administrator.'));
          setIsIdentityVerified(false);
          // Auto-retry after a short delay since the interval might be stopped
          setTimeout(() => {
            runMonitoringCheck();
          }, 2000);
        }
        const msg = data.message || 'Monitoring check failed';
        let warningType = data.warning_type || 'other';

        // Normalize warning_type (trim whitespace, handle case variations)
        warningType = String(warningType).trim();

        // Debug logging
        console.log('Monitoring warning received:', { warningType, message: msg, fullData: data });

        // Handle all warning types with specific messages
        let displayMessage = msg;

        // Use if-else for more reliable matching (handles variations)
        const normalizedType = warningType.toLowerCase().trim();

        if (normalizedType === 'no_face') {
          displayMessage = "Face not detected. Please face the camera.";
        } else if (normalizedType === 'multiple_faces') {
          displayMessage = "Multiple faces detected. Only you should be in frame.";
        } else if (normalizedType === 'person_not_recognized' ||
          normalizedType === 'person not recognized' ||
          normalizedType === 'different_person' ||
          normalizedType.includes('person not recognized') ||
          normalizedType.includes('different person')) {
          displayMessage = "Person not recognized. Please ensure you are the registered examinee.";
        } else if (normalizedType === 'spoof_detected' || normalizedType.includes('spoof')) {
          displayMessage = "Possible photo or screen detected. Please use a live camera with your real face.";
        } else if (normalizedType === 'other') {
          displayMessage = msg;
        } else {
          // Default fallback - use server message
          displayMessage = msg;
          console.warn('Unknown warning_type:', warningType, 'normalized:', normalizedType);
        }

        // Show warning on camera overlay
        setCameraWarning(displayMessage);
        // Auto-clear after 5 seconds
        setTimeout(() => setCameraWarning(null), 5000);

        // ALWAYS show toast for all monitoring warnings including no_face
        showToast(`${displayMessage}`, 'warning');

        // A condition that persists (checked every 2 s) is reported once per interval per type, not every check.
        if (!allowViolationReportRef.current(warningType)) {
          console.log(`Violation ${warningType} already reported within ${ENV.VIOLATION_REPEAT_INTERVAL_SEC}s; not re-sent`);
          return;
        }

        // Emit monitoring-warning to server for real-time proctor notification.
        // Retry up to 3 times with 2s interval if socket is temporarily disconnected.
        const emitWarning = (attemptsLeft) => {
          if (socketRef.current && socketRef.current.connected) {
            console.log(`📡 Emitting monitoring-warning: ${warningType} for ${userId}`);
            socketRef.current.emit('monitoring-warning', {
              roomId: String(currentRoomId),
              userId: String(userId),
              warningType: String(warningType),
            });
          } else if (attemptsLeft > 0) {
            console.warn(`... Socket not connected. Retrying monitoring-warning in 2s (${attemptsLeft} attempts left)...`);
            setTimeout(() => emitWarning(attemptsLeft - 1), 2000);
          } else {
            console.warn('Could not emit monitoring-warning — socket stayed disconnected. Violation is saved via HTTP.');
          }
        };
        emitWarning(3);

        // Log violation in background (single API for all violation types)
        const logViolation = async () => {
          let imageS3Path = null;

          // 1. Upload image to S3 (Node.js) if available
          if (currentFrame && registrationId && assessmentId) {
            try {
              const formData = new FormData();
              formData.append('assessment_id', String(assessmentId));
              formData.append('registration_id', String(registrationId));
              formData.append('user_id', String(userId));
              formData.append('image', currentFrame);
              formData.append('violation_type', warningType);

              const uploadData = await nodeApi.violationUpload(formData);
              if (uploadData && uploadData.status === 'success') {
                imageS3Path = uploadData.s3Key;
              }
            } catch (e) {
              console.warn('Violation image upload failed:', e);
            }
          }

          // 2. Log metadata to DB (PHP)
          const payload = {
            violation_type: warningType,
            user_id: userId,
            message: data.message || displayMessage,
            image_s3_path: imageS3Path, // Send S3 Key instead of base64
            course_code: decoded?.course,
            exam_date: decoded?.exam_date,
            exam_time: decoded?.exam_time,
          };

          if (registrationId) payload.registration_id = registrationId;

          phpApi.recordViolation(payload)
            .then((result) => {
              if (result && result.status === 'success') console.log('Violation logged');
            })
            .catch(() => { });
        };

        logViolation();
      }
    } catch (err) {
      console.warn('Monitoring check request failed:', err);
    } finally {
      isMonitoringProcessingRef.current = false;
    }
  }, [capturedImage, currentRoomId, userId, registrationId, assessmentId, AI_SERVER_BASE, socketRef, fetchImageAsBase64, localStreams, decoded, isMobileDevice, localVideoRef1, showToast]);

  // --- VIDEO RECORDING: Exactly 1-Minute Chunks ---

  // Presigned PUT straight to S3; the Node /part route only when the browser cannot reach S3. Both refuse to
  // overwrite an existing object, so retries are idempotent.
  const chunkUploaderRef = useRef(null);
  if (!chunkUploaderRef.current) {
    chunkUploaderRef.current = createChunkUploader({
      directEnabled: ENV.RECORDING_DIRECT_S3,
      delays: VIDEO_CHUNK_RETRY_DELAYS_MS,
      presign: (item) => nodeApi.recordingPresign({
        uploadId: String(item.registrationId),
        device: item.device,
        filename: item.filename,
        size: item.blob.size,
        contentType: item.blob.type,
      }),
      putToS3: nodeApi.putToS3,
      uploadViaServer: async (item) => {
        const formData = new FormData();
        formData.append('uploadId', String(item.registrationId));
        formData.append('filename', item.filename);
        formData.append('device', item.device);
        formData.append('chunk', item.blob, item.filename);
        const data = await nodeApi.recordingPart(formData);
        if (!data || data.status !== 'success') throw new Error(data?.message || 'chunk upload failed');
        return data;
      },
    });
  }

  const isMobileDeviceRef = useRef(isMobileDevice);
  isMobileDeviceRef.current = isMobileDevice;

  const sendVideoChunk = useCallback(async ({ blob, filename: uniqueFilename, registrationId: chunkRegistrationId, device }) => {
    const resolvedDevice = device || recordingDevice(isMobileDeviceRef.current);
    try {
      const r = await chunkUploaderRef.current({
        blob,
        filename: uniqueFilename,
        registrationId: chunkRegistrationId,
        device: resolvedDevice,
      });
      if (r.ok) {
        setRecordingChunksUploaded((n) => n + 1);
        console.log(`✅ Uploaded 1-minute chunk (${r.via === 'direct' ? 'direct to S3' : 'via server'}${r.duplicate ? ', already stored' : ''}): ${uniqueFilename}`);
        return true;
      }
      console.warn('[Recording] chunk upload failed', {
        stage: 'upload', registrationId: chunkRegistrationId, device: resolvedDevice, filename: uniqueFilename,
        message: r.err?.message, status: r.err?.status,
      });
      return false;
    } catch (e) {
      console.warn('[Recording] chunk upload threw', {
        stage: 'upload', registrationId: chunkRegistrationId, device: resolvedDevice, filename: uniqueFilename, message: e?.message || String(e),
      });
      return false;
    }
  }, []);

  const sendVideoChunkRef = useRef(sendVideoChunk);
  sendVideoChunkRef.current = sendVideoChunk;

  // Chunks upload one at a time; a slow uplink makes chunks wait (bounded) instead of piling up concurrently.
  const recordingQueueRef = useRef(null);
  const recordingNamesRef = useRef(null);
  if (!recordingNamesRef.current) recordingNamesRef.current = new Set();
  if (!recordingQueueRef.current) {
    recordingQueueRef.current = createBoundedUploadQueue({
      maxItems: ENV.RECORDING_QUEUE_MAX_CHUNKS,
      maxBytes: ENV.RECORDING_QUEUE_MAX_MB * 1024 * 1024,
      upload: (item) => sendVideoChunkRef.current(item),
      onDrop: (item, reason, s) => console.warn('[Recording] chunk dropped', {
        stage: 'queue', reason, filename: item.filename, registrationId: item.registrationId, device: item.device,
        bytes: item.size, waiting: s.waiting, dropped: s.dropped,
      }),
    });
  }

  const uploadVideoChunk = useCallback((blob) => {
    const registrationId = registrationIdRef.current;
    const device = recordingDevice(isMobileDeviceRef.current);
    if (!registrationId) {
      console.warn('[Recording] chunk dropped', { stage: 'no-registration', device, size: blob?.size || 0 });
      return;
    }
    if (!blob || blob.size < 1000) {
      console.warn('[Recording] chunk not uploaded', { stage: 'chunk-too-small', registrationId, device, size: blob?.size || 0 });
      return;
    }

    // Name the chunk when it is captured, not when its upload starts. Same-second names get a _N suffix
    // so a retry of one chunk cannot be mistaken for a different chunk (S3 create-only writes).
    const filename = nextChunkFilename(recordingNamesRef.current, new Date());
    recordingQueueRef.current.enqueue({
      blob,
      size: blob.size,
      filename,
      registrationId,
      device,
    });
  }, []);

  useEffect(() => {
    uploadVideoChunkRef.current = uploadVideoChunk;
  }, [uploadVideoChunk]);

  // --- 2. EXAM LOGIC (SYNCED TIMER UPDATE) ---
  const fetchQuestionsImplRef = useRef(null);
  const fetchQuestionsInFlightRef = useRef(null);
  const fetchQuestionsErrorCountRef = useRef(0);
  const fetchQuestionsRetryTimerRef = useRef(null);
  const FETCH_QUESTIONS_MAX_ERROR_RETRIES = 3;

  // Transport/5xx failures only; 4xx (expired token, bad request) and PHP business errors are not retried.
  const scheduleFetchQuestionsRetry = (httpStatus) => {
    if (httpStatus && httpStatus >= 400 && httpStatus < 500) return;
    if (fetchQuestionsRetryTimerRef.current) return;
    if (fetchQuestionsErrorCountRef.current >= FETCH_QUESTIONS_MAX_ERROR_RETRIES) {
      console.warn('getQuestions failed repeatedly; waiting for the next scheduled refresh.');
      return;
    }
    const delay = 2000 * Math.pow(2, fetchQuestionsErrorCountRef.current);
    fetchQuestionsErrorCountRef.current += 1;
    fetchQuestionsRetryTimerRef.current = setTimeout(() => {
      fetchQuestionsRetryTimerRef.current = null;
      fetchQuestionsRef.current?.();
    }, delay);
  };

  const fetchQuestionsImpl = async (retryCount = 0) => {
    setIsLoadingQuestions(true);
    try {
      let result;
      try {
        result = await phpApi.getQuestions({ user_id: userId, room_id: currentRoomId, registration_id: registrationId });
      } catch (e) {
        console.warn('getQuestions request failed:', e?.message || e);
        result = { status: 'error', httpStatus: e?.status, transportError: true };
      }
      if (result?.transportError) {
        if (result.httpStatus === 401) {
          showToast("Your exam session has expired. Please re-open the exam from the portal.", "error");
        }
        scheduleFetchQuestionsRetry(result.httpStatus);
      } else {
        fetchQuestionsErrorCountRef.current = 0;
      }
      if (result.status === 'success') {
        const info = result.info;
        const examStatus = String(info.exam_status || '').toUpperCase();

        setIsOfflineMode(info.is_offline_exam);
        setQuestionPaperPath(info.question_paper_path || null);
        setQuestionPaperUrl(info.question_paper_url || null);
        if (info.logo_url) setInstitutionLogoUrl(info.logo_url);
        if (info.register_number != null) setApiRegisterNumber(info.register_number);
        if (info.room_id != null) setApiRoomId(info.room_id);
        if (info.assessment_id != null) setAssessmentId(info.assessment_id);

        if (info.exam_date && info.exam_time) {
          try {
            const schedule = parseExamSchedule(info.exam_date, info.exam_time, info.duration_minutes);
            if (schedule?.startMs) {
              examScheduledStartEpochRef.current = schedule.startMs;
              const d = new Date(schedule.startMs);
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const year = d.getFullYear();
              let hour = d.getHours();
              const ampm = hour >= 12 ? 'PM' : 'AM';
              hour = hour % 12 || 12;
              const minutes = String(d.getMinutes()).padStart(2, '0');
              setExamScheduledStart(`${day}-${month}-${year} | ${hour}:${minutes} ${ampm}`);
            }
          } catch (err) {
            console.error("Could not format exam scheduled time", err);
          }
        }
        setGetQuestionsTriggeredOnce(true);


        if (info.exam_completed === 1) {
          setExamStarted(false);
          setIsOverlayVisible(false);
          setIsStudentReady(true);
          // STRICT RULE: If exam is completed, BLOCK everything.
          // Do not allow further uploads even if it's an offline exam.
          setExamFinished(true);
          setShowUploadSection(false);

          // Optional: You could redirect here if you want to force them out immediately
          // window.location.href = `${BASE_URL}/studentDashboard`;
        } else if (info.remaining_seconds <= 0 && examStatus !== 'WAITING') {
          setIsTimeExceeded(true);
          setShowUploadSection(false);
          // Duration reduced below the elapsed time: stop the running countdown as well.
          if (examEndTimeRef.current && examEndTimeRef.current > Date.now() + serverOffset) {
            const endedAt = Date.now() + serverOffset;
            examEndTimeRef.current = endedAt;
            setExamEndTime(endedAt);
          }
          // Auto Submit disabled as per requirement
          // handleAutoSubmit();
        } else if (examStatus === 'WAITING') {
          setIsTimeExceeded(false);
          setShowUploadSection(false);
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_STOPPED_KEY)) {
          setIsExamStoppedByInvigilator(true);
        }
          // If we received exam-started socket event but API still says WAITING, retry (PHP might not have updated yet)
          if (examStartedSocketEventRef.current && retryCount < 5) {
            console.log(`API returned WAITING but socket event received. Retrying fetchQuestions... (${retryCount + 1}/5)`);
            setTimeout(() => {
              fetchQuestions(retryCount + 1);
            }, 1000);
            return;
          }
          // Only set to false if we haven't received the socket event
          if (!examStartedSocketEventRef.current) {
            console.log('Exam status: WAITING (no socket event received yet)');
            setExamStarted(false);
            showToast("Waiting for Proctor to start the timer...", "info");
          } else {
            console.log('... Exam status: WAITING but socket event received - keeping examStarted=true and retrying...');
          }
        } else {
          setIsTimeExceeded(false);
          if (
            isExamStoppedByInvigilatorRef.current
            || isStudentPausedByInvigilatorRef.current
            || (typeof sessionStorage !== 'undefined' && (
              sessionStorage.getItem(EXAM_STOPPED_KEY) || sessionStorage.getItem(EXAM_PAUSED_KEY)
            ))
          ) {
            console.log('Exam is stopped/paused by invigilator - blocking live timer restore');
            setExamStarted(false);
            setExamEndTime(null);
            setShowUploadSection(false);
            if (isExamStoppedByInvigilatorRef.current || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_STOPPED_KEY))) {
              setIsExamStoppedByInvigilator(true);
              isExamStoppedByInvigilatorRef.current = true;
            }
            if (isStudentPausedByInvigilatorRef.current || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_PAUSED_KEY))) {
              setIsStudentPausedByInvigilator(true);
              isStudentPausedByInvigilatorRef.current = true;
            }
            return;
          }
          // Exam is LIVE - getQuestions was triggered and returned success; allow video recording to start
          console.log('Exam status: LIVE - API has caught up with socket event');
          // 🔹 FIX: Keep examStartedSocketEventRef.current = true to prevent PHP fallback from overwriting socket timer
          setExamStarted(true);

          // 🔹 FIX: Store student-specific duration from PHP in ref for future socket events
          if (info.duration_minutes) {
            studentDurationRef.current = info.duration_minutes;
          }

          // PHP holds this student's own duration (changed by admin/invigilator at any time), so its end time
          // (scheduled start + duration_minutes) wins over the room-level duration used by the socket timer.
          const scheduledEnd = (info.exam_date && info.exam_time)
            ? calculateLiveEndTime(info.duration_minutes, null, info.exam_date, info.exam_time)
            : null;
          let calculatedEnd = scheduledEnd;
          if (!calculatedEnd && !examEndTimeRef.current && info.remaining_seconds != null && info.remaining_seconds > 0) {
            calculatedEnd = Date.now() + serverOffset + (info.remaining_seconds * 1000);
          }
          if (calculatedEnd && (!examEndTimeRef.current || Math.abs(calculatedEnd - examEndTimeRef.current) > 1000)) {
            examEndTimeRef.current = calculatedEnd;
            setExamEndTime(calculatedEnd);
            const durationMinutes = parseInt(info.duration_minutes, 10) || 60;
            examStartedAtRef.current = calculatedEnd - (durationMinutes * 60 * 1000);
            console.log(`⏰ Timer set from PHP: examEndTime=${new Date(calculatedEnd).toISOString()} (duration ${info.duration_minutes} min)`);
          }
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch questions. Check connection.", "error");
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  fetchQuestionsImplRef.current = fetchQuestionsImpl;

  // Stable identity so effects that depend on it do not re-run (and re-fetch) on every render.
  // Concurrent callers share the in-flight request.
  const fetchQuestions = useCallback((retryCount = 0) => {
    if (fetchQuestionsInFlightRef.current) return fetchQuestionsInFlightRef.current;
    const p = Promise.resolve()
      .then(() => fetchQuestionsImplRef.current(retryCount))
      .finally(() => {
        fetchQuestionsInFlightRef.current = null;
      });
    fetchQuestionsInFlightRef.current = p;
    return p;
  }, []);
  const fetchQuestionsRef = useRef(fetchQuestions);
  fetchQuestionsRef.current = fetchQuestions;

  useEffect(() => () => {
    if (fetchQuestionsRetryTimerRef.current) clearTimeout(fetchQuestionsRetryTimerRef.current);
  }, []);

  // Offline: only proceed to upload screen (exam is NOT submitted until upload completes)
  const handleProceedToUpload = () => {

    setConfirmModal({
      message: "You will be taken to the upload screen. You must upload your answer sheets (PDF or photos) to complete the exam. The exam will be submitted only after a successful upload.",
      onConfirm: () => {
        setConfirmModal(null);
        setShowUploadSection(true);
      }
    });
  };

  const closeFullPreview = () => {
    setIsFullPreviewOpen(false);
    recoverVideoStreams();
  };

  const handleOfflineClose = async () => {
    try {
      let data;
      try {
        data = await phpApi.checkExamStatus({ registration_id: registrationId });
      } catch (e) {
        data = { status: 'error' };
      }
      if (data.status === 'success' && data.exam_completed === 1) {
        // 🔹 STRENGTHENED: Wait for recording finalization if an upload is active
        if (recordingUploadIdRef.current) {
          console.log("... Ensuring recording is finalized before closure...");
          recordingFinalizingRef.current = true;

          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          } else {
            // Already stopped but /end not called? Trigger manually.
            uploadVideoChunk(null);
          }

          // The final chunk arrives via ondataavailable shortly after stop(); then wait (max 10 s total) for the queue.
          await new Promise(r => setTimeout(r, 500));
          const drained = await recordingQueueRef.current.drain(9500);
          console.log(drained ? "Recording finalized." : "Wait timeout, proceeding to close.");
        }
        window.location.href = `${BASE_URL}`;
      }
      else {
        setConfirmModal({
          title: "Submission Required",
          message: "Please submit your answers on your mobile device before closing the examination window.",
          confirmText: "OK",
          hideCancel: true,
          onConfirm: () => setConfirmModal(null)
        });
      }
    } catch (err) {
      console.error("Error checking exam status:", err);
      // Fallback showToast if available, otherwise setToast
      if (typeof showToast === 'function') {
        showToast("Failed to verify exam status. Please try again.", "error");
      } else {
        setToast({ type: 'error', message: 'Failed to verify exam status. Please try again.' });
        setTimeout(() => setToast(null), 5000);
      }
    }
  };

  const confirmSubmission = () => {
    if (isOfflineMode) {
      handleProceedToUpload();
      return;
    }
    setConfirmModal({
      message: "Are you sure you want to submit your answers?",
      onConfirm: handleSubmitExam
    });
  };

  // Call submit API only (used after upload success in offline mode, and for online submit)
  const submitExamApiCall = useCallback(async (answerSheetPath = null) => {
    const payload = {
      user_id: userId,
      room_id: currentRoomId,
      registration_id: registrationId,
      answers: answers,
      questions_map: questionsMap
    };

    if (answerSheetPath) {
      payload.answer_sheet_path = answerSheetPath;
    }

    let data;
    try {
      data = await phpApi.submitExam(payload);
    } catch (e) {
      throw new Error(e.message || 'Submit failed');
    }

    // Check key message
    if (data.status !== 'success') {
      if (data.message === 'Exam already submitted.') {
        showToast("Warning: Exam already submitted.", "warning");
        // setExamFinished(true);
        // setShowUploadSection(false);
        return; // Treat as success/handled
      }
      throw new Error(data.message || 'Submit failed');
    }
  }, [userId, currentRoomId, registrationId, answers, questionsMap]);

  // Validate snapshot on page load/reconnection OR upload pending snapshot when assessmentId becomes available
  // OPTIMIZED: Prevents duplicate calls and excessive network hits
  useEffect(() => {
    const validateAndUploadSnapshot = async () => {
      // Early returns to prevent unnecessary calls
      if (!assessmentId || !registrationId) {
        snapshotValidatedRef.current = false; // Reset when IDs change
        return; // Wait for IDs
      }

      // Prevent duplicate concurrent calls
      if (snapshotValidationInProgressRef.current) {
        console.log('⏸️ Snapshot validation already in progress, skipping duplicate call');
        return;
      }

      // If we already have base64 and IDs haven't changed, skip validation
      const currentIdsKey = `${assessmentId}:${registrationId}`;
      if (snapshotValidatedRef.current === currentIdsKey && capturedImage && capturedImage.startsWith('data:image')) {
        console.log('Snapshot already validated for current IDs, skipping');
        return;
      }

      // If capturedImage is base64 (not S3 URL), try to upload it
      if (capturedImage && capturedImage.startsWith('data:image')) {
        snapshotValidationInProgressRef.current = true;
        console.log('Found pending snapshot, uploading now that assessmentId is available...');
        try {
          // Check if snapshot already exists (NO base64 request - just check existence)
          const checkResult = await nodeApi.snapshotCheck({
            assessment_id: assessmentId,
            registration_id: registrationId
          }).catch(() => null);

          if (checkResult && checkResult.status === 'success' && checkResult.exists) {
            console.log('Snapshot already exists, keeping existing base64 (no fetch needed)');
            snapshotValidatedRef.current = currentIdsKey;
            snapshotValidationInProgressRef.current = false;
            return; // Keep the base64 we already have
          }

          // Upload the pending snapshot
          const uploadResult = await nodeApi.snapshotUpload({
            assessment_id: assessmentId,
            registration_id: registrationId,
            image: capturedImage
          }).catch(() => null);

          if (uploadResult && uploadResult.status === 'success') {
            // Keep the original base64 we uploaded (no need to fetch from S3 again)
            console.log('Snapshot uploaded successfully, keeping existing base64 (no S3 fetch needed)');
            snapshotValidatedRef.current = currentIdsKey;
            snapshotValidationInProgressRef.current = false;
            return; // No state change needed - already have base64
          }
        } catch (err) {
          console.warn('Failed to upload pending snapshot:', err.message);
        } finally {
          snapshotValidationInProgressRef.current = false;
        }
        return;
      }

      // If capturedImage is already base64, mark as validated and return
      if (capturedImage && capturedImage.startsWith('data:image')) {
        console.log('Using cached base64 snapshot (no verification needed)');
        snapshotValidatedRef.current = currentIdsKey;
        return;
      }

      // If capturedImage is an S3 URL, convert it to base64 ONCE
      if (capturedImage && capturedImage.startsWith('http')) {
        snapshotValidationInProgressRef.current = true;
        console.log('🔄 Converting existing S3 URL to base64 via backend proxy (one-time conversion)...');
        try {
          const base64 = await fetchImageAsBase64(capturedImage);
          console.log('Converted to base64 successfully');
          setCapturedImage(base64);
          snapshotValidatedRef.current = currentIdsKey;
        } catch (convertError) {
          console.warn('Failed to convert URL to base64:', convertError.message);
          // Don't set state on error to avoid loop
        } finally {
          snapshotValidationInProgressRef.current = false;
        }
        return;
      }

      // If capturedImage doesn't exist, try to fetch from server ONCE
      // OPTIMIZED: Only fetch base64 when we actually need it (not on every check)
      if (!capturedImage) {
        snapshotValidationInProgressRef.current = true;
        try {
          // First try: Get from Redis (faster) - request base64 ONLY when needed
          const getResult = await nodeApi.snapshotGet({
            assessment_id: assessmentId,
            registration_id: registrationId,
            return_base64: true
          }).catch(() => null);

          if (getResult && getResult.status === 'success') {
            if (getResult.base64) {
              console.log('Existing snapshot found via Redis (base64), using for monitoring');
              setCapturedImage(getResult.base64);
              snapshotValidatedRef.current = currentIdsKey;
              snapshotValidationInProgressRef.current = false;
              return;
            } else if (getResult.s3Url) {
              // Convert S3 URL to base64 immediately via backend proxy (avoids direct S3 fetch)
              console.log('Existing snapshot found via Redis (URL), converting to base64 via proxy...');
              try {
                const base64 = await fetchImageAsBase64(getResult.s3Url);
                console.log('Converted to base64 successfully');
                setCapturedImage(base64);
                snapshotValidatedRef.current = currentIdsKey;
                snapshotValidationInProgressRef.current = false;
                return;
              } catch (convertError) {
                console.warn('Failed to convert URL to base64:', convertError.message);
                setCapturedImage(getResult.s3Url);
                snapshotValidatedRef.current = currentIdsKey;
                snapshotValidationInProgressRef.current = false;
                return;
              }
            }
          }

          // Fallback: Check S3 directly ONLY if Redis didn't return anything
          console.log('Redis snapshot not found, checking S3 directly...');
          const checkResult = await nodeApi.snapshotCheck({
            assessment_id: assessmentId,
            registration_id: registrationId,
            return_base64: true
          }).catch(() => null);

          if (checkResult && checkResult.status === 'success' && checkResult.exists) {
            if (checkResult.base64) {
              console.log('Existing snapshot found via S3 check (base64), using for monitoring');
              setCapturedImage(checkResult.base64);
              snapshotValidatedRef.current = currentIdsKey;
              snapshotValidationInProgressRef.current = false;
              return;
            } else if (checkResult.s3Url) {
              // Convert S3 URL to base64 immediately via backend proxy (avoids direct S3 fetch)
              console.log('Existing snapshot found via S3 check (URL), converting to base64 via proxy...');
              try {
                const base64 = await fetchImageAsBase64(checkResult.s3Url);
                console.log('Converted to base64 successfully');
                setCapturedImage(base64);
                snapshotValidatedRef.current = currentIdsKey;
                snapshotValidationInProgressRef.current = false;
                return;
              } catch (convertError) {
                console.warn('Failed to convert URL to base64:', convertError.message);
                setCapturedImage(checkResult.s3Url);
                snapshotValidatedRef.current = currentIdsKey;
                snapshotValidationInProgressRef.current = false;
                return;
              }
            }
          }

          // If we reach here, snapshot doesn't exist yet
          console.log('No existing snapshot found. Monitoring will start after liveness verification.');
          snapshotValidatedRef.current = currentIdsKey; // Mark as validated even if not found (prevents repeated checks)
        } catch (err) {
          console.warn('Failed to validate existing snapshot (non-blocking):', err.message);
        } finally {
          snapshotValidationInProgressRef.current = false;
        }
      }
    };

    validateAndUploadSnapshot();
  }, [assessmentId, registrationId, fetchImageAsBase64]); // REMOVED capturedImage from deps to prevent loop

  const examTimeVersionRef = useRef(null); // last seen room examTimeVersion (null = not known yet)
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected) return;

    const handleExamTimeUpdate = (data = {}) => {
      // Check if this update corresponds to our current assessment or registration
      const currentAssessmentId = assessmentIdRef.current || assessmentId;
      const currentRegId = registrationId;

      if (Array.isArray(data.registration_ids) && data.registration_ids.length > 0 && currentRegId) {
        const isTargeted = data.registration_ids.some(id => String(id) === String(currentRegId));
        if (!isTargeted) {
          if (data.assessment_id && currentAssessmentId && String(data.assessment_id) !== String(currentAssessmentId)) {
            return;
          }
        }
      } else if (data.assessment_id && currentAssessmentId && String(data.assessment_id) !== String(currentAssessmentId)) {
        console.log(`⏰ Time update received for different assessment (${data.assessment_id}), current is ${currentAssessmentId}. Ignoring.`);
        return;
      }

      console.log('⏰ Received EXAM_TIME_UPDATED:', data);
      examTimeVersionRef.current = null; // re-baselined by the next version check, which then does not fetch again

      // If new_duration was sent in broadcast, update countdown immediately
      if (data.new_duration) {
        const durMin = parseInt(data.new_duration, 10);
        if (!isNaN(durMin) && durMin > 0) {
          studentDurationRef.current = durMin;
          const endTs = calculateLiveEndTime(durMin, examStartedAtRef.current, data.new_exam_date || data.exam_date, data.new_exam_time || data.exam_time);
          if (endTs) {
            examEndTimeRef.current = endTs;
            setExamEndTime(endTs);
            setIsTimeExceeded(false);
          }
        }
      }

      // Sync latest exam status and timer from PHP server. A request already in flight may have read the old
      // duration, so wait for it and fetch again instead of sharing it.
      if (typeof fetchQuestions === 'function') {
        Promise.resolve(fetchQuestionsInFlightRef.current)
          .catch(() => {})
          .then(() => fetchQuestions());
        showToast("Exam time has been updated by administrator. Recalculating timer...", "info");
      }
    };

    socket.on('EXAM_TIME_UPDATED', handleExamTimeUpdate);
    return () => socket.off('EXAM_TIME_UPDATED', handleExamTimeUpdate);
  }, [socketRef, isConnected, assessmentId, registrationId, fetchQuestions, showToast]);

  // Safety net for a missed EXAM_TIME_UPDATED push (socket reconnecting, phone in background): the room's
  // examTimeVersion changes on every admin time change, so a cheap socket check decides when to re-read PHP.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected || !examStarted || examFinished) return;
    const check = () => {
      socket.emit('get-exam-status', { roomId: currentRoomId }, (res) => {
        const version = res?.examTimeVersion;
        if (version == null) return;
        const missedUpdate = examTimeVersionRef.current !== null && examTimeVersionRef.current !== version;
        examTimeVersionRef.current = version;
        if (missedUpdate) {
          console.log('⏰ Exam time changed while the update was missed; re-syncing timer');
          fetchQuestions();
        }
      });
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [socketRef, isConnected, examStarted, examFinished, currentRoomId, fetchQuestions]);

  const handleSubmitExam = async () => {
    setConfirmModal(null);
    try {
      await submitExamApiCall();
      localStorage.removeItem(ANSWERS_KEY);
      showToast("Submitted successfully.", "success");
      setExamStarted(false);
      setExamFinished(true);//chekc
      setShowUploadSection(false);

      // Forcefully finalize recording session on normal submit
      try {
        const url = nodeApi.recordingFinalizeUrl();
        const rid = registrationIdRef.current;
        const aid = assessmentIdRefForBeacon.current ?? recordingStartPayloadRef.current?.assessmentId ?? recordingStartPayloadRef.current?.assessment_id;
        if (rid) {
          navigator.sendBeacon?.(url, new Blob([JSON.stringify({
            registrationId: rid,
            assessmentId: aid,
          })], { type: 'application/json' }));
        }
      } catch (e) {
        console.warn('[Recording] finalize beacon failed', { stage: 'finalize', registrationId: registrationIdRef.current, message: e?.message || String(e) });
      }
    } catch (e) {
      showToast("Submission failed. Please check your internet connection.", "error");
    }
  };

  const handleAutoSubmit = useCallback(async () => {
    if (hasAutoSubmitted.current) return;
    hasAutoSubmitted.current = true;
    setIsTimeExceeded(true);
    setExamStarted(false);
    setExamFinished(true);
    setShowUploadSection(false);
    return;
  }, [answers, userId, currentRoomId, handleSubmitExam]);

  // Build a single PDF from multiple image files (one image per page, fit to A4)
  // Optimizes size by resizing large images and applying JPEG compression
  // Applies institution logo as a centered watermark on each page (15% opacity)
  const imagesToPdfBlob = useCallback(async (files, logoUrl) => {
    if (!files || files.length === 0) return null;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 0;
    const contentW = pageW;
    const contentH = pageH;

    // Helper to resize image and get optimized DataURL
    const getOptimizedDataUrl = async (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const MAX_SIZE = 5000; // Good balance for readability and size
          let w = img.width;
          let h = img.height;

          if (w > MAX_SIZE || h > MAX_SIZE) {
            if (w > h) {
              h = Math.round((h * MAX_SIZE) / w);
              w = MAX_SIZE;
            } else {
              w = Math.round((w * MAX_SIZE) / h);
              h = MAX_SIZE;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.7)); // Consistent quality & format
        };
        img.src = src;
      });
    };

    // --- Watermark Logic Removed ---
    // Background: Previously, we attempted to draw the watermark image here using canvas.toDataURL.
    // However, this caused CORS/Security errors when the logo was hosted on an external S3 bucket,
    // and it resulted in duplicate watermarks since the backend also applied a "FINAL" text watermark.
    // Solution: The backend (s3AnswerSheet.js) will now handle BOTH the image and text fallback watermarks.
    // We just pass the logo_url to the backend in the FormData.

    for (let i = 0; i < files.length; i++) {
      const item = files[i];
      const file = item.file || item;

      // 1. Get original DataURL
      const originalDataUrl = typeof item.preview === 'string' ? item.preview : await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });

      // 2. Resize and compress image
      const optimizedDataUrl = await getOptimizedDataUrl(originalDataUrl);

      if (i > 0) doc.addPage();

      // Use 'FAST' compression for embedding to balance time and size
      doc.addImage(optimizedDataUrl, 'JPEG', margin, margin, contentW, contentH, undefined, 'FAST');

      // Watermark drawing removed from frontend. Backend will handle it.
    }

    return doc.output('blob');
  }, []);

  const handleFileSelect = (e, type) => {
    const chosen = e.target.files;
    if (!chosen || chosen.length === 0) return;
    if (type === 'pdf') {
      const pdf = chosen[0];
      if (pdf && pdf.type === 'application/pdf') {
        setPdfFile(pdf);
        setImageFiles([]);
      }
      e.target.value = '';
      return;
    }
    // type === 'images'
    const imageList = [];
    for (let i = 0; i < chosen.length; i++) {
      const f = chosen[i];
      if (!f.type.startsWith('image/')) continue;
      imageList.push({ file: f, preview: URL.createObjectURL(f) });
    }
    if (imageList.length > 0) {
      setImageFiles(prev => [...prev, ...imageList]);
      setPdfFile(null);
    }
    e.target.value = '';
  };

  const removeImageAt = (index) => {
    setImageFiles(prev => {
      const next = [...prev];
      if (next[index].preview) URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
  };

  // Open camera modal for photo capture (uses selected camera, defaults to first)
  // On devices that support it (mobile), we directly trigger the native rear camera
  // via a hidden <input capture="environment"> and pipe the result into the crop flow.
  const handleTakePhoto = useCallback(() => {
    if (rearCameraInputRef.current) {
      // On Android/iOS the native camera ALWAYS kills the WebRTC video track
      // (OS revokes camera hardware). After it closes, doDirectCameraRestore
      // gets a fresh stream and attaches it directly via ref.

      // Store restore fn so onChange (photo-taken path) can call it too
      restoreMonitoringStreamsRef.current = doDirectCameraRestore;

      // One-shot focus listener — cancel path (no photo taken)
      const onNativeCameraClose = () => {
        window.removeEventListener('focus', onNativeCameraClose);
        // Small delay: some browsers fire focus before hardware is released
        setTimeout(() => doDirectCameraRestore(), 600);
      };
      window.addEventListener('focus', onNativeCameraClose);

      // Trigger native device camera
      nativeCaptureActiveRef.current = true;
      rearCameraInputRef.current.value = '';
      rearCameraInputRef.current.click();
    }
  }, [doDirectCameraRestore]);

  // Switch camera for photo capture (mobile only). Release other camera before acquiring so only one is active.
  const switchCamera = useCallback(async () => {
    if (!isMobileDeviceFallback) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    showToast(`Switching to ${newMode === 'user' ? 'Front' : 'Rear'} Camera...`, "info");

    try {
      // 1. Stop all current tracks in the alternate stream to release hardware
      if (alternateCameraStream) {
        alternateCameraStream.getTracks().forEach(t => t.stop());
      }

      // 2. Request new stream with the specific facing mode
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: newMode } }, // Use exact for reliability on Android/iOS
        audio: false
      });

      setAlternateCameraStream(newStream);

      // 3. Update the Video Element
      if (cameraModalVideoRef.current) {
        cameraModalVideoRef.current.srcObject = newStream;
      }

      // 4. Update the Mediasoup producer if necessary
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack && replaceFirstCameraTrack) {
        await replaceFirstCameraTrack(videoTrack);
      }

      // 🔹 Apply Auto-Focus
      await applyFocusConstraint(newStream);

      setSelectedCameraIndex(newMode === 'user' ? 0 : 1);
    } catch (err) {
      // Fallback if 'exact' fails (some older devices)
      const fallbackStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newMode },
        audio: false
      });
      setAlternateCameraStream(fallbackStream);
      if (cameraModalVideoRef.current) cameraModalVideoRef.current.srcObject = fallbackStream;

      // 🔹 Apply Auto-Focus (fallback stream)
      await applyFocusConstraint(fallbackStream);
    }
  }, [facingMode, alternateCameraStream, replaceFirstCameraTrack]);

  // Helper to apply or re-trigger focus (continuous or single-shot)
  // Helper to apply or re-trigger focus (continuous, single-shot, or specific point)
  // Helper to apply or re-trigger focus (continuous, single-shot, or specific point)
  const applyFocusConstraint = async (stream, mode = 'continuous', point = null) => {
    if (!stream) return "No stream";
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return "No video track";

    // Check if getCapabilities is supported
    if (!videoTrack.getCapabilities) return "Capabilities not supported";

    const capabilities = videoTrack.getCapabilities();
    const constraints = { advanced: [] };

    let focusModeApplied = false;
    let pointApplied = false;

    // 1. Point of Interest (if supported and point provided)
    // Note: 'pointsOfInterest' is experimental but some mobile browsers support it.
    // Spec: { x, y } normalized coordinates (0.0 to 1.0)
    if (point && capabilities.pointsOfInterest) {
      try {
        constraints.advanced.push({ pointsOfInterest: [{ x: point.x, y: point.y }] });
        pointApplied = true;
        console.log(`🎯 Applying focus point: ${point.x.toFixed(2)}, ${point.y.toFixed(2)}`);
      } catch (e) {
        console.warn('Focus point not supported:', e);
      }
    }

    // 2. Focus/Exposure/White Balance Modes
    // Tapping usually sets focus, exposure, and white balance to the point
    const modesToSet = [];
    if (pointApplied) {
      // If focusing on a point, usually we trigger a specific mode (continuous or single-shot)
      // If single-shot is supported, it forces a re-evaluation at that point
      if (capabilities.focusMode?.includes('single-shot')) modesToSet.push({ focusMode: 'single-shot' });
      else if (capabilities.focusMode?.includes('continuous')) modesToSet.push({ focusMode: 'continuous' });

      if (capabilities.exposureMode?.includes('single-shot')) modesToSet.push({ exposureMode: 'single-shot' });
      else if (capabilities.exposureMode?.includes('continuous')) modesToSet.push({ exposureMode: 'continuous' });

      if (capabilities.whiteBalanceMode?.includes('single-shot')) modesToSet.push({ whiteBalanceMode: 'single-shot' });
      else if (capabilities.whiteBalanceMode?.includes('continuous')) modesToSet.push({ whiteBalanceMode: 'continuous' });
    } else {
      // Standard mode setting
      if (capabilities.focusMode?.includes(mode)) modesToSet.push({ focusMode: mode });
      else if (mode === 'continuous' && capabilities.focusMode?.includes('single-shot')) modesToSet.push({ focusMode: 'single-shot' });
    }

    if (modesToSet.length > 0) {
      constraints.advanced.push(...modesToSet);
      focusModeApplied = true;
    }

    if (focusModeApplied || constraints.advanced.length > 0) {
      try {
        await videoTrack.applyConstraints(constraints);
        if (pointApplied) return "Point focused";
        if (focusModeApplied) return `Focus: ${mode}`;
        return "Constraints applied";
      } catch (err) {
        console.warn('Failed to apply focus constraints:', err);
        return "Focus failed";
      }
    }

    if (point && !pointApplied) return "Point focus unsupported";
    return "Focus unsupported";
  };

  // Tap-to-Focus Handler for Video Element
  const handleVideoTap = useCallback(async (e) => {
    if (!cameraModalVideoRef.current || !cameraModalVideoRef.current.srcObject) return;

    // Calculate click position for visual feedback
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setFocusPoint({ x, y, visible: true });

    // Hide focus square after animation
    setTimeout(() => {
      setFocusPoint(prev => prev ? { ...prev, visible: false } : null);
    }, 1500);

    // Calculate normalized coordinates (0.0 to 1.0) for camera constraint
    const normalizedX = x / rect.width;
    const normalizedY = y / rect.height;

    const stream = cameraModalVideoRef.current.srcObject;

    // Pass normalized point and force re-focus
    const result = await applyFocusConstraint(stream, 'continuous', { x: normalizedX, y: normalizedY });

    // Feedback to user
    if (result === "Point focus unsupported") {
      showToast("Focusing center (point focus not supported)", "info");
    } else if (result === "Point focused") {
      // showToast("Focused on selected area", "success");
    }
  }, []);

  // Switch camera for liveness verification (mobile only).
  // On mobile only one camera is often active at a time: release the other before acquiring the new one.
  const switchLivenessCamera = useCallback(async () => {
    if (!isMobileDeviceFallback || !localStreams || localStreams.length === 0) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

    const nextCameraType = livenessCameraIndex === 0 ? 'rear' : 'front';
    showToast("Switching camera...", "info");

    try {
      let streamToUse = null;

      if (livenessCameraIndex === 0) {
        // Switching to REAR: release front first so rear can start cleanly
        if (refreshedFrontStreamRef.current) {
          refreshedFrontStreamRef.current.getTracks().forEach((t) => t.stop());
          refreshedFrontStreamRef.current = null;
          setRefreshedFrontStream(null);
        }
        await new Promise((r) => setTimeout(r, 150));

        if (alternateLivenessStream) {
          streamToUse = alternateLivenessStream;
          setLivenessCameraIndex(1);
        } else {
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
            audio: false
          });
          setAlternateLivenessStream(newStream);
          streamToUse = newStream;
          setLivenessCameraIndex(1);
        }
      } else {
        // Switching to FRONT: release rear first so front can start cleanly (many mobiles allow only one camera)
        if (alternateLivenessStream) {
          alternateLivenessStream.getTracks().forEach((t) => t.stop());
        }
        if (refreshedFrontStreamRef.current) {
          refreshedFrontStreamRef.current.getTracks().forEach((t) => t.stop());
        }
        await new Promise((r) => setTimeout(r, 150));

        const newFrontStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false
        });
        refreshedFrontStreamRef.current = newFrontStream;
        streamToUse = newFrontStream;
        setLivenessCameraIndex(0);
        setRefreshedFrontStream(newFrontStream);
        setAlternateLivenessStream(null); // clear after we have new front so effect never attaches dead stream
      }

      if (!streamToUse) return;

      // 🔹 Apply Auto-Focus
      await applyFocusConstraint(streamToUse);

      const videoEl = checkPreviewRef.current;
      if (videoEl) {
        videoEl.srcObject = streamToUse;
        videoEl.play().catch(() => { });
      }

      const videoTrack = streamToUse.getVideoTracks?.()[0];
      if (videoTrack && replaceFirstCameraTrack) {
        await replaceFirstCameraTrack(videoTrack);
      }
      showToast(`Switched to ${nextCameraType === 'front' ? 'Front' : 'Rear'} Camera`, "success");
    } catch (err) {
      console.error("Camera switch error:", err);
      showToast("Unable to switch camera. Please try again.", "error");
    }
  }, [isMobileDeviceFallback, localStreams, livenessCameraIndex, alternateLivenessStream, replaceFirstCameraTrack]);

  // Re-detect paper edges on captured photo
  const redetectPaper = useCallback(async () => {
    if (!capturedPhotoForCrop) return;

    setIsDetectingPaper(true);
    try {
      const detectedArea = await detectPaperEdges(capturedPhotoForCrop);

      if (detectedArea.detected) {
        // Calculate display coordinates
        const img = cropImageRef.current;
        if (img) {
          const scaleX = imageDisplaySize.width / img.naturalWidth;
          const scaleY = imageDisplaySize.height / img.naturalHeight;

          setCropArea({
            x: detectedArea.x * scaleX,
            y: detectedArea.y * scaleY,
            width: detectedArea.width * scaleX,
            height: detectedArea.height * scaleY,
            startX: detectedArea.x * scaleX,
            startY: detectedArea.y * scaleY
          });
          setPaperDetected(true);
          showToast("Paper re-detected and auto-cropped!", "success");
        }
      } else {
        showToast("Paper not detected. Please crop manually.", "warning");
        setPaperDetected(false);
      }
    } catch (err) {
      console.error("Re-detection error:", err);
      showToast("Re-detection failed.", "error");
    } finally {
      setIsDetectingPaper(false);
    }
  }, [capturedPhotoForCrop, imageDisplaySize]);

  // Capture photo from camera modal (uses CAM-1)
  const capturePhotoFromModal = useCallback(async () => {
    const video = cameraModalVideoRef.current;
    if (!video || video.readyState < 2) {
      showToast("Camera not ready. Please wait.", "warning");
      return;
    }

    // Capture the photo
    const photoDataUrl = captureFrame(video, 1.0);
    setCapturedPhotoForCrop(photoDataUrl);

    // Reset crop area and display size to force re-initialization in image onLoad
    setCropArea({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
    setImageDisplaySize({ width: 0, height: 0 });
    setIsCropEnabled(false); // Hide crop lines by default for new captures

    showToast("Photo captured! Drag the corners to crop.", "info");
  }, [showToast]);

  // Apply crop to captured photo
  const applyCrop = useCallback(async () => {
    if (!capturedPhotoForCrop || cropArea.width === 0 || cropArea.height === 0) return;

    try {
      showToast("Saving page...", "info");

      const img = new Image();
      img.src = capturedPhotoForCrop;

      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Calculate actual crop coordinates in original image based on display-to-natural ratio
      // Ensure we use naturalWidth/naturalHeight for accuracy
      const naturalW = img.naturalWidth || img.width;
      const naturalH = img.naturalHeight || img.height;

      const scaleX = naturalW / imageDisplaySize.width;
      const scaleY = naturalH / imageDisplaySize.height;

      let actualX, actualY, actualWidth, actualHeight;

      if (isCropEnabled) {
        actualX = Math.max(0, cropArea.x * scaleX);
        actualY = Math.max(0, cropArea.y * scaleY);
        actualWidth = Math.min(naturalW - actualX, cropArea.width * scaleX);
        actualHeight = Math.min(naturalH - actualY, cropArea.height * scaleY);
      } else {
        actualX = 0;
        actualY = 0;
        actualWidth = naturalW;
        actualHeight = naturalH;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas to crop dimensions
      canvas.width = actualWidth;
      canvas.height = actualHeight;

      // Draw cropped portion
      ctx.drawImage(
        img,
        actualX, actualY, actualWidth, actualHeight,
        0, 0, actualWidth, actualHeight
      );

      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.95);

      // Convert to blob for file creation
      const blob = await fetch(croppedDataUrl).then(res => res.blob());
      const file = new File([blob], `page_${Date.now()}.jpg`, { type: 'image/jpeg' });

      setImageFiles(prev => [...prev, { file, preview: croppedDataUrl }]);
      setPdfFile(null);
      setShowCameraModal(false);
      setCapturedPhotoForCrop(null);
      setIsCropEnabled(false);
      setCropArea({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
      showToast("Page added successfully!", "success");

      // Restore monitoring camera
      doDirectCameraRestore();
    } catch (err) {
      console.error("Crop error:", err);
      showToast("Failed to process crop.", "error");
    }
  }, [capturedPhotoForCrop, cropArea, imageDisplaySize, doDirectCameraRestore, showToast]);

  // Unified Drag Handler for Move & Resize (Mouse + Touch)
  const handleDragStart = useCallback((type, clientX, clientY) => {
    if (!capturedPhotoForCrop || !cropImageRef.current) return;

    // If starting a 'new' crop usage (clicking outside), we might want to reset or ignore.
    // Here we will ignore clicks outside to prevent accidental reset, unless it's a specific 'new' action.

    const rect = cropImageRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    setDragState({
      type, // 'move', 'nw', 'ne', 'sw', 'se'
      startX: clientX,
      startY: clientY,
      startCrop: { ...cropArea }
    });
  }, [capturedPhotoForCrop, cropArea]);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!dragState || !cropImageRef.current) return;

    const rect = cropImageRef.current.getBoundingClientRect();
    const deltaX = clientX - dragState.startX;
    const deltaY = clientY - dragState.startY;

    const { startCrop } = dragState;
    let newCrop = { ...startCrop };

    // Bounds check helpers
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    if (dragState.type === 'move') {
      newCrop.x = Math.max(0, Math.min(startCrop.x + deltaX, imageDisplaySize.width - startCrop.width));
      newCrop.y = Math.max(0, Math.min(startCrop.y + deltaY, imageDisplaySize.height - startCrop.height));
    } else {
      // Resize Logic
      const minSize = 25; // Minimum width/height in px

      if (dragState.type.includes('w')) { // Left side
        const rightEdge = startCrop.x + startCrop.width;
        newCrop.x = Math.max(0, Math.min(startCrop.x + deltaX, rightEdge - minSize));
        newCrop.width = rightEdge - newCrop.x;
      }
      if (dragState.type.includes('e')) { // Right side
        newCrop.width = Math.max(minSize, Math.min(startCrop.width + deltaX, imageDisplaySize.width - startCrop.x));
      }
      if (dragState.type.includes('n')) { // Top side
        const bottomEdge = startCrop.y + startCrop.height;
        newCrop.y = Math.max(0, Math.min(startCrop.y + deltaY, bottomEdge - minSize));
        newCrop.height = bottomEdge - newCrop.y;
      }
      if (dragState.type.includes('s')) { // Bottom side
        newCrop.height = Math.max(minSize, Math.min(startCrop.height + deltaY, imageDisplaySize.height - startCrop.y));
      }
    }

    setCropArea(newCrop);
  }, [dragState, imageDisplaySize]);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  // Mouse Adapters
  const onMouseDown = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    handleDragStart(type, e.clientX, e.clientY);
  };

  // Touch Adapters
  const onTouchStart = (e, type) => {
    // e.preventDefault(); // meaningful for some interactions
    e.stopPropagation();
    const touch = e.touches[0];
    handleDragStart(type, touch.clientX, touch.clientY);
  };

  // Global move/up handlers attached to overlay/container
  const onGlobalMove = (e) => {
    if (!dragState) return;
    e.preventDefault();
    handleDragMove(e.clientX, e.clientY);
  };
  const onGlobalTouchMove = (e) => {
    if (!dragState) return;
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };
  const onGlobalUp = () => handleDragEnd();

  // --- Photo Review Zoom/Pan Logic ---
  const handleZoomIn = (e) => {
    e?.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.5, 3.0)); // Max 3x
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation();
    setZoomLevel(prev => Math.max(prev - 0.5, 1.0)); // Min 1x
    // Reset pan if zooming out to 1x
    if (zoomLevel <= 1.5) setPanPosition({ x: 0, y: 0 });
  };

  const startPan = (clientX, clientY) => {
    if (zoomLevel <= 1) return; // Only pan if zoomed in
    setIsPanning(true);
    setDragState({
      type: 'pan',
      startX: clientX,
      startY: clientY,
      startPan: { ...panPosition }
    });
  };

  const movePan = (clientX, clientY) => {
    if (!isPanning || !dragState || dragState.type !== 'pan') return;
    const deltaX = clientX - dragState.startX;
    const deltaY = clientY - dragState.startY;

    setPanPosition({
      x: dragState.startPan.x + deltaX,
      y: dragState.startPan.y + deltaY
    });
  };

  const endPan = () => {
    setIsPanning(false);
    setDragState(null);
  };

  // Pinch-to-Zoom Handlers
  const onReviewTouchStart = (e) => {
    e.stopPropagation();
    if (e.touches.length === 2) {
      // Start Pinch
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      lastPinchDistance.current = dist;
      startZoomLevel.current = zoomLevel;
    } else if (e.touches.length === 1) {
      // Start Pan
      startPan(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const onReviewTouchMove = (e) => {
    e.stopPropagation();
    if (e.touches.length === 2 && lastPinchDistance.current !== null) {
      // Handle Pinch
      if (e.cancelable) e.preventDefault(); // Prevent page scale
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      const scaleFactor = dist / lastPinchDistance.current;
      setZoomLevel(Math.min(Math.max(startZoomLevel.current * scaleFactor, 1.0), 4.0));
    } else if (e.touches.length === 1) {
      // Handle Pan
      if (e.cancelable) e.preventDefault();
      movePan(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const onReviewTouchEnd = () => {
    lastPinchDistance.current = null;
    endPan();
  };

  // Legacy handlers removed/replaced
  const handleCropMouseDown = () => { };
  const handleCropMouseMove = () => { };
  const handleCropMouseUp = () => { };
  const handleCropTouchStart = () => { };
  const handleCropTouchMove = () => { };
  const handleCropTouchEnd = () => { };

  const hasUploadContent = pdfFile || imageFiles.length > 0;

  const handlePdfUpload = async () => {
    if (!hasUploadContent) {
      showToast("Please add a PDF or multiple photos first.", "warning");
      return;
    }

    // Confirmation Dialog before final upload
    setConfirmModal({
      message: "Are you sure? You will not be able to upload again.",
      onConfirm: async () => {
        setConfirmModal(null);
        await performPdfUpload();
      }
    });
  };

  const performPdfUpload = async () => {
    // Validate required IDs for S3 upload
    if (!assessmentId || !registrationId) {
      showToast("Missing exam information. Please refresh the page.", "error");
      return;
    }

    setIsPdfUploading(true);
    setUploadStep('converting');
    setUploadStepError(null);

    let fileToSend = null;

    try {
      // ── STEP 1: Convert photos to PDF (with watermark) ──────────────────────
      fileToSend = pdfFile;
      if (!fileToSend && imageFiles.length > 0) {
        fileToSend = await imagesToPdfBlob(imageFiles, institutionLogoUrl)
          .then(blob => {
            if (!blob) throw new Error('PDF generation returned empty.');
            return new File([blob], 'answer_sheet.pdf', { type: 'application/pdf' });
          });
      }
      if (!fileToSend) {
        throw new Error('No file to upload. Please add photos or a PDF.');
      }
    } catch (err) {
      setUploadStep('error');
      setUploadStepError('Photo-to-PDF conversion failed: ' + err.message);
      showToast('PDF conversion failed. Please try again.', 'error');
      setIsPdfUploading(false);
      return;
    }

    // ── STEP 2: Upload PDF to S3 ─────────────────────────────────────────────
    setUploadStep('uploading');
    let s3Key = null;
    try {
      const formData = new FormData();
      formData.append('pdf_file', fileToSend);
      formData.append('assessment_id', String(assessmentId));
      formData.append('registration_id', String(registrationId));
      if (institutionLogoUrl) {
        formData.append('logo_url', institutionLogoUrl);
      }

      const result = await nodeApi.answerSheetUpload(formData);

      if (!result || result.status !== 'success') {
        throw new Error(result?.message || 'S3 upload failed.');
      }
      s3Key = result.s3Key;
    } catch (err) {
      setUploadStep('error');
      setUploadStepError('S3 Upload failed: ' + err.message);
      showToast('Upload to server failed. Please check your connection and try again.', 'error');
      setIsPdfUploading(false);
      return;
    }

    // ── STEP 3: Save to PHP Backend DB ───────────────────────────────────────
    setUploadStep('saving');
    let dbSaveSuccess = false;
    let dbSaveError = null;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        await submitExamApiCall(s3Key);
        dbSaveSuccess = true;
        break; // Success — exit retry loop
      } catch (err) {
        dbSaveError = err.message || 'DB save failed';
        console.warn(`DB save attempt ${attempt} failed: ${dbSaveError}`);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 2000)); // Wait 2s before retry
        }
      }
    }

    if (!dbSaveSuccess) {
      // S3 upload succeeded but DB save failed — critical error. Show a specific message.
      setUploadStep('error');
      setUploadStepError(
        'Your answer sheet was uploaded to the server but could NOT be saved in the database. ' +
        'Please contact the invigilator immediately with your Registration Number and this error: ' + dbSaveError
      );
      showToast('CRITICAL: PDF uploaded but DB save failed. Contact invigilator immediately!', 'error');
      setIsPdfUploading(false);
      return;
    }

    // ── STEP 4: All done — clean up and notify ───────────────────────────────
    setUploadStep('done');
    localStorage.removeItem(ANSWERS_KEY);

    // Notify other sessions (laptop) that submission is complete
    if (socketRef.current) {
      socketRef.current.emit('student-exam-submitted', {
        userId: userId,
        user_id: userId,
        roomId: currentRoomId,
        registrationId: registrationId,
        registration_id: registrationId
      });
    }

    showToast('Answer sheet saved successfully. Signing out...', 'success');
    setExamStarted(false);
    setShowUploadSection(false);
    setExamFinished(true);
    setIsFullPreviewOpen(false);

    // Redirect after a beat so student can see success status
    setTimeout(() => {
      window.location.href = `${BASE_URL}`;
    }, 2500);

    // Kept isPdfUploading true to prevent multiple submissions during redirect
  };

  const scrollToQuestion = (key) => {
    const element = document.getElementById(`question-${key}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const shouldForceFullscreen = examStarted && !showUploadSection && !examFinished;

  // Keep fullscreen state synced across browsers.
  useEffect(() => {
    const updateFullscreenState = () => {
      const active = !!getFullscreenElement();
      setIsFullscreenActive(active);
      if (active) {
        fullscreenExitWarnedRef.current = false;
        // Lock Escape key to prevent accidental exit (Chrome/Edge)
        if (navigator.keyboard && navigator.keyboard.lock) {
          navigator.keyboard.lock(['Escape'])
            .then(() => console.log('Escape key locked'))
            .catch(e => console.warn('Keyboard lock failed:', e));
        }
      } else {
        // Unlock when not in fullscreen
        if (navigator.keyboard && navigator.keyboard.unlock) {
          navigator.keyboard.unlock();
        }
      }
    };

    updateFullscreenState();
    document.addEventListener('fullscreenchange', updateFullscreenState);
    document.addEventListener('webkitfullscreenchange', updateFullscreenState);
    document.addEventListener('msfullscreenchange', updateFullscreenState);
    document.addEventListener('mozfullscreenchange', updateFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', updateFullscreenState);
      document.removeEventListener('webkitfullscreenchange', updateFullscreenState);
      document.removeEventListener('msfullscreenchange', updateFullscreenState);
      document.removeEventListener('mozfullscreenchange', updateFullscreenState);
    };
  }, [getFullscreenElement]);

  // Persist fullscreen requirement while exam is active and restore after refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (shouldForceFullscreen) {
      localStorage.setItem(FULLSCREEN_REQUIRED_KEY, '1');
      // Auto-calling requestExamFullscreen here causes strict browser errors (like Firefox) 
      // because it's not a user-initiated event. Rely on the "RETURN TO FULLSCREEN" button instead.
      return;
    }

    if (showUploadSection || examFinished) {
      localStorage.removeItem(FULLSCREEN_REQUIRED_KEY);
      fullscreenExitWarnedRef.current = false;
    }
  }, [FULLSCREEN_REQUIRED_KEY, shouldForceFullscreen, getFullscreenElement, requestExamFullscreen, showUploadSection, examFinished]);

  // --- KEYBOARD RESTRICTIONS: Block F5, F11, and Esc during exam ---
  useEffect(() => {
    if (!examStarted || examFinished || showUploadSection) return;

    // 2️⃣ Key restrictions
    const handleKeyDown = (e) => {

      // 🔴 Block Refresh
      if (e.key === 'F5' || (e.ctrlKey && e.key.toLowerCase() === 'r')) {
        e.preventDefault();
        showToast('Refresh is disabled during the exam.', 'warning');
        return;
      }

      // 🔴 Block / Re-trigger F11
      if (e.key === 'F11') {
        e.preventDefault();

        showToast(
          'Fullscreen is mandatory. Re-entering fullscreen...',
          'warning'
        );
        requestExamFullscreen(false);
      }

      // 🔴 Discourage Escape
      if (e.key === 'Escape') {

        e.preventDefault();
        showToast(
          'Exiting fullscreen is not allowed during the exam.',
          'warning'
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };

  }, [examStarted, examFinished, showUploadSection, showToast, requestExamFullscreen]);


  // On page refresh, attempt to return to fullscreen automatically.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(FULLSCREEN_REQUIRED_KEY) === '1') {
      requestExamFullscreen(false);
    }
  }, [FULLSCREEN_REQUIRED_KEY, requestExamFullscreen]);

  // If fullscreen is exited during exam, block the student until fullscreen is restored.
  useEffect(() => {
    if (!shouldForceFullscreen) return;
    if (isFullscreenActive) {
      fullscreenExitWarnedRef.current = false;
      return;
    }
    if (!fullscreenExitWarnedRef.current) {
      fullscreenExitWarnedRef.current = true;
      setIsOverlayVisible(true);
      setIsStudentReady(false);
      showToast("Fullscreen is required during exam. Click RETURN TO FULLSCREEN (or press F11).", "warning");
    }
  }, [isFullscreenActive, shouldForceFullscreen, showToast]);

  // --- SOCKETS ---
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected) return;

    const applyIndividualPauseUi = () => {
      setIsStudentPausedByInvigilator(true);
      isStudentPausedByInvigilatorRef.current = true;
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(EXAM_PAUSED_KEY, '1');
      examStartedSocketEventRef.current = false;
      setExamStarted(false);
      setExamEndTime(null);
      examEndTimeRef.current = null;
      setShowUploadSection(false);
      setIsOneMinuteToStart(false);
      setIsFullPreviewOpen(false);
      setConfirmModal(null);
      setIsOverlayVisible(false);
    };

    const clearIndividualPauseUi = () => {
      setIsStudentPausedByInvigilator(false);
      isStudentPausedByInvigilatorRef.current = false;
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(EXAM_PAUSED_KEY);
    };

    const restoreExamTimingFromPayload = (res) => {
      if (res?.serverNow) setServerOffset(res.serverNow - Date.now());
      const startTs = res.examStartedAt || res.serverNow || Date.now();
      examStartedAtRef.current = startTs;
      let durationMinutes = parseInt(studentDurationRef.current || res.duration, 10);
      if (isNaN(durationMinutes) || durationMinutes <= 0) durationMinutes = parseInt(res.duration, 10);
      if (durationMinutes && durationMinutes > 0) {
        const endTs = calculateLiveEndTime(durationMinutes, startTs, res.examDate, res.examTime);
        if (endTs) {
          examEndTimeRef.current = endTs;
          setExamEndTime(endTs);
        }
      }
    };

    socket.on('exam-started', (res) => {
      console.log('Exam started event received:', res);
      // Clear ROOM-WIDE stop only; individual pause must remain if still set on server.
      setIsExamStoppedByInvigilator(false);
      isExamStoppedByInvigilatorRef.current = false;
      if (currentRoomId && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(EXAM_STOPPED_KEY);
      }
      examStartedSocketEventRef.current = true;
      showToast("Proctor has started the exam.", "info");
      // Reset completed state so UI leaves "Exam Completed" when proctor starts again
      setExamFinished(false);
      setShowUploadSection(false);
      restoreExamTimingFromPayload(res);
      // Individually paused students keep the stop overlay; do not let them answer yet.
      if (isStudentPausedByInvigilatorRef.current || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_PAUSED_KEY))) {
        applyIndividualPauseUi();
      } else {
        setExamStarted(true);
      }
      // Fetch questions (will retry if API hasn't updated yet)
      fetchQuestions();
    });

    socket.on('student-exam-paused', () => {
      console.log('Invigilator paused this student\'s examination.');
      applyIndividualPauseUi();
    });

    socket.on('student-exam-resumed', (res = {}) => {
      console.log('Invigilator resumed this student\'s examination.', res);
      clearIndividualPauseUi();
      // Room-wide stop still wins if the whole exam was stopped.
      if (isExamStoppedByInvigilatorRef.current || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_STOPPED_KEY))) {
        setIsExamStoppedByInvigilator(true);
        isExamStoppedByInvigilatorRef.current = true;
        setExamStarted(false);
        return;
      }
      if (res.examStarted) {
        examStartedSocketEventRef.current = true;
        setExamFinished(false);
        setShowUploadSection(false);
        setExamStarted(true);
        restoreExamTimingFromPayload(res);
        fetchQuestions();
      }
    });

    // 🔹 FIX: Mark that we're waiting for socket status to prevent fetchQuestions from overwriting timer
    waitingForSocketStatusRef.current = true;
    // Timeout fallback: if socket doesn't respond within 3 seconds, allow PHP fallback and fetch from API (fixes mobile timer)
    const socketStatusTimeout = setTimeout(() => {
      if (waitingForSocketStatusRef.current) {
        console.warn('Socket get-exam-status timeout, allowing PHP fallback');
        waitingForSocketStatusRef.current = false;
        fetchQuestions();
      }
    }, 3000);

    socket.emit('get-exam-status', { roomId: currentRoomId }, (response) => {
      clearTimeout(socketStatusTimeout);
      console.log('Student get-exam-status response:', response);
      // Mark that socket status has been received
      waitingForSocketStatusRef.current = false;

      // Handle initial proctor media state
      if (response?.proctorVideoEnabled !== undefined) {
        setProctorVideoEnabled(response.proctorVideoEnabled);
        proctorVideoEnabledRef.current = response.proctorVideoEnabled;
      }
      if (response?.proctorAudioEnabled !== undefined) {
        setProctorAudioEnabled(response.proctorAudioEnabled);
        proctorAudioEnabledRef.current = response.proctorAudioEnabled;
      }

      // Server is authoritative for individual pause across refresh/reconnect.
      if (response && Object.prototype.hasOwnProperty.call(response, 'studentExamPaused')) {
        if (response.studentExamPaused) applyIndividualPauseUi();
        else clearIndividualPauseUi();
      }

      if (response?.examStarted) {
        setIsExamStoppedByInvigilator(false);
        if (currentRoomId && typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(EXAM_STOPPED_KEY);
        }
        setExamFinished(false);
        setShowUploadSection(false);
        examStartedSocketEventRef.current = true;
        restoreExamTimingFromPayload(response);
        // Individual pause remains authoritative for this student even when the room exam is running.
        if (response.studentExamPaused || isStudentPausedByInvigilatorRef.current) {
          applyIndividualPauseUi();
        } else {
          setExamStarted(true);
        }

        // 🔹 SYNC PRIVATE CALL STATE
        if (response.privateAudioTargetUserId !== undefined) {
          setPrivateAudioTargetUserId(response.privateAudioTargetUserId);
          console.log(`📡 Synced private audio target: ${response.privateAudioTargetUserId}`);
        }
        fetchQuestions();
      } else {
        examStartedSocketEventRef.current = false; // Reset flag if exam not started
        localStorage.removeItem(FULLSCREEN_REQUIRED_KEY);
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_STOPPED_KEY)) {
          setIsExamStoppedByInvigilator(true);
        }
        if (response?.studentExamPaused || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXAM_PAUSED_KEY))) {
          applyIndividualPauseUi();
        }
        fetchQuestions();
      }
    });

    socket.on('proctor-media-state', ({ videoEnabled, audioEnabled }) => {
      if (videoEnabled !== undefined) {
        setProctorVideoEnabled(videoEnabled !== false);
        proctorVideoEnabledRef.current = videoEnabled !== false;
      }
      if (audioEnabled !== undefined) {
        setProctorAudioEnabled(audioEnabled !== false);
        proctorAudioEnabledRef.current = audioEnabled !== false;
      }
    });

    socket.on('proctor-private-audio-state', ({ targetUserId, enabled }) => {
      const nextTarget = enabled ? targetUserId : null;
      setPrivateAudioTargetUserId(nextTarget);

      const isMe = String(targetUserId) === String(userId) && enabled;
      setPrivateCallWithProctor(isMe);

      if (enabled) {
        if (isMe) {
          setShowPrivateCallNotification(true);
        }
      } else {
        setShowPrivateCallNotification(false);
      }

      // Force update track enabled state on existing proctor audio stream
      const proctorAudioObj = remoteStreamsRef.current.find(s => s.userType === 'staff' && s.kind === 'audio');
      if (proctorAudioObj && proctorAudioObj.stream) {
        const track = proctorAudioObj.stream.getAudioTracks()[0];
        if (track) {
          if (enabled) {
            const isTargetMe = String(targetUserId) === String(userId);
            track.enabled = isTargetMe;
            console.log(`Setting proctor audio track enabled=${isTargetMe}`);
          } else {
            // Restore to global proctor audio state
            track.enabled = proctorAudioEnabledRef.current;
            console.log(`Restoring proctor audio track enabled=${proctorAudioEnabledRef.current}`);
          }
        }
      }
    });


    const handleMobileVerified = (data) => {
      const { userId: verifiedUserId, registrationId: verifiedRegId } = data || {};
      const isMatch = (verifiedRegId && registrationId && String(verifiedRegId) === String(registrationId)) ||
                      (verifiedUserId && userId && String(verifiedUserId) === String(userId));
      if (!isMobileDevice && isMatch) {
        console.log("Remote mobile verification successful! Proceeding...");
        setIsIdentityVerified(true);
      }
    };

    const handleStudentEnteredExam = (data) => {
      const { userId: enteredUserId, isMobile: enteredIsMobile, registrationId: enteredRegId } = data || {};
      const isMatch = (enteredRegId && registrationId && String(enteredRegId) === String(registrationId)) ||
                      (enteredUserId && userId && String(enteredUserId) === String(userId));
      if (!isMobileDevice && enteredIsMobile && isMatch) {
        console.log("Remote mobile device has successfully entered exam hall!");
        setIsMobileEntered(true);
        setIsIdentityVerified(true);
      }
    };

    // Only the laptop mirrors the phone's state; the phone's own isIdentityVerified must not be cleared here.
    const handleUserLeft = (data) => {
      const { userId: leftUserId, isMobile: leftIsMobile, registrationId: leftRegId } = data || {};
      const isMatch = (leftRegId && registrationId && String(leftRegId) === String(registrationId)) ||
                      (leftUserId && userId && String(leftUserId) === String(userId));
      if (!isMobileDevice && leftIsMobile && isMatch) {
        console.warn("Mobile device disconnected from room.");
        setIsIdentityVerified(false);
        setIsMobileEntered(false);
      }
    };

    socket.on('mobile-verified', handleMobileVerified);
    socket.on('student-entered-exam', handleStudentEnteredExam);
    socket.on('user-left', handleUserLeft);

    socket.on('student-exam-submitted', (data) => {
      const {
        userId: submittedUserId,
        user_id,
        registrationId: submittedRegId,
        registration_id
      } = data;

      // Match by either userId or registrationId to be safe across different sessions.
      // 🔹 Use String() conversion to avoid type mismatch issues (number vs string)
      const isMatch = (submittedUserId && String(submittedUserId) === String(userId)) ||
        (user_id && String(user_id) === String(userId)) ||
        (submittedRegId && String(submittedRegId) === String(registrationId)) ||
        (registration_id && String(registration_id) === String(registrationId));

      // 🔹 FIX: Use examFinishedRef.current to avoid stale closure — the socket handler
      // is registered once but `examFinished` state can change without re-registration.
      if (isMatch && !examFinishedRef.current) {
        console.log("🏁 Sync logout: Exam submitted from other device.");
        setExamStarted(false);
        setExamFinished(true); // This removes the "Waiting for Mobile Camera" screen on desktop
        setShowUploadSection(false);
        showToast("Exam submitted successfully from another device. Redirecting...", "success");

        // Auto-redirect after showing the message for 3 seconds
        setTimeout(() => {
          window.location.href = `${BASE_URL}`;
        }, 3000);
      }
    });

    socket.on('exam-ended', () => {
      console.log('Invigilator stopped the examination.');
      examStartedSocketEventRef.current = false;
      setIsExamStoppedByInvigilator(true);
      isExamStoppedByInvigilatorRef.current = true;
      if (currentRoomId && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(EXAM_STOPPED_KEY, '1');
      }
      setExamStarted(false);
      setExamEndTime(null);
      examEndTimeRef.current = null;
      setShowUploadSection(false);
      setIsOneMinuteToStart(false);
      setIsFullPreviewOpen(false);
      setConfirmModal(null);
      setIsOverlayVisible(false);
    });

    return () => {
      clearTimeout(socketStatusTimeout);
      if (socket) {
        socket.off('exam-started');
        socket.off('student-exam-paused');
        socket.off('student-exam-resumed');
        socket.off('proctor-media-state');
        socket.off('proctor-private-audio-state');
        socket.off('exam-ended');
        socket.off('student-exam-submitted');
        socket.off('mobile-verified', handleMobileVerified);
        socket.off('student-entered-exam', handleStudentEnteredExam);
        socket.off('user-left', handleUserLeft);
      }
    };
  }, [socketRef, isConnected, currentRoomId, isMobileDevice, EXAM_STOPPED_KEY, EXAM_PAUSED_KEY, userId]);

  // Device state lives on the server peer, which is recreated on every (re)join: report it again after each join
  // so the student's other device (and late joiners / refreshed tabs) see the real status.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !hasJoinedRoom || !isMobileDevice || !isIdentityVerified) return;
    socket.emit('mobile-verified', { roomId: currentRoomId, userId });
  }, [socketRef, hasJoinedRoom, isMobileDevice, isIdentityVerified, currentRoomId, userId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !hasJoinedRoom || !isStudentReady) return;
    socket.emit('student-entered-exam', { roomId: currentRoomId, userId, isMobile: !!isMobileDevice });
  }, [socketRef, hasJoinedRoom, isStudentReady, isMobileDevice, currentRoomId, userId]);

  // Tab switched / app switched / screen locked / tab closing: the other device shows this one as disconnected.
  // visibilitychange to 'hidden' also fires when the tab is being closed, before the socket drops.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !hasJoinedRoom || typeof document === 'undefined') return;
    const report = () => {
      const hidden = document.visibilityState === 'hidden';
      if (!hidden) nativeCaptureActiveRef.current = false;
      else if (nativeCaptureActiveRef.current) return; // answer-sheet photo via native camera
      socket.emit('device-visibility', { hidden });
    };
    report();
    document.addEventListener('visibilitychange', report);
    return () => document.removeEventListener('visibilitychange', report);
  }, [socketRef, hasJoinedRoom]);

  // Connection state of the student's two devices (this one from local state, the other from the server presence).
  const deviceStatus = useMemo(() => {
    const isSameUser = (u) => {
      if (registrationId && u.registrationId) {
        return String(u.registrationId) === String(registrationId);
      }
      return Boolean(userId && u.userId && String(u.userId) === String(userId));
    };
    const laptopPeer = roomUsers.find(u => isSameUser(u) && !u.isMobile);
    const mobilePeer = roomUsers.find(u => isSameUser(u) && u.isMobile);
    const selfActive = Boolean(isConnected && (isStudentReady || localStreams.length > 0));

    const laptopConnected = isMobileDevice
      ? Boolean(laptopPeer && !laptopPeer.isAway && (laptopPeer.hasCamera || laptopPeer.isEntered || laptopPeer.isVerified))
      : selfActive;

    const isMobileVerified = Boolean(mobilePeer?.isVerified || isIdentityVerified);
    // When exam has started, the mobile MUST have an active camera (hasCamera === true) and not be away
    const isMobileCameraActive = examStarted ? Boolean(mobilePeer && mobilePeer.hasCamera) : true;
    const isMobileReady = examStarted
      ? Boolean(mobilePeer && !mobilePeer.isAway && isMobileCameraActive)
      : Boolean(mobilePeer && (isMobileCameraActive || mobilePeer.isEntered || isMobileEntered));

    const mobileConnected = isMobileDevice
      ? Boolean(selfActive && isIdentityVerified)
      : Boolean(mobilePeer && !mobilePeer.isAway && isMobileVerified && isMobileReady);

    return {
      laptopPeer,
      mobilePeer,
      laptopConnected,
      mobileConnected,
      laptopAway: Boolean(isMobileDevice && laptopPeer?.isAway),
      mobileAway: Boolean(!isMobileDevice && mobilePeer?.isAway),
      bothReady: Boolean(laptopConnected && mobileConnected),
    };
  }, [roomUsers, userId, registrationId, isMobileDevice, isConnected, isStudentReady, localStreams.length, isIdentityVerified, isMobileEntered, examStarted]);

  // Last 15 minutes (answer-sheet scanning window): the laptop may be closed, so the two-device rule is not enforced
  // and no "disconnected" messages are shown.
  const relaxDeviceCheck = Boolean(examStarted && canUploadAnswerSheet);
  const devicesOk = deviceStatus.bothReady || relaxDeviceCheck;

  // The other device dropped mid-exam after both had been connected: 2-minute grace period, then this device is
  // logged out as well. Deadline and "both seen" flag survive a refresh of this device, so refreshing does not reset it.
  const otherDeviceConnected = isMobileDevice ? deviceStatus.laptopConnected : deviceStatus.mobileConnected;
  const [deviceLogoutDeadline, setDeviceLogoutDeadline] = useState(null);
  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !currentRoomId || !registrationId) return;
    const seenKey = `bothDevicesSeen_${currentRoomId}_${registrationId}`;
    const deadlineKey = `deviceLogoutDeadline_${currentRoomId}_${registrationId}`;
    if (examStarted && deviceStatus.bothReady) sessionStorage.setItem(seenKey, '1');

    if (otherDeviceConnected || examFinished || isExamBlockedByInvigilator || isTimeExceeded || relaxDeviceCheck) {
      sessionStorage.removeItem(deadlineKey);
      setDeviceLogoutDeadline(null);
      return;
    }
    // This device not (re)joined yet, or the other device was never connected in this exam: no countdown.
    if (!examStarted || !isConnected || !hasJoinedRoom || !sessionStorage.getItem(seenKey)) {
      setDeviceLogoutDeadline(null);
      return;
    }
    let deadline = Number(sessionStorage.getItem(deadlineKey)) || 0;
    if (!deadline) {
      deadline = Date.now() + DEVICE_DISCONNECT_LOGOUT_MS;
      sessionStorage.setItem(deadlineKey, String(deadline));
    }
    setDeviceLogoutDeadline(deadline);
  }, [currentRoomId, registrationId, examStarted, deviceStatus.bothReady, otherDeviceConnected, examFinished,
    isExamBlockedByInvigilator, isTimeExceeded, relaxDeviceCheck, isConnected, hasJoinedRoom]);

  useEffect(() => {
    if (!deviceLogoutDeadline) return;
    const t = setTimeout(() => {
      console.warn('⏱️ Other device disconnected for 2 minutes: logging out of the exam');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(`deviceLogoutDeadline_${currentRoomId}_${registrationId}`);
        sessionStorage.removeItem(`bothDevicesSeen_${currentRoomId}_${registrationId}`);
      }
      if (socketRef.current?.connected) {
        socketRef.current.emit('monitoring-warning', {
          roomId: String(currentRoomId),
          userId: String(userId),
          warningType: isMobileDevice ? 'laptop_disconnected_logout' : 'mobile_disconnected_logout',
        });
      }
      phpApi.logExamActivity({
        registration_id: registrationId,
        event: 'AUTO_LOGOUT_DEVICE_DISCONNECTED',
        device: isMobileDevice ? 'mobile' : 'laptop',
      }).catch(() => {});
      // Short delay so the proctor notification leaves before the page unloads.
      setTimeout(() => { window.location.href = BASE_URL; }, 300);
    }, Math.max(0, deviceLogoutDeadline - Date.now()));
    return () => clearTimeout(t);
  }, [deviceLogoutDeadline, currentRoomId, registrationId, userId, isMobileDevice, socketRef, BASE_URL]);

  // ── Tab-switch / fullscreen-exit: activity log + periodic shared-screen evidence (every 2s) ──
  // Source: the same getDisplayMedia track published to the invigilator (getScreenShareTrack).
  // Browser APIs cannot read another tab's pixels from the exam page; only the authorized screen share can.
  const SCREEN_EVIDENCE_INTERVAL_MS = 2000;
  const SCREEN_EVIDENCE_JPEG_QUALITY = 0.65;
  const SCREEN_EVIDENCE_MAX_WIDTH = 1280;
  const SCREEN_EVIDENCE_QUEUE_MAX_ITEMS = 8;
  const SCREEN_EVIDENCE_QUEUE_MAX_BYTES = 2 * 1024 * 1024; // waiting frames only

  const [isExamTabHidden, setIsExamTabHidden] = useState(() => (
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  ));
  const screenEvidenceTimerRef = useRef(null);
  const screenEvidenceCaptureInFlightRef = useRef(false);
  const screenEvidenceVideoRef = useRef(null); // off-DOM <video> bound to the screen track
  const screenEvidenceTrackRef = useRef(null);
  const screenEvidenceQueueRef = useRef(null);
  const screenEvidenceEpisodeRef = useRef(null); // { startedAt, sawTabHidden, lastFrame, captureFailed, captureReason, frameCount }
  const screenEvidenceReasonsRef = useRef({ tabHidden: false, fullscreenExit: false });
  const tabSwitchCountRef = useRef(0);
  const tabSwitchFinalizeInFlightRef = useRef(false);
  const fullscreenExitLoggedRef = useRef(false);
  const screenEvidenceMetaRef = useRef({});
  useEffect(() => {
    screenEvidenceMetaRef.current = {
      assessmentId, registrationId, userId, currentRoomId, decoded, socketRef,
    };
  }, [assessmentId, registrationId, userId, currentRoomId, decoded, socketRef]);

  const teardownScreenEvidenceVideo = useCallback(() => {
    const video = screenEvidenceVideoRef.current;
    if (video) {
      try { video.pause(); } catch (_) { /* ignore */ }
      video.srcObject = null;
      if (video.parentNode) video.parentNode.removeChild(video);
      screenEvidenceVideoRef.current = null;
    }
    const track = screenEvidenceTrackRef.current;
    if (track && track._screenEvidenceOnEnded) {
      try { track.removeEventListener('ended', track._screenEvidenceOnEnded); } catch (_) { /* ignore */ }
      delete track._screenEvidenceOnEnded;
    }
    screenEvidenceTrackRef.current = null;
  }, []);

  const ensureScreenEvidenceVideo = useCallback(async () => {
    const track = typeof getScreenShareTrackRef.current === 'function' ? getScreenShareTrackRef.current() : null;
    if (!track || track.readyState !== 'live') {
      teardownScreenEvidenceVideo();
      return { video: null, track: null, reason: 'screen_share_track_unavailable' };
    }
    const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
    // Entire-screen share is the reliable external-content source; window/tab shares are not treated as external evidence.
    if (settings.displaySurface && settings.displaySurface !== 'monitor') {
      teardownScreenEvidenceVideo();
      return { video: null, track: null, reason: `screen_share_surface_${settings.displaySurface}` };
    }
    if (screenEvidenceTrackRef.current === track && screenEvidenceVideoRef.current) {
      return { video: screenEvidenceVideoRef.current, track, reason: null };
    }
    teardownScreenEvidenceVideo();
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('aria-hidden', 'true');
    video.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    video.srcObject = new MediaStream([track]);
    if (typeof document !== 'undefined' && document.body) {
      document.body.appendChild(video);
    }
    try {
      await video.play();
    } catch (err) {
      console.warn('Screen evidence video play failed:', err?.message || err);
      video.srcObject = null;
      if (video.parentNode) video.parentNode.removeChild(video);
      return { video: null, track: null, reason: 'screen_video_play_failed' };
    }
    const onEnded = () => {
      console.warn('Screen-share track ended during evidence capture');
      teardownScreenEvidenceVideo();
    };
    track._screenEvidenceOnEnded = onEnded;
    track.addEventListener('ended', onEnded);
    screenEvidenceVideoRef.current = video;
    screenEvidenceTrackRef.current = track;
    return { video, track, reason: null };
  }, [teardownScreenEvidenceVideo]);

  const captureFrameFromScreenTrack = useCallback(async () => {
    const ensured = await ensureScreenEvidenceVideo();
    if (!ensured.video || !ensured.track) {
      return { frame: null, reason: ensured.reason || 'screen_share_track_unavailable' };
    }
    const { video, track } = ensured;
    try {
      if (typeof ImageCapture !== 'undefined') {
        const bitmap = await new ImageCapture(track).grabFrame();
        const canvas = document.createElement('canvas');
        let w = bitmap.width;
        let h = bitmap.height;
        if (w > SCREEN_EVIDENCE_MAX_WIDTH) {
          h = Math.round((h * SCREEN_EVIDENCE_MAX_WIDTH) / w);
          w = SCREEN_EVIDENCE_MAX_WIDTH;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, w, h);
        if (typeof bitmap.close === 'function') bitmap.close();
        return { frame: canvas.toDataURL('image/jpeg', SCREEN_EVIDENCE_JPEG_QUALITY), reason: null };
      }
    } catch (err) {
      console.warn('ImageCapture.grabFrame failed for screen evidence:', err?.message || err);
    }
    if (!video.videoWidth || !video.videoHeight) {
      await new Promise((resolve) => {
        if (video.videoWidth > 0) resolve();
        else {
          video.onloadeddata = () => resolve();
          setTimeout(resolve, 300);
        }
      });
    }
    if (!video.videoWidth || !video.videoHeight) {
      return { frame: null, reason: 'screen_frame_not_ready' };
    }
    try {
      return { frame: captureFrame(video, SCREEN_EVIDENCE_JPEG_QUALITY, SCREEN_EVIDENCE_MAX_WIDTH), reason: null };
    } catch (err) {
      console.warn('Video drawImage failed for screen evidence:', err?.message || err);
      return { frame: null, reason: 'screen_frame_capture_failed' };
    }
  }, [ensureScreenEvidenceVideo]);

  const ensureScreenEvidenceQueue = useCallback(() => {
    if (screenEvidenceQueueRef.current) return screenEvidenceQueueRef.current;
    screenEvidenceQueueRef.current = createBoundedUploadQueue({
      maxItems: SCREEN_EVIDENCE_QUEUE_MAX_ITEMS,
      maxBytes: SCREEN_EVIDENCE_QUEUE_MAX_BYTES,
      onDrop: (item, reason) => {
        console.warn('[ScreenEvidence] dropped frame (backpressure):', reason, item?.violationType);
      },
      upload: async (item) => {
        const meta = screenEvidenceMetaRef.current;
        if (!meta.assessmentId || !meta.registrationId || !item?.image) return false;
        try {
          const formData = new FormData();
          formData.append('assessment_id', String(meta.assessmentId));
          formData.append('registration_id', String(meta.registrationId));
          formData.append('user_id', String(meta.userId));
          formData.append('image', item.image);
          formData.append('violation_type', item.violationType || 'tab_switch');
          formData.append('evidence_only', '1');
          const uploadData = await nodeApi.violationUpload(formData);
          if (!(uploadData && uploadData.status === 'success' && uploadData.s3Key)) {
            console.warn('[ScreenEvidence] upload did not return s3Key:', uploadData);
            return false;
          }
          // Persist each frame as its own violation-log row so Violation Details can list snapshots.
          tabSwitchCountRef.current += 1;
          const vType = item.violationType || 'tab_switch';
          const payload = {
            violation_type: vType === 'fullscreen_exit' ? 'fullscreen_exit' : 'tab_switch',
            user_id: meta.userId,
            tab_change_count: tabSwitchCountRef.current,
            message: vType === 'fullscreen_exit'
              ? 'Fullscreen exit — screen evidence'
              : 'Tab switch — screen evidence',
            image_s3_path: uploadData.s3Key,
            course_code: meta.decoded?.course,
            exam_date: meta.decoded?.exam_date,
            exam_time: meta.decoded?.exam_time,
            registration_id: meta.registrationId,
          };
          await phpApi.recordViolation(payload).catch((e) => {
            console.warn('[ScreenEvidence] recordViolation failed after S3 upload:', e);
          });
          return true;
        } catch (err) {
          console.warn('[ScreenEvidence] upload failed:', err?.message || err);
          return false;
        }
      },
    });
    return screenEvidenceQueueRef.current;
  }, []);

  const enqueueScreenEvidenceFrame = useCallback((frameDataUrl, violationType) => {
    if (!frameDataUrl) return;
    const queue = ensureScreenEvidenceQueue();
    const size = Math.round((frameDataUrl.length * 3) / 4); // approx decoded bytes
    queue.enqueue({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      image: frameDataUrl,
      violationType: violationType || 'tab_switch',
      size,
    });
  }, [ensureScreenEvidenceQueue]);

  const finalizeTabSwitchViolation = useCallback(async (episode) => {
    if (!episode || !episode.sawTabHidden || tabSwitchFinalizeInFlightRef.current) return;
    // Frames already stored as individual rows — only notify proctor, do not add a summary row.
    if ((episode.frameCount || 0) > 0) {
      const meta = screenEvidenceMetaRef.current;
      if (meta.socketRef?.current?.connected) {
        meta.socketRef.current.emit('monitoring-warning', {
          roomId: String(meta.currentRoomId),
          userId: String(meta.userId),
          warningType: 'tab_switch',
        });
      }
      return;
    }
    // No usable screenshot (e.g. screen_share_track_unavailable): do not create a violation row.
    if (episode.captureFailed) {
      console.warn('Tab-switch evidence not captured; skipping violation log:', episode.captureReason || 'unknown');
      return;
    }
    if (!allowViolationReportRef.current('tab_switch')) {
      console.log('Tab-switch violation already reported within throttle window; skipping duplicate');
      return;
    }
    tabSwitchFinalizeInFlightRef.current = true;
    tabSwitchCountRef.current += 1;
    const tabChangeCount = tabSwitchCountRef.current;
    const meta = screenEvidenceMetaRef.current;
    try {
      if (meta.socketRef?.current?.connected) {
        meta.socketRef.current.emit('monitoring-warning', {
          roomId: String(meta.currentRoomId),
          userId: String(meta.userId),
          warningType: 'tab_switch',
        });
      }
      const payload = {
        violation_type: 'tab_switch',
        user_id: meta.userId,
        tab_change_count: tabChangeCount,
        message: 'Tab switch detected',
        image_s3_path: null,
        course_code: meta.decoded?.course,
        exam_date: meta.decoded?.exam_date,
        exam_time: meta.decoded?.exam_time,
      };
      if (meta.registrationId) payload.registration_id = meta.registrationId;
      await phpApi.recordViolation(payload).catch((e) => {
        console.warn('Tab-switch recordViolation failed:', e);
      });
    } finally {
      tabSwitchFinalizeInFlightRef.current = false;
    }
  }, []);

  const stopScreenEvidenceLoop = useCallback((finalize = false) => {
    if (screenEvidenceTimerRef.current) {
      clearInterval(screenEvidenceTimerRef.current);
      screenEvidenceTimerRef.current = null;
    }
    const episode = screenEvidenceEpisodeRef.current;
    screenEvidenceEpisodeRef.current = null;
    teardownScreenEvidenceVideo();
    if (finalize && episode) {
      finalizeTabSwitchViolation(episode);
    }
  }, [finalizeTabSwitchViolation, teardownScreenEvidenceVideo]);

  const tickScreenEvidenceCapture = useCallback(async () => {
    if (screenEvidenceCaptureInFlightRef.current) return; // never overlap captures
    const episode = screenEvidenceEpisodeRef.current;
    if (!episode) return;
    screenEvidenceCaptureInFlightRef.current = true;
    try {
      const { frame, reason } = await captureFrameFromScreenTrack();
      if (!screenEvidenceEpisodeRef.current) return; // stopped while capturing
      if (frame) {
        episode.lastFrame = frame;
        episode.captureFailed = false;
        episode.captureReason = null;
        episode.frameCount = (episode.frameCount || 0) + 1;
        const reasons = screenEvidenceReasonsRef.current;
        const violationType = reasons.tabHidden ? 'tab_switch' : 'fullscreen_exit';
        enqueueScreenEvidenceFrame(frame, violationType);
      } else {
        episode.captureFailed = true;
        episode.captureReason = reason || 'screen_frame_unavailable';
        if (reason === 'screen_share_track_unavailable') {
          // Stream gone: stop loop; leave episode for later finalize if tab-away ends.
          if (screenEvidenceTimerRef.current) {
            clearInterval(screenEvidenceTimerRef.current);
            screenEvidenceTimerRef.current = null;
          }
          teardownScreenEvidenceVideo();
        }
      }
    } finally {
      screenEvidenceCaptureInFlightRef.current = false;
    }
  }, [captureFrameFromScreenTrack, enqueueScreenEvidenceFrame, teardownScreenEvidenceVideo]);

  const startScreenEvidenceLoop = useCallback(() => {
    if (screenEvidenceTimerRef.current) return; // single loop only
    // Immediate first frame, then every 2 seconds.
    tickScreenEvidenceCapture();
    screenEvidenceTimerRef.current = setInterval(() => {
      if (!screenEvidenceEpisodeRef.current) return;
      tickScreenEvidenceCapture();
    }, SCREEN_EVIDENCE_INTERVAL_MS);
  }, [tickScreenEvidenceCapture]);

  // Drive the single capture loop from tab-hidden and/or fullscreen-exit; overlapping reasons share one timer.
  useEffect(() => {
    const fullscreenExitActive = !!(shouldForceFullscreen && !isFullscreenActive && !isMobileDevice);
    const tabHiddenActive = !!(isExamTabHidden && !isMobileDevice);
    const examActive = !!(examStarted && !examFinished && !isExamBlockedByInvigilator);
    const shouldCapture = examActive && (tabHiddenActive || fullscreenExitActive);
    const prevReasons = screenEvidenceReasonsRef.current;
    screenEvidenceReasonsRef.current = { tabHidden: tabHiddenActive, fullscreenExit: fullscreenExitActive };

    // Tab returned while fullscreen-exit (or other) capture continues: finalize the tab-switch record once.
    if (prevReasons.tabHidden && !tabHiddenActive && screenEvidenceEpisodeRef.current?.sawTabHidden) {
      const episodeSnapshot = { ...screenEvidenceEpisodeRef.current };
      screenEvidenceEpisodeRef.current.sawTabHidden = false;
      finalizeTabSwitchViolation(episodeSnapshot);
    }

    if (!shouldCapture) {
      const hadEpisode = !!screenEvidenceEpisodeRef.current;
      const sawTab = screenEvidenceEpisodeRef.current?.sawTabHidden;
      stopScreenEvidenceLoop(hadEpisode && sawTab);
      return undefined;
    }

    if (!screenEvidenceEpisodeRef.current) {
      screenEvidenceEpisodeRef.current = {
        startedAt: Date.now(),
        sawTabHidden: tabHiddenActive,
        lastFrame: null,
        captureFailed: true,
        captureReason: 'capture_in_progress',
        frameCount: 0,
      };
    } else if (tabHiddenActive) {
      screenEvidenceEpisodeRef.current.sawTabHidden = true;
    }

    // Screen share required for external-screen frames; do not fall back to exam-page DOM.
    const track = typeof getScreenShareTrackRef.current === 'function' ? getScreenShareTrackRef.current() : null;
    if (!track || track.readyState !== 'live') {
      if (screenEvidenceEpisodeRef.current) {
        screenEvidenceEpisodeRef.current.captureFailed = true;
        screenEvidenceEpisodeRef.current.captureReason = 'screen_share_track_unavailable';
      }
      if (screenEvidenceTimerRef.current) {
        clearInterval(screenEvidenceTimerRef.current);
        screenEvidenceTimerRef.current = null;
      }
      teardownScreenEvidenceVideo();
      return undefined;
    }

    startScreenEvidenceLoop();
    return undefined;
  }, [
    examStarted, examFinished, isExamBlockedByInvigilator, isExamTabHidden, isFullscreenActive,
    shouldForceFullscreen, isMobileDevice, isScreenSharing, startScreenEvidenceLoop, stopScreenEvidenceLoop,
    teardownScreenEvidenceVideo, finalizeTabSwitchViolation,
  ]);

  // Cleanup on unmount: stop timer/video; do not finalize (avoids duplicate records on remount).
  useEffect(() => () => {
    if (screenEvidenceTimerRef.current) {
      clearInterval(screenEvidenceTimerRef.current);
      screenEvidenceTimerRef.current = null;
    }
    screenEvidenceEpisodeRef.current = null;
    teardownScreenEvidenceVideo();
  }, [teardownScreenEvidenceVideo]);

  // Existing tab visibility activity log + drive isExamTabHidden for the capture loop.
  useEffect(() => {
    if (!examStarted || examFinished || !registrationId || typeof document === 'undefined') return;
    const handleVisibility = () => {
      if (nativeCaptureActiveRef.current) return;
      const hidden = document.visibilityState === 'hidden';
      setIsExamTabHidden(hidden);
      const ev = hidden
        ? (isMobileDevice ? 'MOBILE_DISCONNECTED' : 'TAB_SWITCHED_AWAY')
        : (isMobileDevice ? 'MOBILE_RECONNECTED' : 'TAB_RETURNED');
      phpApi.logExamActivity({
        registration_id: registrationId,
        event: ev,
        device: isMobileDevice ? 'mobile' : 'laptop',
      }).catch(() => {});
    };
    setIsExamTabHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [examStarted, examFinished, registrationId, isMobileDevice]);

  // Fullscreen exit / restore activity log (events already supported by PHP / ActivityLogModal).
  useEffect(() => {
    if (!examStarted || examFinished || !registrationId || isMobileDevice) return;
    if (!shouldForceFullscreen) {
      fullscreenExitLoggedRef.current = false;
      return;
    }
    if (!isFullscreenActive) {
      if (!fullscreenExitLoggedRef.current) {
        fullscreenExitLoggedRef.current = true;
        phpApi.logExamActivity({
          registration_id: registrationId,
          event: 'FULLSCREEN_EXITED',
          device: 'laptop',
        }).catch(() => {});
      }
    } else if (fullscreenExitLoggedRef.current) {
      fullscreenExitLoggedRef.current = false;
      phpApi.logExamActivity({
        registration_id: registrationId,
        event: 'FULLSCREEN_RESTORED',
        device: 'laptop',
      }).catch(() => {});
    }
  }, [examStarted, examFinished, registrationId, isMobileDevice, shouldForceFullscreen, isFullscreenActive]);

  // ── Log Other Device Disconnect / Reconnect during Exam ──
  const prevOtherDeviceConnectedRef = useRef(null);
  useEffect(() => {
    if (!examStarted || examFinished || !registrationId || !isConnected || !hasJoinedRoom) return;
    const seenKey = `bothDevicesSeen_${currentRoomId}_${registrationId}`;
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(seenKey)) return;

    if (prevOtherDeviceConnectedRef.current !== null && prevOtherDeviceConnectedRef.current !== otherDeviceConnected) {
      const otherDeviceType = isMobileDevice ? 'laptop' : 'mobile';
      const ev = otherDeviceConnected
        ? (isMobileDevice ? 'LAPTOP_RECONNECTED' : 'MOBILE_RECONNECTED')
        : (isMobileDevice ? 'LAPTOP_DISCONNECTED' : 'MOBILE_DISCONNECTED');
      phpApi.logExamActivity({
        registration_id: registrationId,
        event: ev,
        device: otherDeviceType,
      }).catch(() => {});
    }
    prevOtherDeviceConnectedRef.current = otherDeviceConnected;
  }, [examStarted, examFinished, registrationId, isConnected, hasJoinedRoom, currentRoomId, otherDeviceConnected, isMobileDevice]);

  // ── Log Socket Connection Lost / Restored during Exam ──
  const wasConnectedRef = useRef(null);
  useEffect(() => {
    if (!examStarted || examFinished || !registrationId) return;
    if (wasConnectedRef.current !== null && wasConnectedRef.current !== isConnected) {
      phpApi.logExamActivity({
        registration_id: registrationId,
        event: isConnected ? 'CONNECTION_RESTORED' : 'CONNECTION_LOST',
        device: isMobileDevice ? 'mobile' : 'laptop',
      }).catch(() => {});
    }
    wasConnectedRef.current = isConnected;
  }, [examStarted, examFinished, registrationId, isConnected, isMobileDevice]);

  // Fetch exam status from PHP on load so timer works on mobile (socket may connect later; PHP gives remaining_seconds immediately)
  useEffect(() => {
    if (!currentRoomId || !registrationId || initialExamStatusFetchedRef.current) return;

    initialExamStatusFetchedRef.current = true;
    const t = setTimeout(() => {
      fetchQuestions();
    }, 400);

    return () => clearTimeout(t);
  }, [currentRoomId, registrationId, fetchQuestions]);

  // Retry fetching timer when exam has started but examEndTime wasn't set (e.g. mobile join after start or get-exam-status missed)
  const timerRetryCountRef = useRef(0);
  useEffect(() => {
    if (!examStarted || examEndTime || examFinished || showUploadSection || isExamBlockedByInvigilator || isExamStoppedByInvigilatorRef.current || isStudentPausedByInvigilatorRef.current) return; return;
    const maxRetries = 2;
    if (timerRetryCountRef.current >= maxRetries) return;

    const t = setTimeout(() => {
      timerRetryCountRef.current += 1;
      fetchQuestions();
    }, 2000);
    return () => clearTimeout(t);
  }, [examStarted, examEndTime, examFinished, showUploadSection, fetchQuestions]);

  // ── Auto-Retry when scheduled exam time has passed but exam hasn't started yet ──
  // This handles the case where the student was on the "Waiting for Proctor" screen,
  // the scheduled start time passed, but they missed the `exam-started` socket event
  // (e.g. brief disconnect, app in background, etc.).
  // Every 15 seconds after the scheduled start time, we re-poll fetchQuestions AND
  // attempt to re-listen on the socket if it had reconnected.
  useEffect(() => {
    // Only run while waiting (not started, not finished)
    if (examStarted || examFinished || showUploadSection || isExamBlockedByInvigilator || isExamStoppedByInvigilatorRef.current || isStudentPausedByInvigilatorRef.current) return;

    const POLL_INTERVAL_MS = 15000; // 15 seconds

    const tick = () => {
      const scheduledEpoch = examScheduledStartEpochRef.current;
      const now = Date.now();
      if (!scheduledEpoch || now < scheduledEpoch) return; // Not yet time

      const minutesPast = Math.floor((now - scheduledEpoch) / 60000);
      // Stop auto-polling if more than 90 minutes have passed (exam may have ended)
      if (minutesPast > 90) return;

      console.log(`⏰ Scheduled start time has passed (${minutesPast}m ago). Auto-retrying fetchQuestions...`);
      fetchQuestions();
    };

    // Fire once immediately in case we just crossed the threshold
    tick();

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [examStarted, examFinished, showUploadSection, fetchQuestions]);

  // Watchdog
  useEffect(() => {
    // 🔹 FIX: Don't show overlay if exam is already completed or in upload section (prevents overlay after refresh)
    if (showUploadSection || examFinished) {
      setIsOverlayVisible(false);
      return;
    }

    if (examStarted && !showUploadSection && !examFinished) {
      // On mobile, fullscreen is not required
      if (!isFullscreenActive && !isMobileDevice) {
        setIsOverlayVisible(true);
        setIsStudentReady(false);
        return;
      }
      // On mobile, screen sharing is not required, so skip that check
      if (!isScreenSharing && !isMobileDevice) {
        setIsOverlayVisible(true);
        setIsStudentReady(false);
      } else {
        // On mobile: also require identity verification before allowing exam access
        if (capturedImage && (!isMobileDevice || isIdentityVerified)) {
          setIsOverlayVisible(false);
          setIsStudentReady(true);
        }
      }
    }
  }, [examStarted, isScreenSharing, isHumanVerified, capturedImage, showUploadSection, examFinished, isMobileDevice, isFullscreenActive, isIdentityVerified]);

  // MOBILE SHORTCUT: On mobile + offline exam, jump straight to upload section
  // as soon as the exam starts — but ONLY after:
  //   1. Identity verification passes
  //   2. The laptop/desktop has joined the room (laptopJoined)
  // This ensures the unified waiting screen is shown on mobile until the laptop connects.
  // Once the laptop joins (roomUsers updates), this effect re-runs and proceeds.
  useEffect(() => {
    // Check if laptop peer of the same user is present in the room
    const laptopJoined = roomUsers.some(
      u => String(u.userId) === String(userId) && !u.isMobile
    );

    if (!examStarted || isExamBlockedByInvigilator) {
      if (showUploadSection) setShowUploadSection(false);
      return;
    }
    if (
      isMobileDevice &&          // mobile device only
      isOfflineMode &&           // offline (paper-based) exam only
      examStarted &&             // exam has been started by proctor
      isIdentityVerified &&      // identity check must pass first
      laptopJoined &&            // laptop must be connected before proceeding
      !showUploadSection &&      // not already on upload screen
      !examFinished &&           // exam not yet submitted
      !isTimeExceeded            // time must not be exceeded
    ) {
      setShowUploadSection(true);
    }
  }, [isMobileDevice, isOfflineMode, examStarted, isIdentityVerified, showUploadSection, examFinished, isTimeExceeded, roomUsers, userId, isExamBlockedByInvigilator]);

  useEffect(() => {
    proctorVideoEnabledRef.current = proctorVideoEnabled;
    // The received track was disabled while the proctor camera was off; enable it again before (re)attaching.
    proctorVideo?.stream?.getVideoTracks?.().forEach(t => { t.enabled = proctorVideoEnabled; });
    if (proctorVideo && proctorVideoRef.current && proctorVideoEnabled) {
      attachStream(proctorVideoRef.current, proctorVideo);
    }
  }, [proctorVideo, proctorVideoEnabled]);

  useEffect(() => {
    if (proctorScreen && proctorScreenRef.current) attachStream(proctorScreenRef.current, proctorScreen);
  }, [proctorScreen]);

  useEffect(() => {
    const showLivePreview = isOverlayVisible && !examFinished && !showUploadSection && checkPreviewRef.current && localStreams.length > 0 &&
      (!capturedImage || (isMobileDeviceFallback && capturedImage && !isIdentityVerified));
    if (showLivePreview) {
      // Use selected camera: rear = alternate stream; front = refreshed stream (after switch) or original
      const selectedStream = livenessCameraIndex === 1 && alternateLivenessStream
        ? alternateLivenessStream
        : (refreshedFrontStream || localStreams[0]);
      attachStream(checkPreviewRef.current, selectedStream);
    }
    if (!isOverlayVisible) {
      if (localVideoRef1.current && localStreams[0]) attachStream(localVideoRef1.current, localStreams[0]);
      if (localVideoRef2.current && localStreams[1]) attachStream(localVideoRef2.current, localStreams[1]);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') recoverVideoStreams();
    };

    const handleWindowFocus = () => {
      recoverVideoStreams();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [localStreams, isOverlayVisible, capturedImage, localVideoRef1, localVideoRef2, examFinished, showUploadSection, livenessCameraIndex, alternateLivenessStream, refreshedFrontStream, isMobileDeviceFallback, isIdentityVerified, recoverVideoStreams]);

  useEffect(() => {
    if (showCameraModal && cameraModalVideoRef.current) {
      // Priority: 1. Switched/Alternate Stream, 2. Refreshed Front, 3. Original Mediasoup track
      const streamToAttach = alternateCameraStream || refreshedFrontStream || (localStreams[0]?.stream || localStreams[0]);

      if (streamToAttach) {
        attachStream(cameraModalVideoRef.current, streamToAttach);
      }
    }
  }, [showCameraModal, alternateCameraStream, refreshedFrontStream, localStreams, capturedPhotoForCrop]); // 🔹 FIX: Add capturedPhotoForCrop to reattach stream on retake

  // Cleanup refreshed front stream on unmount (mobile camera switch)
  useEffect(() => {
    return () => {
      if (refreshedFrontStreamRef.current) {
        refreshedFrontStreamRef.current.getTracks().forEach((t) => t.stop());
        refreshedFrontStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // 🔹 FIX: Skip liveness scanning if capturedImage already exists (liveness optional once image stored)
    if (localStreams.length > 0 && checkPreviewRef.current && !isHumanVerified && !capturedImage && isOverlayVisible && !showUploadSection && !examFinished) {
      const timer = setTimeout(() => startLiveScanning(), 1000);
      return () => clearTimeout(timer);
    }
    return () => { if (scanInterval.current) clearInterval(scanInterval.current); };
  }, [localStreams, isHumanVerified, capturedImage, startLiveScanning, isOverlayVisible, showUploadSection, examFinished]);

  // --- EXAM PROGRESS ALERTS + TIME EXCEEDED ---
  // Checked every second from the clock directly (the visible countdown lives in ExamTimerDisplay), so ExamRoom
  // re-renders only when an alert fires or the time runs out, not on every tick.
  useEffect(() => {
    if (!examStarted || examFinished || !examEndTime) return;

    const triggerAlert = (text) => {
      setInlineAlertMessage(text);

      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();

        // Split text into chunks to avoid the Chrome 15-second speech limit and cloud voice dropouts
        let chunks = text.match(/[^.!?]+[.!?]+/g);
        if (!chunks || chunks.length === 0) chunks = [text];

        // Store utterances globally to prevent garbage collection cutting off audio
        window.speechUtterances = window.speechUtterances || [];
        let chunkIndex = 0;

        const speakNextChunk = () => {
          if (chunkIndex >= chunks.length) {
            window.speechUtterances = []; // Clean up
            return;
          }

          const chunkText = chunks[chunkIndex].trim();
          if (!chunkText) {
            chunkIndex++;
            speakNextChunk();
            return;
          }

          const utterance = new SpeechSynthesisUtterance(chunkText);

          // Prefer a local voice to eliminate the 5-10 second network latency of cloud voices
          const voices = window.speechSynthesis.getVoices();
          if (voices && voices.length > 0) {
            const localEn = voices.find(v => v.localService && v.lang.startsWith('en'));
            const localAny = voices.find(v => v.localService);
            if (localEn) {
              utterance.voice = localEn;
            } else if (localAny) {
              utterance.voice = localAny;
            }
          }

          window.speechUtterances.push(utterance);
          window.currentUtterance = utterance; // legacy fallback

          utterance.onend = () => {
            chunkIndex++;
            speakNextChunk();
          };

          utterance.onerror = (e) => {
            console.warn('Speech synthesis error for chunk:', e);
            chunkIndex++;
            speakNextChunk();
          };

          window.speechSynthesis.speak(utterance);
        };

        // Add a slight delay before speaking to ensure cancel() has completed
        setTimeout(() => {
          speakNextChunk();
        }, 50);
      }
    };

    const check = () => {
      const timeRemaining = Math.max(0, Math.floor((examEndTime - (Date.now() + serverOffset)) / 1000));

      if (timeRemaining <= 0) {
        // Auto Submit disabled as per requirement
        // handleAutoSubmit();
        setIsTimeExceeded(true);
        setShowUploadSection(false);
        return;
      }
      if (!examStartedAtRef.current) return;

      const totalDurationSec = Math.floor((examEndTime - examStartedAtRef.current) / 1000);
      if (totalDurationSec <= 0) return;

      const elapsedTimeSec = totalDurationSec - timeRemaining;
      const progress = (elapsedTimeSec / totalDurationSec) * 100;

      const remainingMinutes = Math.ceil(timeRemaining / 60);

      // Rearm alerts if progress went backwards (e.g. time was extended)
      if (progress < 50) alert50Shown.current = false;
      if (progress < 75) alert75Shown.current = false;
      if (progress < 90) alert90Shown.current = false;

      // Handle initial page load where progress might already be high
      if (!alertsInitialized.current) {
        alertsInitialized.current = true;
        if (progress >= 50) alert50Shown.current = true;
        if (progress >= 75) alert75Shown.current = true;
        if (progress >= 90) alert90Shown.current = true;
        return;
      }

      if (progress >= 50 && progress < 75 && !alert50Shown.current) {
        alert50Shown.current = true;
        triggerAlert(`${remainingMinutes} minutes remaining to complete the exam. Please plan your time to finish writing and prepare for submission.`);
      } else if (progress >= 75 && progress < 90 && !alert75Shown.current) {
        alert75Shown.current = true;
        triggerAlert(`Only ${remainingMinutes} minutes remaining to complete the exam. Please start preparing your answer sheets for submission.`);
      } else if (progress >= 90 && !alert90Shown.current) {
        alert90Shown.current = true;
        triggerAlert(`Only ${remainingMinutes} minutes left to complete your uploads. No time extension will be provided. Please ensure all answer sheets are ready for upload. Kindly take clear pictures of your answer sheets and submit them immediately.`);
      }
    };

    check();
    const tick = setInterval(check, 1000);
    return () => clearInterval(tick);
  }, [examStarted, examFinished, examEndTime, serverOffset]);

  // Retake Expiration Timer (10 minutes after exam drops)
  useEffect(() => {
    if (!examStarted) return;

    // Set a timeout to expire the retake option 10 minutes (600,000 ms) after the exam starts
    const timer = setTimeout(() => {
      setRetakeExpired(true);
    }, 10 * 60 * 1000);

    return () => clearTimeout(timer);
  }, [examStarted]);

  // Retry snapshot fetch when exam starts (if still missing after refresh)
  useEffect(() => {
    if (!examStarted || !isStudentReady || capturedImage || !assessmentId || !registrationId) {
      return; // Don't retry if exam not started, already have image, or missing IDs
    }

    // Retry fetching snapshot when exam starts (important after page refresh)
    const retrySnapshotFetch = async () => {
      console.log('🔄 Exam started but snapshot missing, retrying fetch...');
      try {
        // Try Redis first - request base64 to avoid CORS
        const getResult = await nodeApi.snapshotGet({
          assessment_id: assessmentId,
          registration_id: registrationId,
          return_base64: true
        }).catch(() => null);

        if (getResult && getResult.status === 'success') {
          if (getResult.base64) {
            console.log('Snapshot retrieved on exam start (base64)');
            setCapturedImage(getResult.base64);
            return;
          } else if (getResult.s3Url) {
            // Convert S3 URL to base64 immediately via backend proxy (avoids direct S3 fetch)
            console.log('Snapshot retrieved on exam start (URL), converting to base64 via proxy...');
            try {
              const base64 = await fetchImageAsBase64(getResult.s3Url);
              console.log('Converted to base64 successfully');
              setCapturedImage(base64);
              return;
            } catch (convertError) {
              console.warn('Failed to convert URL to base64, storing URL as fallback:', convertError.message);
              setCapturedImage(getResult.s3Url);
              return;
            }
          }
        }

        // Fallback to S3 check - request base64 to avoid CORS
        const checkResult = await nodeApi.snapshotCheck({
          assessment_id: assessmentId,
          registration_id: registrationId,
          return_base64: true
        }).catch(() => null);

        if (checkResult && checkResult.status === 'success' && checkResult.exists) {
          if (checkResult.base64) {
            console.log('Snapshot retrieved via S3 check on exam start (base64)');
            setCapturedImage(checkResult.base64);
            return;
          } else if (checkResult.s3Url) {
            // Convert S3 URL to base64 immediately via backend proxy (avoids direct S3 fetch)
            console.log('Snapshot retrieved via S3 check on exam start (URL), converting to base64 via proxy...');
            try {
              const base64 = await fetchImageAsBase64(checkResult.s3Url);
              console.log('Converted to base64 successfully');
              setCapturedImage(base64);
              return;
            } catch (convertError) {
              console.warn('Failed to convert URL to base64, storing URL as fallback:', convertError.message);
              setCapturedImage(checkResult.s3Url);
              return;
            }
          }
        }

        console.warn('Snapshot still not found after exam start. Monitoring will not run until snapshot is available.');
      } catch (err) {
        console.warn('Failed to retry snapshot fetch on exam start:', err.message);
      }
    };

    // Small delay to allow other effects to complete first
    const timeoutId = setTimeout(retrySnapshotFetch, 500);
    return () => clearTimeout(timeoutId);
  }, [examStarted, isStudentReady, capturedImage, assessmentId, registrationId, fetchImageAsBase64]);

  // Check if offline PDF URL actually serves a PDF; if not (e.g. redirect to login), show "No PDF available"
  useEffect(() => {
    if (!questionPaperPath && !questionPaperUrl) {
      setPdfUnavailable(false);
      return;
    }
    const url = questionPaperUrl || (questionPaperPath ? `${String(questionPaperPath).replace(/^\//, '')}` : null);
    if (!url) {
      setPdfUnavailable(false);
      return;
    }
    setPdfUnavailable(false);
    let cancelled = false;
    fetch(url, { method: 'GET', redirect: 'follow', credentials: 'same-origin' })
      .then((res) => {
        if (cancelled) return;
        const ct = (res.headers.get('Content-Type') || '').toLowerCase();
        if (!res.ok || (!ct.includes('pdf') && !ct.includes('octet-stream'))) {
          setPdfUnavailable(true);
        }
      })
      .catch(() => {
        if (!cancelled) setPdfUnavailable(true);
      });
    return () => { cancelled = true; };
  }, [questionPaperPath, questionPaperUrl]);

  // --- EXAM MONITORING INTERVAL ---
  useEffect(() => {
    // Run monitoring as soon as exam starts — capturedImage guard is inside runMonitoringCheck itself
    // (it prints a warning and retries on next tick until reference photo is loaded)
    if (!examStarted || !isStudentReady || examFinished || showUploadSection) {
      if (monitoringIntervalRef.current) {
        clearInterval(monitoringIntervalRef.current);
        monitoringIntervalRef.current = null;
      }
      // Clear camera warning when monitoring stops
      setCameraWarning(null);
      return;
    }

    // Log when monitoring starts (helpful for debugging)
    console.log('🎥 Starting exam monitoring. Reference image:', capturedImage ? capturedImage.substring(0, 50) + '...' : 'NOT YET LOADED — will retry each tick');

    const run = () => runMonitoringCheck();
    // The proctor starts the whole room at once: a random first-check offset spreads students across the
    // cycle instead of sending every student's AI request in the same instant (same frequency, no bursts).
    const monitorInterval = ENV.MONITORING_INTERVAL_MS || 5000;
    const startTimer = setTimeout(() => {
      run();
      // Run monitoring periodically; isMonitoringProcessingRef prevents overlapping calls
      monitoringIntervalRef.current = setInterval(run, monitorInterval);
    }, Math.floor(Math.random() * (ENV.MONITORING_START_JITTER_MS || 2000)));
    return () => {
      clearTimeout(startTimer);
      if (monitoringIntervalRef.current) clearInterval(monitoringIntervalRef.current);
      monitoringIntervalRef.current = null;
    };
  }, [examStarted, isStudentReady, examFinished, showUploadSection, capturedImage, runMonitoringCheck]);

  // Keep ref in sync so recording overlay can show current violation on the stored video
  useEffect(() => {
    cameraWarningRef.current = cameraWarning;
  }, [cameraWarning]);

  // --- Refs for Video Recording Hook ---
  const isStudentReadyRef = useRef(isStudentReady);
  useEffect(() => { isStudentReadyRef.current = isStudentReady; }, [isStudentReady]);

  const examFinishedRef = useRef(examFinished);
  useEffect(() => { examFinishedRef.current = examFinished; }, [examFinished]);

  const showUploadSectionRef = useRef(showUploadSection);
  useEffect(() => { showUploadSectionRef.current = showUploadSection; }, [showUploadSection]);

  const localStreamsRef = useRef(localStreams);
  useEffect(() => { localStreamsRef.current = localStreams; }, [localStreams]);

  const getQuestionsTriggeredOnceRef = useRef(getQuestionsTriggeredOnce);
  useEffect(() => { getQuestionsTriggeredOnceRef.current = getQuestionsTriggeredOnce; }, [getQuestionsTriggeredOnce]);

  const currentRoomIdRef = useRef(currentRoomId);
  useEffect(() => { currentRoomIdRef.current = currentRoomId; }, [currentRoomId]);

  const userIdRef = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  const registerNoRef = useRef(registerNo);
  useEffect(() => { registerNoRef.current = registerNo; }, [registerNo]);

  const courseCodeRef = useRef(courseCode);
  useEffect(() => { courseCodeRef.current = courseCode; }, [courseCode]);

  const assessmentIdRefForRec = useRef(assessmentId);
  useEffect(() => { assessmentIdRefForRec.current = assessmentId; }, [assessmentId]);

  // --- VIDEO RECORDING: Exactly 1-Minute Chunks ---
  // Laptop and mobile are separate pages. This effect never reads the other device's connection.
  // A failed MediaRecorder start must keep retrying: clearing the timer before start() used to
  // abandon the laptop recording for the whole exam while violation snapshots (a different pipeline) still uploaded.
  useEffect(() => {
    if (!examStarted) return;

    let initInterval = null;
    let chunkInterval = null;
    let firstChunkTimeout = null;
    let overlayVideo = null;
    let overlayCanvas = null;
    let canvasStream = null;
    let boundTrack = null;
    let selectedMimeType = null;
    let rotating = false;
    let closed = false;
    let tinyChunks = 0;
    let dimensionWaits = 0;
    let waitLogs = 0;
    let lastFailLog = 0;
    let forceVideoOnly = false;
    let lastGoodChunkAt = null;
    let recorderStartedAt = null;
    let wasHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const logRec = (level, stage, detail = {}) => {
      const payload = {
        stage,
        registrationId: registrationIdRef.current || null,
        device: recordingDevice(isMobileDeviceRef.current),
        userId: userIdRef.current || null,
        roomId: currentRoomIdRef.current || null,
        ...detail,
      };
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      fn(`[Recording] ${stage}`, payload);
    };

    const stopRotation = () => {
      if (firstChunkTimeout) { clearTimeout(firstChunkTimeout); firstChunkTimeout = null; }
      if (chunkInterval) { clearInterval(chunkInterval); chunkInterval = null; }
    };

    const stopRecorder = () => {
      const rec = mediaRecorderRef.current;
      mediaRecorderRef.current = null;
      if (rec && rec.state !== 'inactive') {
        try { rec.stop(); } catch (e) { logRec('warn', 'stop-failed', { message: e?.message || String(e) }); }
      }
    };

    const destroyPipeline = () => {
      stopRotation();
      if (cctvOverlayFrameRef.current) {
        cancelAnimationFrame(cctvOverlayFrameRef.current);
        cctvOverlayFrameRef.current = null;
      }
      if (canvasStream) {
        canvasStream.getTracks().forEach((track) => { try { track.stop(); } catch (_) { /* already ended */ } });
        canvasStream = null;
      }
      if (overlayVideo) { overlayVideo.srcObject = null; overlayVideo = null; }
      overlayCanvas = null;
      boundTrack = null;
      selectedMimeType = null;
      dimensionWaits = 0;
      tinyChunks = 0;
      recorderStartedAt = null;
      // Reset stall clock on teardown so a rebuild cannot immediately stall-restart from a stale timestamp.
      lastGoodChunkAt = null;
    };

    const softRestartPipeline = (stage, detail = {}) => {
      logRec('warn', stage, {
        ageSinceGoodChunkMs: lastGoodChunkAt != null ? Date.now() - lastGoodChunkAt : null,
        ...detail,
      });
      stopRecorder();
      destroyPipeline();
    };

    const requestSession = () => {
      if (recordingUploadIdRef.current || recordingStartSentRef.current) return;
      recordingStartSentRef.current = true;
      const device = recordingDevice(isMobileDeviceRef.current);
      (async () => {
        try {
          const currentAssessmentId = assessmentIdRefForRec.current;
          const data = await nodeApi.recordingStart({
            roomId: currentRoomIdRef.current,
            registrationId: registrationIdRef.current,
            assessmentId: currentAssessmentId ?? undefined,
            assessment_id: currentAssessmentId ?? undefined,
            userId: userIdRef.current,
            registerNumber: registerNoRef.current,
            courseCode: courseCodeRef.current || '',
            device,
          }).catch((e) => ({ status: 'error', message: e?.message || String(e) }));
          if (closed) return;
          if (data.status === 'success' && data.uploadId) {
            recordingUploadIdRef.current = data.uploadId;
            recordingFilenameRef.current = data.filename ?? null;
            setIsRecordingActive(true);
          } else {
            logRec('warn', 'session-not-started', { device, message: data?.message || 'no uploadId' });
            recordingStartSentRef.current = false;
          }
        } catch (e) {
          logRec('error', 'session-start-failed', { device, message: e?.message || String(e) });
          recordingStartSentRef.current = false;
        }
      })();
    };

    const startNewRecorder = () => {
      if (!canvasStream) throw new Error('recording pipeline is not ready');
      if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder is not available');
      const previous = mediaRecorderRef.current;
      if (previous && previous.state !== 'inactive') {
        try { previous.stop(); } catch (e) { logRec('warn', 'replace-stop-failed', { message: e?.message || String(e) }); }
      }
      const options = { videoBitsPerSecond: 1000000 };
      if (selectedMimeType) options.mimeType = selectedMimeType;
      const attachHandlers = (recorder) => {
        recorder.ondataavailable = (e) => {
          const size = e.data?.size || 0;
          const progress = recordingBadChunkProgress(size, tinyChunks);
          tinyChunks = progress.consecutiveBad;
          if (size >= RECORDING_GOOD_CHUNK_BYTES) {
            lastGoodChunkAt = Date.now();
            if (uploadVideoChunkRef.current) uploadVideoChunkRef.current(e.data);
            return;
          }
          logRec('warn', size === 0 ? 'empty-chunk' : 'chunk-too-small', { size, consecutive: tinyChunks });
          if (progress.shouldRebuild) {
            setTimeout(() => {
              if (closed) return;
              logRec('error', 'recorder-producing-empty-chunks', { size });
              softRestartPipeline('empty-chunk-rebuild', { size });
            }, 0);
          }
        };
        recorder.onerror = (ev) => {
          const err = ev?.error || ev;
          logRec('error', 'media-recorder-error', { name: err?.name, message: err?.message || String(err) });
          if (mediaRecorderRef.current === recorder) mediaRecorderRef.current = null;
          // Rebuild only when this recorder is still the current one. A replacement is left alone.
          setTimeout(() => {
            if (closed) return;
            const current = mediaRecorderRef.current;
            if (current && current !== recorder && current.state !== 'inactive') return;
            destroyPipeline();
          }, 0);
        };
        recorder.onstop = () => logRec('info', 'chunk-stopped', {});
        return recorder;
      };
      // Firefox throws on start() when vp8/vp9 is paired with a mic track. Drop audio and start video-only.
      const created = startRecorderOrVideoOnly({
        stream: canvasStream,
        onAudioDropped: (error) => {
          forceVideoOnly = true;
          logRec('warn', 'audio-dropped', { name: error?.name, message: error?.message || String(error), mimeType: selectedMimeType || 'default' });
        },
        create: () => {
          const recorder = attachHandlers(new MediaRecorder(canvasStream, options));
          recorder.start();
          return recorder;
        },
      });
      mediaRecorderRef.current = created.recorder;
      recorderStartedAt = Date.now();
    };

    const scheduleRotation = () => {
      if (chunkInterval || firstChunkTimeout) return;
      const rotate = () => {
        rotating = true;
        try {
          const rec = mediaRecorderRef.current;
          if (rec && rec.state !== 'inactive') {
            try { rec.stop(); } catch (e) { logRec('warn', 'rotate-stop-failed', { message: e?.message || String(e) }); }
          }
          startNewRecorder();
        } catch (e) {
          mediaRecorderRef.current = null;
          logRec('error', 'rotate-failed', { name: e?.name, message: e?.message || String(e) });
        } finally {
          rotating = false;
        }
      };
      const firstRotationMs = VIDEO_RECORD_CHUNK_MS - Math.floor(Math.random() * VIDEO_RECORD_CHUNK_STAGGER_MS);
      firstChunkTimeout = setTimeout(() => {
        firstChunkTimeout = null;
        if (closed) return;
        rotate();
        chunkInterval = setInterval(rotate, VIDEO_RECORD_CHUNK_MS);
      }, firstRotationMs);
    };

    const buildPipeline = (stream, videoTrack) => {
      overlayVideo = document.createElement('video');
      overlayVideo.muted = true;
      overlayVideo.playsInline = true;
      overlayVideo.autoplay = true;
      overlayVideo.srcObject = stream;
      overlayVideo.play().catch((e) => logRec('warn', 'overlay-play-failed', { message: e?.message || String(e) }));

      overlayCanvas = document.createElement('canvas');
      const ctx = overlayCanvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2d context unavailable');
      const targetFps = RECORDING_OVERLAY_FPS;
      const minFrameGapMs = 1000 / targetFps - 2;
      let lastDrawAt = 0;

      const resizeCanvas = () => {
        if (overlayVideo.videoWidth && overlayVideo.videoHeight) {
          const targetHeight = 720;
          const aspectRatio = overlayVideo.videoWidth / overlayVideo.videoHeight;
          overlayCanvas.width = Math.round(targetHeight * aspectRatio);
          overlayCanvas.height = targetHeight;
        }
      };
      overlayVideo.onloadedmetadata = resizeCanvas;
      overlayVideo.onresize = resizeCanvas;
      if (overlayVideo.videoWidth) resizeCanvas();

      const formatCctvTime = () => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };

      const drawOverlay = (now) => {
        if (closed) return;
        if (now - lastDrawAt < minFrameGapMs) {
          cctvOverlayFrameRef.current = requestAnimationFrame(drawOverlay);
          return;
        }
        lastDrawAt = now;
        if (overlayVideo && overlayVideo.readyState >= 2 && overlayCanvas.width && overlayCanvas.height) {
          const cw = overlayCanvas.width;
          const ch = overlayCanvas.height;
          const vw = overlayVideo.videoWidth;
          const vh = overlayVideo.videoHeight;
          if (vw && vh) {
            const scale = Math.max(cw / vw, ch / vh);
            const srcW = cw / scale;
            const srcH = ch / scale;
            const srcX = (vw - srcW) / 2;
            const srcY = (vh - srcH) / 2;
            ctx.drawImage(overlayVideo, srcX, srcY, srcW, srcH, 0, 0, cw, ch);
          }

          const fontSize = Math.max(14, Math.floor(cw / 25));
          const violationText = cameraWarningRef.current;
          let yTime = ch - 8;

          if (violationText) {
            const label = 'VIOLATION: ';
            const fullText = label + violationText;
            const vFontSize = Math.max(12, Math.floor(cw / 28));
            const vPadding = 6;
            const vHeight = vFontSize + vPadding * 2;
            ctx.font = `bold ${vFontSize}px monospace`;
            ctx.fillStyle = 'rgba(180, 0, 0, 0.85)';
            ctx.fillRect(0, ch - vHeight, cw, vHeight);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 2;
            ctx.strokeRect(0, ch - vHeight, cw, vHeight);
            ctx.fillStyle = '#ffffff';
            ctx.fillText(fullText, vPadding, ch - vPadding);
            yTime = ch - vHeight - 8;
          }

          const text = formatCctvTime();
          ctx.font = `bold ${fontSize}px monospace`;
          const metrics = ctx.measureText(text);
          const padding = 8;
          const x = cw - metrics.width - padding - 4;
          ctx.fillStyle = '#00ff00';
          ctx.fillText(text, x, yTime);
        }
        cctvOverlayFrameRef.current = requestAnimationFrame(drawOverlay);
      };
      cctvOverlayFrameRef.current = requestAnimationFrame(drawOverlay);

      canvasStream = overlayCanvas.captureStream(targetFps);
      if (!forceVideoOnly) {
        const audio = attachLiveAudio(canvasStream, stream);
        if (audio.attached) logRec('info', 'audio-attached', {});
        else if (audio.error) logRec('warn', 'audio-skipped', { message: audio.error.message || String(audio.error) });
      }

      const preferredMimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4;codecs=avc1',
        'video/mp4',
      ];
      selectedMimeType = typeof MediaRecorder !== 'undefined'
        ? preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type))
        : null;
      boundTrack = videoTrack;
    };

    const failAndRetry = (stage, error) => {
      stopRecorder();
      destroyPipeline();
      const now = Date.now();
      if (now - lastFailLog > 10000) {
        lastFailLog = now;
        logRec('error', stage, { name: error?.name, message: error?.message || String(error) });
      }
    };

    const tryStartRecording = () => {
      if (closed || rotating) return;
      if (!registrationIdRef.current) {
        if (waitLogs++ % 15 === 0) logRec('warn', 'waiting-for-registration', {});
        return;
      }
      requestSession();

      const stream = cameraStreamFromList(localStreamsRef.current);
      const videoTrack = liveVideoTrack(stream);
      const now = Date.now();
      const decision = recordingStartDecision({
        recorderState: mediaRecorderRef.current?.state || null,
        hasRegistration: true,
        hasLiveVideo: !!videoTrack,
        sourceTrackLive: !boundTrack || boundTrack.readyState === 'live',
        lastGoodChunkAt,
        recorderStartedAt,
        now,
        stallMs: RECORDING_STALL_MS,
      });

      if (decision === 'keep') return;
      if (decision === 'wait' || decision === 'stop-and-wait') {
        if (decision === 'stop-and-wait') {
          logRec('warn', 'camera-ended', {});
          stopRecorder();
          destroyPipeline();
          setIsRecordingActive(false);
        } else if (waitLogs++ % 15 === 0) {
          logRec('warn', 'waiting-for-camera', { hasVideoTrack: !!videoTrack });
        }
        return;
      }
      if (decision === 'restart') {
        const stalled = recordingChunkHealth({
          lastGoodChunkAt, recorderStartedAt, now, stallMs: RECORDING_STALL_MS,
        }) === 'stalled';
        softRestartPipeline(stalled ? 'stall-restart' : 'rebind-camera');
      }

      try {
        if (!canvasStream) buildPipeline(stream, videoTrack);
        if (overlayVideo && !overlayVideo.videoWidth && dimensionWaits < 5) {
          dimensionWaits += 1;
          logRec('info', 'waiting-for-frames', { attempt: dimensionWaits });
          return;
        }
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
          startNewRecorder();
          scheduleRotation();
          setIsRecordingActive(true);
          logRec('info', 'started', { mimeType: selectedMimeType || 'default' });
        }
      } catch (e) {
        failAndRetry('start-failed', e);
      }
    };

    const handleRecordingVisibility = () => {
      if (closed || typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        wasHidden = true;
        return;
      }
      if (!wasHidden) return;
      wasHidden = false;

      const finishVisibleRecovery = (overlayPlayFailed) => {
        if (closed) return;
        if (!shouldRestartRecordingAfterVisible({
          lastGoodChunkAt,
          now: Date.now(),
          overlayPlayFailed,
        })) return;
        softRestartPipeline('visibility-recording-restart', { overlayPlayFailed: !!overlayPlayFailed });
      };

      if (!overlayVideo) {
        finishVisibleRecovery(false);
        return;
      }
      // Kick the off-DOM overlay; soft-restart only when chunks look stale or play fails.
      overlayVideo.play().then(() => {
        finishVisibleRecovery(false);
      }).catch((e) => {
        logRec('warn', 'overlay-play-failed', { message: e?.message || String(e) });
        finishVisibleRecovery(true);
      });
    };

    initInterval = setInterval(tryStartRecording, 1000);
    tryStartRecording();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleRecordingVisibility);
    }

    return () => {
      closed = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleRecordingVisibility);
      }
      if (initInterval) clearInterval(initInterval);
      stopRecorder();
      destroyPipeline();
      setIsRecordingActive(false);
    };
  }, [examStarted]); // Retry stays inside the effect. examStarted is the only dependency so a phone event cannot restart it.

  const registrationIdRef = useRef(registrationId);
  useEffect(() => {
    registrationIdRef.current = registrationId;
  }, [registrationId]);

  // On refresh/close: stop recorder and call /api/recording/finalize so server can upload to S3
  const assessmentIdRefForBeacon = useRef(assessmentId);
  useEffect(() => {
    assessmentIdRefForBeacon.current = assessmentId;
  }, [assessmentId]);
  useEffect(() => {
    const sendFinalizeBeacon = () => {
      const rid = registrationIdRef.current;
      if (!rid) return;
      const url = nodeApi.recordingFinalizeUrl();
      const aid = assessmentIdRefForBeacon.current ?? recordingStartPayloadRef.current?.assessmentId ?? recordingStartPayloadRef.current?.assessment_id;
      const fname = recordingFilenameRef.current;
      const body = JSON.stringify({
        registrationId: rid,
        registration_id: rid,
        assessmentId: aid ?? undefined,
        assessment_id: aid ?? undefined,
        filename: fname ?? undefined,
        device: recordingDevice(isMobileDeviceRef.current),
      });
      navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
    };
    const handleBeforeUnload = () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        recordingFinalizingRef.current = true;
        try { mediaRecorderRef.current.stop(); } catch (e) {
          console.warn('[Recording] stop on unload failed', { stage: 'unload', registrationId: registrationIdRef.current, message: e?.message || String(e) });
        }
      }
      sendFinalizeBeacon();
    };
    const handlePageHide = () => {
      sendFinalizeBeacon();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendFinalizeBeacon();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // ---------------- RENDER UI ----------------
  return (
    <div className="exam-container">
      {mediaConnectivityError && (
        <div role="alert" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100000, padding: '12px 16px',
          background: '#b71c1c', color: '#fff', fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
        }}>
          <span>{mediaConnectivityError}</span>
          <button type="button" onClick={() => window.location.reload()} style={{ padding: '6px 14px', fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}
      {/* 🔹 ADD THIS: Invisible Audio Element to play Proctor's voice */}
      <audio
        ref={proctorAudioRef}
        autoPlay
        playsInline
        // If proctor disabled their mic via socket state, mute locally too
        muted={!proctorAudioEnabled}
      />

      {toast && (
        <div className={`custom-toast ${toast.type}`}>
          <div className="toast-icon">
            {toast.type === 'success' && <FaCheckCircle />}
            {toast.type === 'error' && <FaTimes />}
            {toast.type === 'warning' && <FaExclamationTriangle />}
            {toast.type === 'info' && <FaInfoCircle />}
          </div>
          <div className="toast-message">{toast.message}</div>
        </div>
      )}

      {confirmModal && (
        <div className="modal-backdrop">
          <div className="custom-modal">
            <div className="modal-header">
              <FaExclamationCircle size={30} color={confirmModal.titleColor || "#667eea"} />
              <h3>{confirmModal.title || "Confirmation Required"}</h3>
            </div>
            <div className="modal-body">
              <p>{confirmModal.message}</p>
            </div>
            <div className="modal-footer">
              {!confirmModal.hideCancel && (
                <button className="btn-cancel" onClick={() => (confirmModal.onCancel ? confirmModal.onCancel() : setConfirmModal(null))}>
                  {confirmModal.cancelText || "Cancel"}
                </button>
              )}
              <button className="btn-confirm" onClick={confirmModal.onConfirm}>
                {confirmModal.confirmText || "Confirm & Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isOverlayVisible && !examFinished && !showUploadSection && (
        <div className="system-check-overlay">
          <div className="check-card">
            <h2>{examStarted && !isMobileDevice ? (!isFullscreenActive ? "Resume Fullscreen Mode" : "Resume Examination") : (isMobileDevice && capturedImage && !isIdentityVerified ? "Verification Screen" : (examStarted ? "Resume Examination" : "Verification Screen"))}</h2>
            <p>
              {isMobileDevice && capturedImage && !isIdentityVerified
                ? "Capture your face again below to compare with your existing photo and continue to the exam."
                : examStarted && !isFullscreenActive && !isMobileDevice
                  ? "Fullscreen mode is required. Click RETURN TO FULLSCREEN (or press F11) to continue."
                  : examStarted && !isScreenSharing && !isMobileDevice
                    ? "Screen Share Lost. You must share your screen to continue."
                    : capturedImage && !isHumanVerified
                      ? "Reference image found. AI liveness check is optional. You may proceed."
                      : !isHumanVerified
                        ? "Please complete the following checks to proceed."
                        : "Verification Successful."}
            </p>
            <div className="examflex">
              <div className={`check-preview-area ${isHumanVerified ? 'verified' : ''}`} style={{ position: 'relative' }}>
                {/* On mobile when waiting for identity verify: show reference image above camera, then live feed */}
                {(isMobileDeviceFallback && capturedImage && !isIdentityVerified && localStreams.length > 0) ? (
                  <>
                    <div className="mobile-reference-photo-box">
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#2b6cb0', marginBottom: '8px' }}>Your stored photo (compare below)</div>
                      <img src={capturedImage} alt="Reference" />
                    </div>
                    <div className="mobile-video-wrapper" style={{ position: 'relative', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
                      <video ref={checkPreviewRef} autoPlay playsInline muted className="check-video-feed" />
                      <button
                        className="camera-switch-btn-overlay"
                        onClick={switchLivenessCamera}
                        title={`Switch to ${livenessCameraIndex === 0 ? 'Rear' : 'Front'} Camera`}
                        style={{
                          position: 'absolute', top: '10px', right: '10px', zIndex: 1000,
                          backgroundColor: 'rgba(43, 108, 176, 0.9)', color: 'white', border: '3px solid white',
                          borderRadius: '50%', width: '50px', height: '50px', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '20px',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.7)', transition: 'all 0.3s ease', fontWeight: 'bold'
                        }}
                      >
                        <FaSync />
                      </button>
                    </div>
                  </>
                ) : capturedImage && !(isMobileDeviceFallback && !isIdentityVerified) ? (
                  <img src={capturedImage} alt="Captured" className="check-video-feed" />
                ) : localStreams.length > 0 ? (
                  <>
                    <video ref={checkPreviewRef} autoPlay playsInline muted className="check-video-feed" />
                    <div className="ai-scanning-overlay">
                      {isScanning && <FaSpinner className="spin-icon ai-icon" />}
                      {isHumanVerified && <FaCheckCircle className="ai-icon success" />}
                      <p className="ai-text large">{livenessMessage}</p>
                      {isMobileDeviceFallback && !isHumanVerified && (
                        <span style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                          Camera: {livenessCameraIndex === 0 ? 'Front' : 'Rear'}
                        </span>
                      )}
                    </div>
                    {/* Floating Camera Switch Button for Liveness - Mobile Only - Always show on mobile for front/rear switching */}
                    {isMobileDeviceFallback && !isHumanVerified && (
                      <button
                        className="camera-switch-btn-overlay"
                        onClick={switchLivenessCamera}
                        title={`Switch to ${livenessCameraIndex === 0 ? 'Rear' : 'Front'} Camera`}
                        style={{
                          position: 'absolute',
                          top: '15px',
                          right: '15px',
                          zIndex: 1000,
                          backgroundColor: 'rgba(43, 108, 176, 0.9)',
                          color: 'white',
                          border: '3px solid white',
                          borderRadius: '50%',
                          width: '60px',
                          height: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '24px',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.7)',
                          transition: 'all 0.3s ease',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(43, 108, 176, 1)';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(43, 108, 176, 0.9)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FaSync />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="check-placeholder">
                    <FaCamera size={40} />
                    <span>{!isSystemActivated ? "Click 'Activate Camera & Screen' below" : "Waiting for Camera..."}</span>
                  </div>
                )}

                {/* Retake / Try again: show when not satisfied (during scan or after captured). Hidden on mobile. Max 3 attempts. Expires in 10 minutes. */}
                {!isUploading && !isMobileDeviceFallback && retakeAttempts < 3 && !retakeExpired && (capturedImage || (localStreams.length > 0 && !isHumanVerified)) && (
                  <button className="retake-btn-overlay" onClick={handleRetake}>
                    <FaRedo /> Retake {retakeAttempts > 0 ? `(${3 - retakeAttempts} left)` : ''}
                  </button>
                )}

              </div>


              {showScreenShareButton && (
                <button
                  className="force-start-btn"
                  onClick={async () => {
                    try {
                      await startScreenShare();
                      // Request fullscreen AFTER screen share stream is acquired, 
                      // or immediately after the user interacts with this button.
                      await requestExamFullscreen(true);
                    } catch (e) {
                      console.error("Manual screen share failed:", e);
                    }
                  }}
                >
                  START FULL SCREEN SHARE
                </button>
              )}

              {!isSystemActivated ? (
                <button
                  className="force-start-btn"
                  onClick={() => {
                    setIsSystemActivated(true);
                    handleActivateSystem();
                  }}
                  disabled={isConfiguring}
                >
                  {isConfiguring ? <FaSpinner className="spin-icon spin-anim" /> : "ACTIVATE CAMERA & SCREEN"}
                </button>
              ) : localStreams.length < REQUIRED_CAMERAS || (!isScreenSharing && !isMobileDevice) ? (
                <span></span>
              ) : (
                (isMobileDevice && capturedImage && !isIdentityVerified) ? (
                  <button
                    className="force-start-btn success"
                    disabled={identityCheckStatus === 'checking'}
                    onClick={async () => {
                      // Ensure mobile users also enter fullscreen when they click Verify
                      await requestExamFullscreen(true);
                      runMonitoringCheck();
                    }}
                  >
                    {identityCheckStatus === 'checking'
                      ? <><FaSpinner className="spin-icon" /> Verifying Identity...</>
                      : "CAPTURE & VERIFY IDENTITY"}
                  </button>
                ) : (
                  <button
                    className={`force-start-btn ${capturedImage &&
                      localStreams.length >= REQUIRED_CAMERAS &&
                      (isScreenSharing || isMobileDevice) &&
                      (!isMobileDevice || isIdentityVerified)
                      ? 'success' : 'disabled'
                      }`}
                    disabled={
                      !capturedImage ||
                      localStreams.length < REQUIRED_CAMERAS ||
                      isUploading ||
                      (!isScreenSharing && !isMobileDevice) ||
                      // On mobile: block access until identity check completes successfully
                      // 'no_reference' is treated as verified (first login, no reference to compare)
                      (isMobileDevice && !isIdentityVerified)
                    }
                    onClick={async () => {
                      // Failsafe fullscreen request before the exam actually triggers
                      await requestExamFullscreen(true);
                      handleConfirmAndStart();
                    }}
                  >
                    {isUploading
                      ? <FaSpinner className="spin-icon" />
                      : identityCheckStatus === 'checking'
                        ? <><FaSpinner className="spin-icon" /> Verifying Identity...</>
                        : (examStarted ? (!isFullscreenActive ? "RETURN TO FULLSCREEN" : "RESUME EXAM") : "ENTER EXAM HALL")}
                  </button>
                )
              )}



              <div className="status-list">
                <div className="check-item ready">
                  <FaCheckCircle /> Ensure you are using the latest updated version of the browser before starting the exam.
                </div>
                <div className={`check-item ${localStreams.length > 0 ? 'ready' : 'pending'}`}>
                  {localStreams.length > 0 ? <FaCheckCircle /> : <FaExclamationCircle />} Camera and microphone permissions/access must be allowed in your browser usually seen during connection.
                </div>
                <div className={`check-item ${localStreams.length >= REQUIRED_CAMERAS ? 'ready' : 'pending'}`}>
                  {localStreams.length >= REQUIRED_CAMERAS ? <FaCheckCircle /> : <FaCamera />} At least one camera is mandatory, and it should be properly focused on your face.
                </div>
                <div className={`check-item ${isScreenSharing || isMobileDevice ? 'ready' : 'pending'}`}>
                  {isScreenSharing || isMobileDevice ? <FaCheckCircle /> : <FaExclamationTriangle />}
                  You must share your entire screen when prompted by the system.
                </div>
                <div className="check-item ready">
                  <FaCheckCircle /> Sufficient Internet bandwidth is always required on both devices.
                </div>
                <div className={`check-item ${isFullscreenActive || isMobileDevice ? 'ready' : 'pending'}`}>
                  {isFullscreenActive || isMobileDevice ? <FaCheckCircle /> : <FaDesktop />}
                  The exam must be taken in Full Screen Mode. Do not exit full screen.
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ---------------- LEFT SIDEBAR (Monitoring) ---------------- */}
      <aside className="exam-sidebar left">
        <div className="sidebar-room-top" title="Room ID" style={{ padding: '8px 10px', background: 'rgba(43, 108, 176, 0.15)', borderRadius: '6px', marginBottom: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
          <span className="sidebar-room-label" style={{ fontWeight: 600 }}>Room:</span>{' '}
          <span className="sidebar-room-value">{roomDisplay}</span>
        </div>
        <div className="proctor-flex">
          <div>
            <div className="section-label">Proctor View</div>
            <div className="proctor-cam-preview">
              {proctorVideo && proctorVideoEnabled ? (
                <video
                  key={proctorVideo.id}
                  ref={proctorVideoRef}
                  autoPlay playsInline
                  muted
                  className="proctor-video"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (proctorVideo && !proctorVideoEnabled) || (roomUsers.some(u => u.userType === 'staff') && !proctorVideo) ? (
                <div className="proctor-placeholder"><FaChalkboardTeacher size={30} /><p>Proctor not on camera</p></div>
              ) : (
                <div className="proctor-placeholder"><FaChalkboardTeacher size={30} /><p>Proctor Offline</p></div>
              )}
            </div>
          </div>
          <div className="proctor-flexl">
            <div className="section-label">Your Cameras</div>
            <div className={`student-cam-preview ${cameraWarning ? 'has-violation' : ''}`}>
              <video ref={localVideoRef1} autoPlay playsInline muted className="video-live" />
              <div className="cam-overlay"><span className="cam-status"><span className="status-live">● CAM 1</span></span></div>
              {cameraWarning && (
                <div className="camera-warning-bar">
                  <FaExclamationTriangle className="warning-icon" />
                  <span className="warning-text">{cameraWarning}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {localStreams.length > 1 && (
          <div className="student-cam-preview">
            <video ref={localVideoRef2} autoPlay playsInline muted className="video-live" />
            <div className="cam-overlay"><span className="cam-status"><span className="status-live">● CAM 2</span></span></div>
          </div>
        )}

        <div className="sidebar-stats">
          <p><FaDesktop /> Screen: {isMobileDevice ? "N/A (Mobile)" : (isScreenSharing ? "Shared" : "Not Shared")}</p>
        </div>
      </aside>

      {/* --- Time Progress Alert Modal --- */}
      {inlineAlertMessage && (
        <div className="inline-progress-backdrop">
          <div className="inline-progress-modal">
            <div className="modal-icon-container">
              <FaExclamationTriangle className="modal-alert-icon pulse-animation" />
            </div>
            <h3 className="modal-title">Time Alert</h3>
            <p className="modal-message text-red-600 font-bold">{inlineAlertMessage}</p>
            <button className="modal-ok-btn" onClick={() => setInlineAlertMessage(null)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* ---------------- MIDDLE (Exam Paper) ---------------- */}
      <main className="exam-main">

        {proctorScreen && (
          <div className="proctor-screen-container">
            <div className="screen-header"><FaDesktop /> Proctor is presenting</div>
            <video ref={proctorScreenRef} autoPlay playsInline muted className="proctor-screen-video" />
          </div>
        )}

        <header className="exam-top-bar">
          <div className="room-info" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'black' }}>Register No. {registerNo}</h2>
            {privateCallWithProctor && (
              <div
                className="private-call-badge"
                title="Private Audio Session with Proctor"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: 'rgba(56, 161, 105, 0.15)',
                  border: '1px solid rgba(56, 161, 105, 0.3)',
                  padding: !isMobileDevice ? '5px 15px 5px 5px' : '5px',
                  borderRadius: '30px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div
                  className="private-call-icon-container"
                  style={{
                    backgroundColor: '#38a169',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    flexShrink: 0
                  }}
                >
                  <FaPhone className="private-call-pulse-anim" />
                  <style>{`
                    .private-call-pulse-anim {
                      animation: privateCallPulse 1.5s infinite;
                    }
                    @keyframes privateCallPulse {
                      0% { transform: scale(1); }
                      50% { transform: scale(1.2); }
                      100% { transform: scale(1); }
                    }
                  `}</style>
                </div>
                {!isMobileDevice && (
                  <span style={{
                    color: '#2f855a',
                    fontWeight: '600',
                    fontSize: '13px',
                    letterSpacing: '0.3px',
                    paddingRight: '5px'
                  }}>
                    Proctor has started private communication
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="top-right-section">
            <ExamTimerDisplay examStarted={examStarted} examFinished={examFinished} examEndTime={examEndTime} serverOffset={serverOffset} examStopped={isExamBlockedByInvigilator} />
            {isRecordingActive && (
              <div className="recording-indicator" title="Video is being recorded and saved locally">
                <span className="recording-dot" /> Recording
              </div>
            )}
            <button
              type="button"
              className="exam-logout-btn"
              onClick={() => {
                phpApi.logExamActivity({
                  registration_id: registrationId,
                  event: 'PROCTORING_LOGOUT',
                  device: isMobileDevice ? 'mobile' : 'laptop',
                }).catch(() => {});
                window.location.href = `${BASE_URL}`;
              }}
              title="Logout"
            >
              Logout
            </button>
          </div>
        </header>

        <div className="exam-paper-container">
          {/* EXAM STOPPED BY INVIGILATOR SCREEN - hides questions/upload, shows clear notice */}
          {isExamBlockedByInvigilator && !examFinished && (
            <div className="exam-stopped-screen-container">
              <div className="exam-stopped-card">
                <div className="exam-stopped-icon-circle">
                  <FaStopCircle size={56} color="#e53e3e" />
                </div>
                <h2 className="exam-stopped-title">
                  The examination has been stopped by the Invigilator
                </h2>
                <div className="exam-stopped-badge">
                  Proctor Stopped the Examination
                </div>
                <div className="exam-stopped-info-box">
                  <p className="exam-stopped-message">
                    The Question Paper and Answer Submission have been paused.
                  </p>
                  <p className="exam-stopped-sub-message">
                    Please remain on this screen. Your camera and proctor communication remain active. Await further instructions or announcements from the Invigilator.
                  </p>
                </div>
                <div className="exam-stopped-status-badge">
                  <span className="exam-stopped-pulse-dot" />
                  <span>Proctor Monitoring Active</span>
                </div>
              </div>
            </div>
          )}
          {/* Waiting / Upload / Finish States */}
          {/* ═══════════════════════════════════════════════════════════════════
               UNIFIED WAITING SCREEN
               Shows in ALL 3 states:
                 1. Before exam starts  → "Waiting for Proctor..."
                 2. Exam started, missing the other camera → "Cameras are connecting..."
                 3. Both cameras connecting/verifying
               Automatically disappears when both cameras are streaming.
          ═══════════════════════════════════════════════════════════════════ */}
          {(() => {
            // --- Camera connection logic ---

            const { mobilePeer, laptopConnected, mobileConnected, laptopAway, mobileAway, bothReady } = deviceStatus;

            // 🔹 FIX (Issue 2): When exam is running, always show waiting if a camera is
            // missing — regardless of isStudentReady — so mobile detects laptop logout
            // in real-time without requiring a refresh.
            // earlyReveal hides the waiting screen 1 min before start — desktop only.
            // Mobile always waits for the official examStarted flag.
            const earlyReveal = isOneMinuteToStart && !isMobileDevice;
            if (isExamBlockedByInvigilator) return null;
            const showWaiting = !showUploadSection && !examFinished &&
              ((!examStarted && !earlyReveal && isStudentReady) || ((examStarted || earlyReveal) && !bothReady && !relaxDeviceCheck));

            if (!showWaiting) return null;

            // Determine headline
            let headline = 'Waiting for Proctor...';
            let headlineIcon = <FaHourglassHalf size={60} color="#2b6cb0" className="spin-icon" />;
            let subMessage = 'The examination has not started yet or has been stopped by the invigilator. Please stay on this page.';

            const hadBothDevices = typeof sessionStorage !== 'undefined' && currentRoomId && registrationId &&
              Boolean(sessionStorage.getItem(`bothDevicesSeen_${currentRoomId}_${registrationId}`));

            if (examStarted) {
              if (!laptopConnected && !mobileConnected) {
                headline = 'Connecting Cameras...';
                headlineIcon = <FaCamera size={60} color="#e53e3e" className="spin-icon" style={{ animationDuration: '3s' }} />;
                subMessage = 'Both cameras must be streaming before the exam can begin.';
              } else if (!mobileConnected) {
                const isMobileDropped = mobileAway || hadBothDevices;
                headline = isMobileDropped ? 'Exam In Progress — Mobile Disconnected' : 'Exam In Progress — Waiting for Mobile Camera...';
                headlineIcon = isMobileDropped ? <FaExclamationTriangle size={60} color="#e53e3e" /> : <FaCamera size={60} color="#e53e3e" className="spin-icon" style={{ animationDuration: '3s' }} />;
                subMessage = isMobileDropped
                  ? 'Your mobile phone is disconnected or its camera stopped. Please open the exam page on your mobile to resume.'
                  : 'Please log in from your mobile device to enable the secondary camera.';
              } else if (!laptopConnected) {
                const isLaptopDropped = laptopAway || hadBothDevices;
                headline = isLaptopDropped ? 'Exam In Progress — Laptop Disconnected' : 'Exam In Progress — Waiting for Laptop Camera...';
                headlineIcon = isLaptopDropped ? <FaExclamationTriangle size={60} color="#e53e3e" /> : <FaCamera size={60} color="#e53e3e" className="spin-icon" style={{ animationDuration: '3s' }} />;
                subMessage = isLaptopDropped
                  ? 'Your laptop/computer is disconnected. Return to the exam page on your laptop to continue.'
                  : 'Please log in from your laptop or desktop computer to enable the primary camera.';
              }
            }

            let awayNote = null;
            if (mobileAway || (!mobileConnected && hadBothDevices)) {
              awayNote = 'Your mobile phone is disconnected: the exam tab on your mobile was closed, switched to another tab/app, the screen was locked, or the camera stopped. Open the exam page on your mobile again to continue.';
            } else if (laptopAway || (!laptopConnected && hadBothDevices)) {
              awayNote = 'Your laptop/computer is disconnected: the exam tab on your laptop was closed or switched to another tab. Return to the exam page on your laptop to continue.';
            }

            return (
              <div className="waiting-room-screen" style={{ textAlign: 'center', marginTop: '50px' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>{headlineIcon}</div>
                <h2 style={{ color: '#2d3748', margin: '20px 0 10px 0' }}>{headline}</h2>

                {/* ── Live Camera Status Badges ── */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', margin: '20px 0', flexWrap: 'wrap' }}>
                  {/* Laptop status */}
                  <div style={{
                    padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: 'bold',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    backgroundColor: laptopConnected ? '#e6fffa' : '#fefcbf',
                    color: laptopConnected ? '#2c7a7b' : '#975a16',
                    border: `1px solid ${laptopConnected ? '#319795' : '#d69e2e'}`,
                    transition: 'all 0.5s ease'
                  }}>
                    <FaDesktop />
                    Computer / Laptop
                    <span style={{ marginLeft: '4px' }}>
                      {laptopConnected ? '✓ Connected' : ((laptopAway || hadBothDevices) ? 'Disconnected' : 'Not Connected Yet...')}
                    </span>
                  </div>

                  {/* Mobile status */}
                  <div style={{
                    padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: 'bold',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    backgroundColor: mobileConnected ? '#e6fffa' : '#fefcbf',
                    color: mobileConnected ? '#2c7a7b' : '#975a16',
                    border: `1px solid ${mobileConnected ? '#319795' : '#d69e2e'}`,
                    transition: 'all 0.5s ease'
                  }}>
                    <FaMobileAlt />
                    Mobile
                    <span style={{ marginLeft: '4px' }}>
                      {mobileConnected ? '✓ Connected' : ((mobileAway || hadBothDevices) ? 'Disconnected' : (mobilePeer ? 'Verification In Progress...' : 'Not Connected Yet...'))}
                    </span>
                  </div>
                </div>

                <div style={{ color: '#2b6cb0', fontWeight: 'bold', fontSize: '18px', margin: '15px 0' }}>
                  Exam Scheduled Time: {examScheduledStart || '10-03-2026 | 3:50 PM'}
                </div>

                <div style={{ maxWidth: '600px', margin: '20px auto', textAlign: 'center', color: '#4a5568' }}>
                  {deviceLogoutDeadline && (
                    <DeviceLogoutCountdown deadline={deviceLogoutDeadline} deviceLabel={isMobileDevice ? 'laptop / computer' : 'mobile phone'} />
                  )}
                  {awayNote && (
                    <p style={{ fontWeight: 'bold', color: '#c53030', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                      <FaExclamationTriangle style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      {awayNote}
                    </p>
                  )}
                  <p style={{ marginBottom: '10px', fontWeight: '500' }}>
                    Both connections must be successful before the exam begins.
                  </p>
                  <p style={{ fontWeight: '500', color: '#2d3748', marginBottom: '10px' }}>
                    Sufficient Internet bandwidth is always required on both devices.
                  </p>
                  <p style={{ fontStyle: 'italic', marginBottom: '10px' }}>
                    The Question Paper will be displayed based on the exam start time and once both devices are connected successfully.
                  </p>
                  <p style={{ fontWeight: 'bold', color: '#e53e3e' }}>
                    Please do not leave this page until the exam begins.
                  </p>
                </div>

                {/* Important Instructions Block */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '700px',
                  margin: '30px auto',
                  textAlign: 'left',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ color: '#1a202c', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaInfoCircle color="#3182ce" /> Important Instructions
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#4a5568', lineHeight: '1.7' }}>
                    <li style={{ marginBottom: '8px' }}>Both cameras, screen, and voice will be monitored throughout the entire exam duration.</li>
                    <li style={{ marginBottom: '8px' }}>Ensure your web camera and mobile camera are properly connected and focused towards you until you start scanning answer scripts.</li>
                    <li style={{ marginBottom: '8px' }}>Keep your face clearly visible to the camera at all times.</li>
                    <li style={{ marginBottom: '8px' }}>On your mobile device, set the Auto-Lock option to “Never” during the exam to avoid camera disconnection.</li>
                    <li style={{ marginBottom: '8px' }}>Do not close the exam tab or switch to another tab/app on your laptop or mobile. If you do, that device is shown as disconnected on your other device and the question paper is hidden until you return to the exam page.</li>
                    <li style={{ marginBottom: '8px' }}>Follow all invigilator/proctor instructions during the exam.</li>
                    <li style={{ marginTop: '12px', fontWeight: '600', listStyle: 'none', paddingLeft: '0', display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#2d3748' }}>
                      <FaMicrophone size={14} style={{ marginTop: '4px' }} />
                      <span>Allow microphone permission to continue the exam. You can communicate with the Invigilator using the Mic (Voice Chat) or the Chat Box if you face any issues.</span>
                    </li>
                  </ul>
                </div>
              </div>
            );
          })()}

          {examStarted && showUploadSection && !examFinished && !isExamBlockedByInvigilator && (
            <div className="upload-section-container">
              {deviceLogoutDeadline && (
                <DeviceLogoutCountdown deadline={deviceLogoutDeadline} deviceLabel={isMobileDevice ? 'laptop / computer' : 'mobile phone'} />
              )}
              {isMobileDevice && !deviceStatus.laptopConnected && !relaxDeviceCheck && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', textAlign: 'left', fontWeight: 'bold', color: '#c53030', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                  <FaDesktop style={{ marginTop: '3px', flexShrink: 0 }} />
                  <span>
                    {deviceStatus.laptopAway
                      ? 'Computer / Laptop Disconnected: the exam tab on your laptop was closed or switched to another tab. Return to the exam page on your laptop.'
                      : 'Computer / Laptop Not Connected: open the exam page on your laptop or computer and keep it on screen.'}
                  </span>
                </div>
              )}
              {/* <div className="upload-icon-wrapper">
                <FaFilePdf size={60} />
              </div> */}
              <h2>Upload Answer Sheet</h2>
              <p>Take photos of your answer sheet to upload</p>
              <div className="file-upload-zone upload-options-row">
                {/* <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleFileSelect(e, 'pdf')}
                  style={{ display: 'none' }}
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload" className="file-select-btn">
                  <FaFilePdf /> {pdfFile ? "Change PDF" : "Select PDF"}
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(e) => handleFileSelect(e, 'images')}
                  style={{ display: 'none' }}
                  id="images-upload"
                />
                <label htmlFor="images-upload" className="file-select-btn">
                  <FaImage /> Add Photos
                </label> */}
                {/* Hidden file input – triggers native rear camera */}
                <input
                  ref={rearCameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const dataUrl = reader.result;
                      // Reset crop area and open the modal in crop-only mode
                      setCropArea({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
                      setImageDisplaySize({ width: 0, height: 0 });
                      setIsCropEnabled(false);
                      setCapturedPhotoForCrop(dataUrl);
                      setShowCameraModal(true);
                      // Re-enable disabled monitoring tracks now that native camera is done
                      if (restoreMonitoringStreamsRef.current) {
                        restoreMonitoringStreamsRef.current();
                        restoreMonitoringStreamsRef.current = null;
                      }
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', width: '100%' }}>
                  <button
                    type="button"
                    className={`file-select-btn ${!canUploadAnswerSheet ? 'disabled' : ''}`}
                    onClick={(e) => {
                      if (!canUploadAnswerSheet) {
                        e.preventDefault();
                        return;
                      }
                      handleTakePhoto();
                    }}
                    title={canUploadAnswerSheet ? "Capture from camera" : "Camera locked until 15 minutes remaining"}
                    disabled={!canUploadAnswerSheet}
                    style={{
                      opacity: canUploadAnswerSheet ? 1 : 0.6,
                      cursor: canUploadAnswerSheet ? 'pointer' : 'not-allowed',
                      backgroundColor: canUploadAnswerSheet ? 'green' : '#818181ff',
                    }}
                  >
                    <FaCamera /> Take Photo
                  </button>

                  {!canUploadAnswerSheet && allowedUploadTimeString && (
                    <div className="time-restriction-banner" style={{
                      backgroundColor: 'red',
                      color: '#ffbabaff',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #ff9a9aff',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontWeight: '600',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      textAlign: 'center',
                      maxWidth: '400px',
                      marginTop: '5px'
                    }}>
                      <FaClock size={18} style={{ flexShrink: 0 }} />
                      <span>You will be allowed to submit after <strong>{allowedUploadTimeString}</strong> (15 minutes before the end of exam)</span>
                    </div>
                  )}
                </div>
              </div>

              {pdfFile && (
                <div className="selected-pdf-row">
                  <FaFilePdf /> <span className="selected-file-name">{pdfFile.name}</span>
                  <button type="button" className="remove-file-btn" onClick={() => setPdfFile(null)} aria-label="Remove PDF"><FaTimes /></button>
                </div>
              )}

              {imageFiles.length > 0 && (
                <div className="photo-previews">
                  <div className="photo-previews-label">
                    {imageFiles.length} photo(s) — will be combined into one PDF • Click image to zoom
                    <br />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#0056b3', fontWeight: '600', fontSize: '14px', background: '#e6f2ff', padding: '4px 8px', borderRadius: '4px' }}>
                      <FaArrowsAlt /> Drag images to reorder pages
                    </span>
                  </div>
                  <div className="photo-grid">
                    {imageFiles.map((item, idx) => (
                      <div
                        key={idx}
                        className="photo-preview-item"
                        draggable
                        data-index={idx}
                        onDragStart={(e) => handleReorderDragStart(e, idx)}
                        onDragEnter={(e) => handleReorderDragEnter(e, idx)}
                        onDragOver={handleReorderDragOver}
                        onDrop={(e) => handleReorderDrop(e, idx)}
                        style={{
                          opacity: draggedIdx === idx ? 0.3 : 1,
                          transform: dragOverIdx === idx ? (draggedIdx < idx ? 'translateX(-10px)' : 'translateX(10px)') : 'translateX(0)',
                          transition: 'all 0.2s ease',
                          border: dragOverIdx === idx ? '2px dashed #007bff' : 'none',
                          position: 'relative',
                          touchAction: 'none', // Allow touch events to handle dragging reliably
                          cursor: 'grab' // Make cursor indicate whole card is draggable
                        }}
                        onTouchStart={(e) => handleReorderTouchStart(e, idx)}
                        onTouchMove={handleReorderTouchMove}
                        onTouchEnd={handleReorderTouchEnd}
                      >
                        <div
                          className="drag-handle"
                          style={{ position: 'absolute', top: '5px', left: '5px', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '4px', padding: '6px 8px', fontSize: '14px', display: 'flex', alignItems: 'center', zIndex: 2, pointerEvents: 'none' }}
                          title="Drag to reorder"
                        >
                          <FaArrowsAlt />
                        </div>
                        <img
                          src={item.preview}
                          alt={`Page ${idx + 1}`}
                          onClick={(e) => {
                            // Don't trigger zoom if we just dragged
                            if (draggedIdx !== null) return;
                            setPreviewImage(item.preview);
                            setZoomLevel(1);
                            setPanPosition({ x: 0, y: 0 });
                          }}
                          style={{ cursor: draggedIdx !== null ? 'grabbing' : 'zoom-in', pointerEvents: draggedIdx !== null ? 'none' : 'auto' }}
                          title="Click to zoom for better view"
                        />
                        <span className="photo-page-num" style={{ fontWeight: 'bold' }}>Page {idx + 1}</span>
                        <button type="button" className="remove-photo-btn" onClick={(e) => { e.stopPropagation(); removeImageAt(idx); }} aria-label="Remove" style={{ zIndex: 2 }}><FaTrash /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mobile-upload-footer">
                <button
                  type="button"
                  className="btn-preview-all"
                  onClick={() => setIsFullPreviewOpen(true)}
                  disabled={!hasUploadContent || isPdfUploading}
                >
                  <FaEye /> Preview &amp; Submit
                </button>

                {/* Finish & Sign Out button moved into the preview popup */}
                {/* <button
                  className="btn-finish-upload"
                  onClick={handlePdfUpload}
                  disabled={!hasUploadContent || isPdfUploading || !canUploadAnswerSheet}
                >
                  {isPdfUploading ? (
                    <>
                      <FaSpinner className="spin-icon" />
                      {uploadStep === 'converting' && ' Converting to PDF...'}
                      {uploadStep === 'uploading' && ' Uploading PDF...'}
                      {uploadStep === 'saving' && ' Saving the PDF...'}
                      {uploadStep === 'done' && ' Done! Signing out...'}
                      {uploadStep === 'error' && ' Error - See below'}
                      {!uploadStep && ' Processing...'}
                    </>
                  ) : (
                    <><FaCheckCircle /> Finish & Sign Out</>
                  )}
                </button>
                {isPdfUploading && (
                  <div className="upload-step-status">
                    <div className={`upload-step ${uploadStep === 'converting' ? 'active' : (uploadStep === 'uploading' || uploadStep === 'saving' || uploadStep === 'done') ? 'complete' : ''}`}>
                      {(uploadStep === 'uploading' || uploadStep === 'saving' || uploadStep === 'done') ? <FaCheckCircle color="#38a169" /> : <FaSpinner className="spin-icon" />}
                      &nbsp; Converting
                    </div>
                    <div className={`upload-step ${uploadStep === 'uploading' ? 'active' : (uploadStep === 'saving' || uploadStep === 'done') ? 'complete' : ''}`}>
                      {(uploadStep === 'saving' || uploadStep === 'done') ? <FaCheckCircle color="#38a169" /> : uploadStep === 'uploading' ? <FaSpinner className="spin-icon" /> : <span style={{ display: 'inline-block', width: '14px' }} />}
                      &nbsp; Uploading
                    </div>
                    <div className={`upload-step ${uploadStep === 'saving' ? 'active' : uploadStep === 'done' ? 'complete' : ''}`}>
                      {uploadStep === 'done' ? <FaCheckCircle color="#38a169" /> : uploadStep === 'saving' ? <FaSpinner className="spin-icon" /> : <span style={{ display: 'inline-block', width: '14px' }} />}
                      &nbsp; Saving
                    </div>
                    {uploadStep === 'error' && uploadStepError && (
                      <div className="upload-step-error">{uploadStepError}</div>
                    )}
                  </div>
                )} */}
              </div>



              {/* Camera Preview Modal with Crop - Uses CAM-1 */}
              {
                showCameraModal && (
                  <div className="camera-modal-backdrop">
                    <div className="camera-modal-content">
                      <div className="camera-modal-header">
                        <div className="camera-modal-header-left">
                          <h3>
                            📷 Camera Preview {capturedPhotoForCrop ? ' - Crop Image' : ''}
                          </h3>
                          <span className="camera-badge">
                            <FaCamera /> CAM-{selectedCameraIndex + 1}
                          </span>
                        </div>
                        <button
                          className="camera-modal-close-btn"
                          onClick={() => {
                            // Clean up alternate stream if exists
                            if (alternateCameraStream) {
                              alternateCameraStream.getTracks().forEach(track => track.stop());
                              setAlternateCameraStream(null);
                            }
                            setShowCameraModal(false);
                            setCapturedPhotoForCrop(null);
                            setCropArea({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
                            setSelectedCameraIndex(0); // Reset to first camera
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>

                      {!capturedPhotoForCrop ? (
                        <>
                          <div className="camera-modal-video-container" style={{ position: 'relative' }}>
                            <video
                              ref={cameraModalVideoRef}
                              autoPlay
                              playsInline
                              muted
                              className="camera-modal-video"
                              onClick={handleVideoTap}
                            />
                            {focusPoint && focusPoint.visible && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: focusPoint.y,
                                  left: focusPoint.x,
                                  width: '60px',
                                  height: '60px',
                                  border: '2px solid #fbbf24', // Amber-400
                                  borderRadius: '8px',
                                  transform: 'translate(-50%, -50%)',
                                  pointerEvents: 'none',
                                  animation: 'focusPulse 1s ease-out forwards',
                                  boxShadow: '0 0 10px rgba(251, 191, 36, 0.5)',
                                  zIndex: 10
                                }}
                              />
                            )}
                            <style>{`
                            @keyframes focusPulse {
                              0% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
                              20% { opacity: 1; }
                              100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
                            }
                          `}</style>
                            <div className="camera-live-indicator">
                              <span className="status-live">●</span> CAM-{selectedCameraIndex + 1} Live
                              {isMobileDeviceFallback && (
                                <span style={{ marginLeft: '10px', fontSize: '12px', opacity: 0.8 }}>
                                  ({selectedCameraIndex === 0 ? 'Front' : 'Rear'})
                                </span>
                              )}
                            </div>
                            {/* Floating Camera Switch Button - Mobile Only - Always show on mobile for front/rear switching */}
                            {isMobileDeviceFallback && (
                              <button
                                className="camera-switch-btn-floating"
                                onClick={switchCamera}
                                title={`Switch to ${selectedCameraIndex === 0 ? 'Rear' : 'Front'} Camera`}
                                style={{
                                  position: 'absolute',
                                  top: '15px',
                                  right: '15px',
                                  zIndex: 1000,
                                  backgroundColor: 'rgba(43, 108, 176, 0.9)',
                                  color: 'white',
                                  border: '3px solid white',
                                  borderRadius: '50%',
                                  width: '60px',
                                  height: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  fontSize: '24px',
                                  boxShadow: '0 4px 15px rgba(0,0,0,0.7)',
                                  transition: 'all 0.3s ease',
                                  fontWeight: 'bold'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(43, 108, 176, 1)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(43, 108, 176, 0.9)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                <FaSync />
                              </button>
                            )}
                          </div>
                          <div className="camera-modal-buttons">
                            <button
                              className="camera-capture-btn"
                              onClick={capturePhotoFromModal}
                            >
                              <FaCamera /> Capture Photo
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className={`camera-modal-crop-container ${dragState ? 'dragging' : ''}`}
                            onMouseMove={onGlobalMove}
                            onMouseUp={onGlobalUp}
                            onTouchMove={onGlobalTouchMove}
                            onTouchEnd={onGlobalUp}
                            style={{ touchAction: 'none' }} // Prevent scrolling
                          >
                            <div style={{ position: 'relative', display: 'inline-block', margin: 'auto' }}>
                              <img
                                ref={cropImageRef}
                                src={capturedPhotoForCrop}
                                alt="Captured Content"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  display: 'block',
                                  userSelect: 'none'
                                }}
                                draggable={false}
                                onLoad={(e) => {
                                  const img = e.target;
                                  const displayWidth = img.offsetWidth;
                                  const displayHeight = img.offsetHeight;
                                  setImageDisplaySize({ width: displayWidth, height: displayHeight });

                                  // Initialize crop area: Reduced size with A4 aspect ratio (centered)
                                  const a4Ratio = 1 / 1.414; // ~0.707 (Portrait)

                                  // Default to 80% of image size as a baseline to keep handles away from edges
                                  let targetW = displayWidth * 0.8;
                                  let targetH = displayHeight * 0.8;

                                  // Adjust to maintain A4 aspect ratio
                                  if (targetW / targetH > a4Ratio) {
                                    // Image is wider than A4 ratio, adjust width based on height
                                    targetW = targetH * a4Ratio;
                                  } else {
                                    // Image is taller than A4 ratio, adjust height based on width
                                    targetH = targetW / a4Ratio;
                                  }

                                  const cropX = (displayWidth - targetW) / 2;
                                  const cropY = (displayHeight - targetH) / 2;

                                  setCropArea({
                                    x: cropX,
                                    y: cropY,
                                    width: targetW,
                                    height: targetH,
                                    startX: cropX,
                                    startY: cropY
                                  });
                                }}
                              />

                              {isCropEnabled && cropArea.width > 0 && cropArea.height > 0 && imageDisplaySize.width > 0 && (
                                <>
                                  {/* Crop overlay - positioned relative to the image wrapper */}
                                  <div
                                    onMouseDown={(e) => onMouseDown(e, 'move')}
                                    onTouchStart={(e) => onTouchStart(e, 'move')}
                                    style={{
                                      position: 'absolute',
                                      left: `${(cropArea.x / imageDisplaySize.width) * 100}%`,
                                      top: `${(cropArea.y / imageDisplaySize.height) * 100}%`,
                                      width: `${(cropArea.width / imageDisplaySize.width) * 100}%`,
                                      height: `${(cropArea.height / imageDisplaySize.height) * 100}%`,
                                      border: '3px solid #007bff',
                                      boxSizing: 'border-box',
                                      pointerEvents: 'auto',
                                      cursor: 'move',
                                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                                      zIndex: 10
                                    }}
                                  />

                                  {/* Resize handles */}
                                  {['nw', 'ne', 'sw', 'se'].map(corner => {
                                    let topPos, leftPos, cursorType;
                                    if (corner === 'nw') { topPos = cropArea.y; leftPos = cropArea.x; cursorType = 'nwse-resize'; }
                                    if (corner === 'ne') { topPos = cropArea.y; leftPos = cropArea.x + cropArea.width; cursorType = 'nesw-resize'; }
                                    if (corner === 'sw') { topPos = cropArea.y + cropArea.height; leftPos = cropArea.x; cursorType = 'nesw-resize'; }
                                    if (corner === 'se') { topPos = cropArea.y + cropArea.height; leftPos = cropArea.x + cropArea.width; cursorType = 'nwse-resize'; }

                                    return (
                                      <div
                                        key={corner}
                                        onMouseDown={(e) => onMouseDown(e, corner)}
                                        onTouchStart={(e) => onTouchStart(e, corner)}
                                        style={{
                                          position: 'absolute',
                                          width: '28px',
                                          height: '28px',
                                          backgroundColor: '#007bff',
                                          border: '3px solid white',
                                          borderRadius: '50%',
                                          pointerEvents: 'auto',
                                          cursor: cursorType,
                                          left: `${(leftPos / imageDisplaySize.width) * 100}%`,
                                          top: `${(topPos / imageDisplaySize.height) * 100}%`,
                                          transform: 'translate(-50%, -50%)',
                                          zIndex: 11,
                                          boxShadow: '0 2px 5px rgba(0,0,0,0.4)'
                                        }}
                                      />
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          </div>
                          {isCropEnabled && (
                            <div className="camera-crop-status">
                              Drag to select crop area • Selected: {Math.round(cropArea.width)} × {Math.round(cropArea.height)} pixels
                            </div>
                          )}
                          <div className="camera-modal-buttons">
                            <button
                              className="camera-retake-btn"
                              onClick={() => {
                                // Close the modal and reset crop state
                                setShowCameraModal(false);
                                setCapturedPhotoForCrop(null);
                                setCropArea({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
                                setImageDisplaySize({ width: 0, height: 0 });
                                // Use handleTakePhoto so the restore listener is registered properly
                                setTimeout(() => handleTakePhoto(), 100);
                              }}
                            >
                              <FaRedo /> Retake
                            </button>
                            {capturedPhotoForCrop && (
                              /* Re-detect button removed */
                              null
                            )}
                            <button
                              className={`camera-crop-toggle-btn ${isCropEnabled ? 'active' : ''}`}
                              onClick={() => setIsCropEnabled(!isCropEnabled)}
                            >
                              {isCropEnabled ? <><FaCrop /> Cancel Crop</> : <><FaCrop /> Crop</>}
                            </button>
                            <button
                              className={`camera-apply-btn ${(!isCropEnabled || (cropArea.width > 0 && cropArea.height > 0)) ? '' : 'disabled'}`}
                              onClick={applyCrop}
                              disabled={isCropEnabled && (cropArea.width === 0 || cropArea.height === 0)}
                            >
                              <FaCheckCircle /> Save
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              }

            </div >
          )}
          {(examFinished || isTimeExceeded) && (
            <div className="exam-complete-wrapper">
              <div className="exam-complete-card">
                <div className="exam-complete-card-iflex">
                  {isTimeExceeded ? <FaExclamationTriangle className="exam-complete-icon" style={{ color: '#dd6b20' }} /> : <FaCheckCircle className="exam-complete-icon" />}
                </div>
                <h2 className="exam-complete-title">
                  {isTimeExceeded ? "Exam Time Over" : "Exam Completed"}
                </h2>
                <p className="exam-complete-text">
                  {isTimeExceeded ? "Your exam time is over. For further details, please contact the admin." : "You may now close this window."}
                </p>

                <button
                  className="exam-complete-btn"
                  onClick={() => {
                    window.location.href = BASE_URL;
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          )}


          {/* Brief loading until getQuestions returns (then we show either offline PDF or online questions) */}
          {
            examStarted && !showUploadSection && !examFinished && !getQuestionsTriggeredOnce && (
              <div className="questions-container">
                <div className="loading-state"><FaSpinner className="spin-icon" /> Preparing exam...</div>
              </div>
            )
          }

          {/* Unified waiting screen above handles all camera-waiting states. */}

          {/* OFFLINE EXAM MODE UI - PDF from getQuestions question_paper_url */}
          {(() => {
            const bothReady2 = devicesOk;

            if (!isExamBlockedByInvigilator && !isOverlayVisible && isStudentReady && !showUploadSection && !isMobileDevice && examStarted && !examFinished && isOfflineMode && getQuestionsTriggeredOnce && bothReady2) {
              return (
                <div className="offline-exam-screen">
                  <div className="offline-card">
                    {(questionPaperUrl || questionPaperPath && !pdfUnavailable) ? (
                      <div className="offline-question-paper-viewer">
                        {courseCode && (
                          <div className="offline-course-code">Course Code : {courseCode}</div>
                        )}
                        <iframe
                          title="Question Paper"
                          src={questionPaperUrl || (questionPaperPath ? `${String(questionPaperPath).replace(/^\//, '')}` : '')}
                          className="question-paper-iframe"
                        />
                      </div>
                    ) : (questionPaperUrl || questionPaperPath && pdfUnavailable) ? (
                      <div className="offline-no-file">
                        <FaFilePdf size={48} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                        <p>No PDF available</p>
                      </div>
                    ) : (
                      <div className="offline-no-file">
                        <FaFilePdf size={48} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                        <p>No file available</p>
                      </div>
                    )}

                    <div className="offline-action-bar">
                      <div className="offline-instructions-container">
                        <h3 className="instructions-title">Offline Examination Instructions</h3>
                        <div className='offline-flex'>
                          <div className="instruction-section">
                            <h4><span className="step-badge">1</span> Writing Your Answers</h4>
                            <ul className="instruction-list">
                              <li>Write your answers on <strong>A4 sheets</strong>.</li>
                              <li>Clearly mention the <strong>Question Number</strong> before each answer.</li>
                              <li>Write your <strong>Name, Course, and Page Number</strong> on top of every page.</li>
                              <li>Use <strong>blue or black ink pen</strong> (.5 or higher) for writing answers.</li>
                            </ul>
                          </div>
                          <div className="instruction-section">
                            <h4><span className="step-badge">2</span> During the Examination</h4>
                            <ul className="instruction-list">
                              <li>Both cameras, screen, and voice will be monitored throughout the entire exam duration.</li>
                              <li>Allow <strong>microphone permission</strong> to continue the exam. You can communicate with the Invigilator using the Mic (Voice Chat) or the Chat Box if you face any issues.</li>
                              <li>Do not leave your seat during the exam.</li>
                              <li>Do not close the exam tab or switch to another tab/app on your laptop or mobile. If you do, that device is shown as disconnected on your other device and the question paper is hidden until you return.</li>
                              <li>Make sure the room is <strong>well lit</strong> so your face remains clearly visible.</li>
                            </ul>
                          </div>
                          <div className="instruction-section">
                            <h4><span className="step-badge">3</span> Submission Process</h4>
                            <ul className="instruction-list">
                              <li>Login to the examination portal using your mobile browser: <br />
                                <a href="https://iitonline.enovasolutions.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                                  https://iitonline.enovasolutions.com
                                </a>
                              </li>
                              <li>You must complete the login and view question paper within <strong>15 minutes from the start time</strong>, beyond which you will not be allowed to take the exam.</li>
                              <li>You will be allowed to scan and upload your answer sheets <strong>15 minutes before the exam ends</strong>.</li>
                              <li>No time extension will be provided.</li>
                              <li>Take <strong>clear photos</strong> of each answer sheet page.</li>
                              <li>Ensure the entire page is visible and readable including question numbers/margins while cropping.</li>
                              <li>Upload images in the <strong>correct sequence</strong> (ascending page numbers).</li>
                              <li>Ensure all pages are uploaded before submission.</li>
                              <li>Click <strong>Preview</strong> to review the uploaded pages.</li>
                              <li>After verification, click <strong>Preview & Submit</strong> to complete the Examination.</li>
                            </ul>
                          </div>
                        </div>
                        <div className="completion-hint">
                          <p>After submission from your mobile device, you may click the <button className="btn-submit-final small" onClick={handleOfflineClose}>Close</button> button below to close the examination window on your desktop/laptop.</p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* ONLINE CBT MODE UI */}
          {(() => {
            const bothReady3 = devicesOk;

            if (!isExamBlockedByInvigilator && !isOverlayVisible && isStudentReady && examStarted && !showUploadSection && !examFinished && !isOfflineMode && getQuestionsTriggeredOnce && bothReady3) {
              return (
                <div className="questions-container">
                  {sections.length === 0 ? (
                    <div className="loading-state"><FaSpinner className="spin-icon" /> Loading Questions...</div>
                  ) : (
                    sections.map((section) => (
                      <div key={section.id} className="section-card">
                        <div className="section-header">
                          <h3>{section.name}</h3>
                          <span className="section-info">
                            Marks per Question: {section.marks_per_question} | Type: {section.choice_type.replace(/_/g, ' ')}
                          </span>
                        </div>

                        <div className="section-questions">
                          {(section.choice_type === 'either_or_choice' || section.choice_type === 'either_or_choice_with_multiple') ? (
                            section.questions.map((group, gIndex) => (
                              <div key={gIndex} className="or-group-container">
                                <span className="or-badge">EITHER / OR GROUP {gIndex + 1}</span>
                                {group.map((q, qIndex) => (
                                  <div key={q.question_key}>
                                    {qIndex > 0 && <div className="or-divider">--- OR ---</div>}
                                    <QuestionItem q={q} index={q.q_no} answers={answers} onAnswerChange={handleAnswerChange} />
                                  </div>
                                ))}
                              </div>
                            ))
                          ) : (
                            section.questions.map((q) => (
                              <QuestionItem key={q.question_key} q={q} index={q.q_no} answers={answers} onAnswerChange={handleAnswerChange} />
                            ))
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div className="submission-area">
                    <button onClick={confirmSubmission} className="btn-submit-final">Submit Examination</button>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div >
      </main >

      {/* Photo Review Modal */}
      {
        previewImage && (
          <div
            className="modal-backdrop"
            style={{ zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setPreviewImage(null)} // Close on backdrop click
          >
            <div
              className="photo-review-modal"
              style={{
                position: 'relative',
                width: '95vw',
                height: '90vh',
                backgroundColor: 'black',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header / Controls */}
              <div style={{
                padding: '10px 15px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(50, 50, 50, 0.9)',
                color: 'white',
                zIndex: 10
              }}>
                <span style={{ fontWeight: 'bold' }}>Review Photo</span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleZoomOut}
                    style={{ padding: '5px 12px', borderRadius: '4px', border: 'none', background: '#4a5568', color: 'white', cursor: 'pointer' }}
                  >
                    <FaDesktop style={{ transform: 'scale(0.8) translateY(1px)' }} /> -
                  </button>
                  <span style={{ minWidth: '40px', textAlign: 'center', lineHeight: '28px' }}>{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={handleZoomIn}
                    style={{ padding: '5px 12px', borderRadius: '4px', border: 'none', background: '#4a5568', color: 'white', cursor: 'pointer' }}
                  >
                    <FaDesktop style={{ transform: 'scale(0.8) translateY(1px)' }} /> +
                  </button>
                  <button
                    onClick={() => setPreviewImage(null)}
                    style={{ marginLeft: '15px', padding: '5px 12px', borderRadius: '4px', border: 'none', background: '#e53e3e', color: 'white', cursor: 'pointer' }}
                  >
                    <FaTimes /> Close
                  </button>
                </div>
              </div>

              {/* Image Container */}
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#1a202c',
                  cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                  touchAction: 'none' // Prevent scroll on mobile
                }}
                onMouseDown={(e) => startPan(e.clientX, e.clientY)}
                onMouseMove={(e) => movePan(e.clientX, e.clientY)}
                onMouseUp={endPan}
                onMouseLeave={endPan}
                onTouchStart={(e) => startPan(e.touches[0].clientX, e.touches[0].clientY)}
                onTouchMove={(e) => {
                  if (e.cancelable) e.preventDefault();
                  movePan(e.touches[0].clientX, e.touches[0].clientY);
                }}
                onTouchEnd={endPan}
              >
                <img
                  src={previewImage}
                  alt="Review"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                    userSelect: 'none',
                    pointerEvents: 'none' // Let container handle events
                  }}
                />
              </div>
              <div style={{
                padding: '8px',
                textAlign: 'center',
                color: '#a0aec0',
                fontSize: '12px',
                background: 'rgba(0,0,0,0.8)'
              }}>
                {zoomLevel > 1 ? "Drag to pan image" : "Use controls to zoom"}
              </div>
            </div>
          </div>
        )
      }

      {/* ---------------- RIGHT SIDEBAR (Navigation) ---------------- */}
      {
        !isOverlayVisible && examStarted && !showUploadSection && !examFinished && !isOfflineMode && (
          <aside className="exam-sidebar right">
            <div className="section-label"><FaListOl /> Question Navigator</div>

            <div className="nav-scroll-area">
              {sections.map((section) => (
                <div key={section.id} className="nav-section-group">
                  <div className="nav-section-title">{section.name}</div>
                  <div className="nav-question-grid">
                    {(section.choice_type.includes('either_or')) ? (
                      section.questions.flat().map((q) => (
                        <button
                          key={q.question_key}
                          className={`nav-q-btn ${answers[q.question_key] ? 'answered' : ''}`}
                          onClick={() => scrollToQuestion(q.question_key)}
                        >
                          {q.q_no}
                        </button>
                      ))
                    ) : (
                      section.questions.map((q) => (
                        <button
                          key={q.question_key}
                          className={`nav-q-btn ${answers[q.question_key] ? 'answered' : ''}`}
                          onClick={() => scrollToQuestion(q.question_key)}
                        >
                          {q.q_no}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="nav-legend">
              <div className="legend-item"><span className="dot answered"></span> Answered</div>
              <div className="legend-item"><span className="dot"></span> Unanswered</div>
            </div>
          </aside>
        )
      }
      {/* Full-Page Photo Preview Modal (PDF-like experience) */}
      {isFullPreviewOpen && (
        <div className="full-photo-preview-overlay">
          <div className="full-preview-header">
            <div className="full-preview-title">
              <FaEye /> Upload Preview ({imageFiles.length} pages)
              <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '4px', opacity: 0.9, display: 'flex', alignItems: 'center', gap: '5px' }}>
                <FaArrowsAlt /> Drag images to reorder pages
              </div>
            </div>
            <button className="full-preview-close" onClick={closeFullPreview}>
              <FaTimes />
            </button>
          </div>

          <div className="full-preview-content">
            {imageFiles.length > 0 ? (
              imageFiles.map((item, idx) => (
                <div
                  key={idx}
                  className="preview-page-card"
                  data-index={idx}
                  onDragEnter={(e) => handleReorderDragEnter(e, idx)}
                  onDragOver={handleReorderDragOver}
                  onDrop={(e) => handleReorderDrop(e, idx)}
                  style={{
                    opacity: draggedIdx === idx ? 0.3 : 1,
                    transform: dragOverIdx === idx ? (draggedIdx < idx ? 'translateY(-10px)' : 'translateY(10px)') : 'translateY(0)',
                    transition: 'all 0.2s ease',
                    border: dragOverIdx === idx ? '2px dashed #007bff' : '1px solid #ddd',
                    position: 'relative'
                  }}
                  onTouchMove={handleReorderTouchMove}
                  onTouchEnd={handleReorderTouchEnd}
                >
                  <div className="preview-page-info">
                    <span
                      style={{
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'grab',
                        touchAction: 'none'
                      }}
                      draggable
                      onDragStart={(e) => handleReorderDragStart(e, idx)}
                      onTouchStart={(e) => handleReorderTouchStart(e, idx)}
                    >
                      <span style={{
                        background: 'rgba(0,123,255,0.1)',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <FaArrowsAlt style={{ color: '#0056b3' }} />
                      </span>
                      Page {idx + 1}
                    </span>
                    <button
                      type="button"
                      className="remove-photo-btn small"
                      onClick={() => removeImageAt(idx)}
                      style={{ padding: '4px 8px', fontSize: '12px', zIndex: 2 }}
                    >
                      <FaTrash /> Remove
                    </button>
                  </div>
                  <img
                    src={item.preview}
                    alt={`Page ${idx + 1}`}
                    className="preview-page-img"
                    style={{ pointerEvents: draggedIdx !== null ? 'none' : 'auto' }}
                  />
                </div>
              ))
            ) : (
              <div className="no-previews-msg">No photos captured yet</div>
            )}
          </div>

          <div className="full-preview-footer">
            <button
              className="full-preview-submit-btn"
              onClick={handlePdfUpload}
              disabled={!hasUploadContent || isPdfUploading || !canUploadAnswerSheet}
            >
              {isPdfUploading ? (
                <>
                  <FaSpinner className="spin-icon" />
                  {uploadStep === 'converting' && ' Converting...'}
                  {uploadStep === 'uploading' && ' Uploading...'}
                  {uploadStep === 'saving' && ' Saving...'}
                  {uploadStep === 'done' && ' Done!'}
                  {uploadStep === 'error' && ' Error'}
                  {!uploadStep && ' Processing...'}
                </>
              ) : (
                <><FaCheckCircle /> Submit &amp; Logout</>
              )}
            </button>

            {/* Compact upload progress — shown inside the popup while uploading */}
            {isPdfUploading && (
              <div className="preview-upload-steps">
                <div className={`preview-upload-step ${uploadStep === 'converting' ? 'pu-active' : (uploadStep === 'uploading' || uploadStep === 'saving' || uploadStep === 'done') ? 'pu-done' : ''}`}>
                  {(uploadStep === 'uploading' || uploadStep === 'saving' || uploadStep === 'done')
                    ? <FaCheckCircle className="pu-icon done" />
                    : <FaSpinner className="pu-icon spin-icon" />}
                  <span>Convert</span>
                </div>
                <div className="pu-divider" />
                <div className={`preview-upload-step ${uploadStep === 'uploading' ? 'pu-active' : (uploadStep === 'saving' || uploadStep === 'done') ? 'pu-done' : ''}`}>
                  {(uploadStep === 'saving' || uploadStep === 'done')
                    ? <FaCheckCircle className="pu-icon done" />
                    : uploadStep === 'uploading'
                      ? <FaSpinner className="pu-icon spin-icon" />
                      : <span className="pu-icon empty" />}
                  <span>Upload</span>
                </div>
                <div className="pu-divider" />
                <div className={`preview-upload-step ${uploadStep === 'saving' ? 'pu-active' : uploadStep === 'done' ? 'pu-done' : ''}`}>
                  {uploadStep === 'done'
                    ? <FaCheckCircle className="pu-icon done" />
                    : uploadStep === 'saving'
                      ? <FaSpinner className="pu-icon spin-icon" />
                      : <span className="pu-icon empty" />}
                  <span>Save</span>
                </div>
                {uploadStep === 'error' && uploadStepError && (
                  <div className="pu-error">{uploadStepError}</div>
                )}
              </div>
            )}

            <button className="btn-cancel-preview" onClick={closeFullPreview} style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              color: '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              Close
            </button>
          </div>
        </div>
      )}
      {/* Private Audio Notification Popup */}
      {showPrivateCallNotification && (
        <div className="private-call-popup-overlay">
          <div className="private-call-popup">
            <div className="private-call-icon-pulse">
              <FaMicrophone size={36} color="#ce3131ff" />
            </div>

            <h2 style={{ margin: '0 0 10px 0', color: '#2c1a1aff', fontSize: '24px' }}>
              Proctor is Speaking
            </h2>

            <p style={{ margin: '0', color: '#684a4aff', fontSize: '16px', lineHeight: '1.5' }}>
              The invigilator has started a private audio session. You can hear them now.
            </p>

            <p style={{ margin: '10px 0 0 0', color: '#684a4aff', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Note:</strong> Audio will work properly only after both devices are connected successfully.
            </p>

            <button
              className="private-call-close-btn"
              onClick={() => setShowPrivateCallNotification(false)}
            >
              OK, I Understand
            </button>
          </div>
        </div>
      )}

      {<Calculator />}
    </div >
  );

};

export default ExamRoom;
